import React, {
  useEffect,
  useMemo,
  useState,
  KeyboardEvent,
  useLayoutEffect,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import { AppDispatch, RootState } from "../../../redux/store";
import { clearTraditionalQuoteData } from "../../../redux/reducer/locationSlice/locationSlice";
import GetQuote from "../../../components/getQuote/GetQuote";
import { MutliDropdown, MultiSelectChips, SingleDropdown } from "../../../components/shared";
import { SingleDropdownItemType } from "../../../components/shared/SingleDropdown";
import AddDocumentURLModal from "../../../components/modals/AddDocumentURLModal";
import {
  cmToInch,
  kgToLbs,
  onInputPhNumFormat,
  expiredData,
  getISODate,
  getDockTimeInUTC,
  extractTime,
  storedTimeToPicker,
  isPastPickupDate,
  pickerTimeToStored,
  formatTimeForDisplay,
} from "../../../utils/utils";

import {
  validateZipCode,
  validateContactEmail,
  ValidateDate,
  ValidateExpedietedDate,
  ValidateExpedietedCrrentTime,
  validateMaskedPhoneNumber,
} from "../../../utils/validation";
import {
  GET_DATA_FROM_ZIPCODE,
  SAVE_QUOTATION,
  SAVE_LTL_QUOTATION,
  PHONE_VALIDATE_API,
} from "../../../apis/end-points";
import {
  customerOrderColumns,
  agentOrderColumns,
  CustomerRowDataType,
  AgentRowDataType,
  DataTypes,
  DocumentInfo,
  Fields,
  FormErrorsType,
  GetQuoteApiResponse,
  MappedService,
  orderTypeColumns,
  OrderTypeRowDataType,
  QuoteInfo,
  ServiceLevelType,
  tableConfig,
  MoreReferencesType,
  GetLTLQuoteApiResponse,
  GetLTLFinalMileQuoteApiResponse,
  accessorialsColumn,
  hubData,
} from "./CustomerOrder-types";

import { getApi, postApi } from "../../../apis/api";
import {
  ORDER_REFERENCE_2_INDEX,
  OrderReference2ConfigMap,
  fetchOrderReference2Config,
  getOrderReference2Value,
  isOrderReference2Locked,
  resolveOrderReference2Source,
} from "./orderReference.utils";
import { Loader } from "../../../components/loaders/loaders";
import { useMessage } from "../../../context/MessageContext";
import EmailChips from "../../../components/shared/EmailChips";
import useFormChangeHandler from "../../../hooks/useFormChangeHandler";
import useMandatoryFieldFocus from "../../../hooks/useMandatoryFieldFocus";
import {
  DROPDOWN_FIELD_KEYS,
  FINAL_MILE_SERVICE_TYPE_OPTIONS,
  HANDLING_UNITS_FIELD_KEY,
  LOGISTICS_TYPE,
} from "../../../utils/constants";
import {
  isMandatoryMessage,
  pruneItemIssues,
  prunePackageIssues,
  validateOrderForm,
} from "../../../utils/orderValidation";
import ConfirmModal from "../../../components/modals/ConfirmModal";
import { CUSTOMER_ID } from "../../../utils/authUtils";

import { AddIcon, DeleteIcon } from "../../../assets";
import {
  GENERAL_ERROR_MESSAGE,
  HANDLING_UNITS_REQUIRED_MESSAGE,
  ORDER_MANAGEMENT_MESSAGES,
  PICKUP_DATE_PAST_ERROR,
} from "../../../utils/message";
import GetLTLQuote from "../../../components/getQuote/GetLTLQuote";
import OrderItemDetailTable from "../../../components/shared/table/OrderItemDetailTable";
import { DEFAULT_DOCK_CLOSE_TIME, DEFAULT_DOCK_OPEN_TIME, EXPEDIET_PACKAGE_TYPE, PACKAGE_TYPE, PHONENUMBER } from "../../../utils/constants";
import moment from "moment";
import CustomTimePicker from "../../../components/CustomTimePicker";
import { useSvgRef } from "@mui/x-charts";
import AddressDropDown from "../../../components/shared/AddressDropDown";
import GetTraditionalQuote from "../../../components/getQuote/GetTraditionalQuote";

interface ColumnTypes {
  key: string;
  title: string;
}

const agentHubColumns: ColumnTypes[] = [{ key: "name", title: "Name" }];
const serviceColumns: ColumnTypes[] = [{ key: "name", title: "Service Level" }];

interface AgentHubDropdown {
  id?: string;
  name: string | null;
  agentHubNo: string;
  isVirtualHub?: boolean;
  masterHubId?: string | null;
  masterHubName?: string;
}
interface DropwdownValueTypes {
  id: string;
  name: string;
}
const AdditionalServices = [
  "Additional Services",
  "Remove & Disposal",
  "Order Swap & Exchange",
];

interface OrderProps {
  quoteTypeRadio?: any;
  setQuoteType?: (data: any) => void;
  savedQuoteData?: any;
  setSavedQuoteData?: (data: any) => void;
}

/**
 * CustomerOrder Component
 *
 * This component allows users to create a new customer order by filling out a form with various fields,
 * including destination address, service level, item details, and more. The form supports validation,
 * fetching data from APIs, and submitting the order data to the backend.
 */
const CustomerOrder: React.FC<OrderProps> = ({
  quoteTypeRadio,
  setQuoteType,
  savedQuoteData,
  setSavedQuoteData,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { setMessage } = useMessage();

  // Get Traditional Quote data from Redux if available, otherwise fall back to location state
  const { traditionalQuoteData } = useSelector(
    (state: RootState) => state.locationState
  );
  const { state: locationState } = location;
  const state = traditionalQuoteData || locationState;
  const referenceLimit = 5;
  const { countryCode, orderTypes } = useSelector(
    (state: RootState) => state.staticData
  );
  const { isUserOfOmniMove, userRole, isCustomer, customerLevel } = useSelector(
    (state: RootState) => state.auth
  );
  const [originCities, setOriginCities] = useState<string[]>([]);
  const [destinationCities, setDestinationCities] = useState<string[]>([]);
  // boolean states
  const [loading, setLoading] = useState<boolean>(false);
  const [groupQuoteId, setGroupQuoteId] = useState<string>(state?.id ?? "");
  const [stateQuoteId, setStateQuoteId] = useState<string>(
    state?.quoteId ?? ""
  );
  const [getQuote, setGetQuote] = useState<boolean>(false);
  const [isQuickQuoteDataReady, setIsQuickQuoteDataReady] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const initialQuoteLoadCompleteRef = useRef(false);
  const skipQuoteClearRef = useRef(false);
  const originPhoneRef = useRef<HTMLInputElement>(null);
  const destinationPhoneRef = useRef<HTMLInputElement>(null);
  const [hasMasterHub, setHasMasterHub] = useState(false);
  const [masterHubName, setMasterHubName] = useState("");
  const [lfmQuoteId, setLfmQuoteId] = useState("");
  const addressColumns = [{ key: "address", title: "" }];
  const [savedData, setSavedData] = useState<any>(null);
  const [destAddressList, setDestAddressList] = useState<any[]>([]);
  const [destAddressSelected, setDestAddressSelected] = useState<any>(null);
  const [originAddressList, setOriginAddressList] = useState<any[]>([]);
  const [originAddressSelected, setOriginAddressSelected] = useState<any>(null);
  const [masterHubAddress, setMasterHubAddress] = useState<any>({});
  const [selectedHubData, setSelectedHubData] = useState<hubData>({
    operationFrom: "",
    operationTo: "",
    hubName: "",
  });
  const [hubNameFallBack, setHubNameFallBack] = useState("");
  // modal States
  const [viewAddDocumentModal, setViewAddDocumentModal] =
    useState<boolean>(false);
  // Add item details table data states
  const [data, setData] = useState<DataTypes[]>([
    {
      id: "",
      serial: 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: "IN",
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      package: [],
      stackable: "No",
      hazmat: false,
    },
  ]);
  // get quotes table data states
  const [quoteData, SetQuoteData] = useState<
    { info: QuoteInfo; services: MappedService[] }[]
  >([]);
  const [serviceSelected, setServiceSelected] = useState<any>({});
  // Final Mile only: the full set of services to quote. serviceSelected is kept in sync
  // with the first entry so every existing single-service consumer keeps working.
  const [serviceSelectedList, setServiceSelectedList] = useState<any[]>([]);
  const [addressSelected, setAddressSelected] = useState<any>({});
  const [addressSelectedOrigin, setAddressSelectedOrigin] = useState<any>({});
  const [serviceList, setServiceList] = useState<DropwdownValueTypes[]>([]);
  const [showLTLList, setShowLTLList] = useState<boolean>(false);
  const [showFMList, setShowFMList] = useState<boolean>(false);
  const [showLTLData, setShowLTLData] = useState<boolean>(false);
  const [TLList, setLTLList] = useState<any[]>([]);
  const [isLtlFinalmile, setIsLTLFinalMile] = useState(false);
  const [saveQuotationPage, setSaveQuotationPage] = useState(false);
  const [quickQuoteLtlData, setQuickQuoteLtlData] = useState<any>(null);
  const [quickQuoteLtlFMData, setQuickQuoteLtlFMData] = useState<any>(null);
  const [ltlFMData, setLtlFMData] = useState<any>(null);
  const ltlFMOrderType: any = orderTypes.find(
    (order: any) => order?.name === "LTL + Final Mile"
  );
  const [ltlFMQuoteSelected, setLtlFMQuoteSelected] = useState<boolean>(false);
  // fields states, errors states
  const [fields, setFields] = useState<Fields>({
    orderReference: "",
    orderReference2: "",
    destinationAddress: "",
    destinationAPTSuite: "",
    destinationZipcode: "",
    destinationCity: "",
    destinationState: "",
    destinationCountry: "",
    destinationBusinessLocation: "",
    destinationConsigneeName: "",
    destinationConsigneePhoneNumber: "",
    destinationConsigneePhoneExt: "+1",
    destinationConsigneeContactEmail: "",
    OriginAddress: "",
    OriginAPTSuite: "",
    OriginZipcode: "",
    OriginCity: "",
    OriginState: "",
    OriginCountry: "",
    OriginBusinessLocation: "",
    OriginConsigneeName: "",
    OriginConsigneePhoneNumber: "",
    OriginConsigneePhoneExt: "+1",
    OriginConsigneeContactEmail: "",
    LTLPickupAccessorials: [],
    PickUpDockName: "",
    PickUpDOckNumber: "",
    PickUpDockDate: moment().format("YYYY-MM-DD"),
    PickUpDockTime: "08:00 AM",
    PickUpDockOpenTime: DEFAULT_DOCK_OPEN_TIME,
    PickUpDOckCloseTime: DEFAULT_DOCK_CLOSE_TIME,
    PickUpDOckNote: "",
    PickUpFCFS: false,
    LTLDropAccessorials: [],
    DropDockName: "",
    DropDOckNumber: "",
    DropDockDate: "",
    DropDockTime: "",
    DropDockOpenTime: DEFAULT_DOCK_OPEN_TIME,
    DropDOckCloseTime: DEFAULT_DOCK_CLOSE_TIME,
    DropDOckNote: "",
    DropFCFS: false,
    markup: 0,
    totalLinearFeet: "0.00",
    totalDensity: "0.00",
    totalCBM: "0.00",
    destinationExtramen: "",
    destinationNoOfFlights: "",
    destinationWaitTimeMinutes: "",
    destinationStorage: "",
    destinationHandingUnits: "",
    destinationWeight: "",
    destinationPieces: "",
    destinationSpecialInstructions: "",
    MidStopAddress: "",
    MidStopAPTSuit: "",
    MidStopZipcode: "",
    MidStopCity: "",
    MidStopState: "",
    MidStopCountry: "",
    MidStopContactName: "",
    MidStopPhoneNumber: "",
    MidStopPhoneNumberExt: "+1",
    courierPro1: "",
    courierPro2: "",
    plProvider: "",
    MidStopContactEmail: [],
    CarrierName: "",
    serviceCost: "",
    costDetails: [],
    scac: "",
    distance: "",
    offerId: "",
    productTransactionId: "",
    agentHubId: "",
    selectedServiceId: "",
    selectedQuoteId: "",
    customerMarkupType: "",
    allQuoteId: [],
    orderSwapAndExchange: false,
    additionalServices: [],
    removeAndDisposal: false,
    PickupTargetDateFrom: "",
    PickupTargetDateTo: "",
    PickupTargetTimeFrom: "",
    PickupTargetTimeTo: "",
    PickupDeliveryDateFrom: "",
    PickupDeliveryDateTo: "",
    PickupDeliveryTimeFrom: "",
    PickupDeliveryTimeTo: "",
    AgentHub: {
      id: "",
      name: "",
    },
    originCity: "",
    finalMileDistance: "",
    finalMileCost: "",
    totalDensityView: "",
    totalLinearFeetView: "",
    totalLinearCBMView: "",
    internalLTL: "",
    finalmile_quote_id: "",
    source: '',
    finalMileSource: '',
    isProvidence: false,
    quoteDetailId: "",
  });
  const [errors, setErrors] = useState<FormErrorsType>({});
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo>({
    files: [],
    urls: [],
  });

  // Mandatory-field validation: highlights everything missing when the user asks for a
  // quote or saves, then scrolls to and focuses the control highest on the page.
  const [invalidCells, setInvalidCells] = useState<Record<number, string[]>>({});
  const [invalidPackageCells, setInvalidPackageCells] = useState<
    Record<number, Record<number, string[]>>
  >({});
  const [itemsEmpty, setItemsEmpty] = useState(false);
  const [focusCell, setFocusCell] = useState<{
    rowIndex: number;
    columnKey: string;
  } | null>(null);
  const { applyValidation, clearMandatory } = useMandatoryFieldFocus({
    containerRef,
    setErrors,
    values: fields,
  });

  // dropdown data and selected row states
  const [parentCustomerId, setParentCustomerId] = useState<string>();
  // added for button able and disable
  const [isSelectedParent, setIsSelectedParent] = useState<boolean>(false);
  const [customerRowData, setCustomerRowData] = useState<CustomerRowDataType>({
    id: "",
    name: "",
    customerNumber: "",
    customerLevel: "",
  });
  const [apiPackageType, setApiPackageType] = useState<any>([]);
  const handleTimeChange = (time: string, property: string) => {
    setFields((prev) => ({
      ...prev,
      [property]: time,
    }));
    compareExpediteDates({ ...fields, [property]: time });
    if (property === "PickUpDockTime") {
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }
    // check error
    let error = "";
    switch (property) {
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${convertTo24HourFormat(
              fields.DropDockTime
            )}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${convertTo24HourFormat(time)}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${convertTo24HourFormat(time)}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${convertTo24HourFormat(
                fields.PickUpDockTime
              )}:00`
            );
          }
        }
        break;
      default:
        break;
    }
    if (
      !(
        property === "PickupTargetTimeFrom" ||
        property === "PickupTargetTimeTo" ||
        property === "PickupDeliveryTimeFrom" ||
        property === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [property]: error,
        };
      });
    }
  };
  const [assCustomerRowData, setAssCustomerRowData] =
    useState<CustomerRowDataType>({
      id: "",
      name: "",
      customerNumber: "",
      customerLevel: "",
    });
  const [orderTypeRowData, setOrderTypeRowData] =
    useState<OrderTypeRowDataType>({
      id: "",
      name: "",
      description: "",
    });
  // LTL + Final Mile drop dock hours are sent exactly as entered (unchanged behaviour);
  // every other order type sends them as 24-hour HH:mm like the pickup hours.
  const dropTimeForPayload = (time?: string) =>
    orderTypeRowData?.name === "LTL + Final Mile" ? time || "" : pickerTimeToStored(time);
  // Final Mile is the only order type that quotes multiple services at once.
  const isFinalMileOrderType = orderTypeRowData?.name === "Final Mile";
  // Multi-service quoting is opt-in per request. A single Final Mile selection still
  // uses it, because it also returns every serving agent ordered by distance.
  const isMultiServiceQuote =
    isFinalMileOrderType && serviceSelectedList.length > 0;
  // Final Mile Service Type (Delivery / Pickup). Delivery by default; ignored for other order types.
  const [serviceType, setServiceType] = useState<string>(LOGISTICS_TYPE.DELIVERY);
  // Latest Service Type, so a slow service-list response for a previous type is ignored.
  const serviceTypeRef = useRef<string>(LOGISTICS_TYPE.DELIVERY);
  serviceTypeRef.current = serviceType;
  const isFinalMilePickup =
    isFinalMileOrderType && serviceType === LOGISTICS_TYPE.PICKUP;
  const [customerNameDropdown, setCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [assCustomerNameDropdown, setAssCustomerNameDropdown] = useState<
    CustomerRowDataType[]
  >([]);
  const [orderTypeDropdown, setOrderTypeDropdown] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorials, setDropAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropAccessorialsSelected, setDropAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [pickupAccessorials, setPickupAccessorialDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupAccessorialsSelected, setPickupAccessorialsSelected] = useState<
    { id: string; name: string }[]
  >([]);
  const [dropLimitedAccess, setDropLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [dropLimitedAccessSelected, setDropLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();
  const [pickupLimitedAccess, setPickupLimitedAccessDropdownData] = useState<
    OrderTypeRowDataType[]
  >([]);
  const [pickupLimitedAccessSelected, setPickupLimitedAccesssSelected] =
    useState<DropwdownValueTypes>();

  const [notLTL, setNotLTL] = useState<boolean>(true);
  const [agentNameDropdown, setAgentNameDropdown] = useState<
    AgentRowDataType[]
  >([]);
  const [agentRowData, setAgentRowData] = useState<AgentRowDataType>({
    id: "",
    name: "",
    agentType: "",
  });
  const [dropdownValues, setDropdownValues] = useState<{
    [key: string]: SingleDropdownItemType | null;
  }>({
    hubName: null,
  });
  const [agentHubDropdownData, setAgentHubDropdownData] = useState<
    AgentHubDropdown[]
  >([]);
  const [agentHubRowData, setAgentHubRowData] = useState<AgentHubDropdown>();
  // unsave model
  const {
    checkInputChange,
    handleNavigation,
    handleConfirmNavigation,
    handleCancelNavigation,
    handleCustomerOrderExit,
    handleUpdateNavigation,
    showModal,
    isFormChanged,
  } = useFormChangeHandler();
  const [isQuote, setIsQuote] = useState<boolean>(false);
  const [associatedCusReqired, setAssociatedCusReqired] = useState<boolean>(
    !isUserOfOmniMove &&
      (isCustomer || userRole === "manager" || userRole === "employee" || userRole === "admin")
      ? false
      : true
  );
  const resetLocationState = () => {
    // Clear the location state by navigating to the same route with empty state
    setIsSelectedParent(false);
    // Clear the Redux state for Traditional Quote data
    dispatch(clearTraditionalQuoteData());
    navigate(location.pathname, {
      state: null,
      replace: true, // This replaces the current history entry instead of adding a new one
    });
  };
  const [moreReferences, setMoreReferences] = useState<MoreReferencesType[]>([
    {
      referenceCount: 2,
      referenceValue: "",
    },
  ]);

  // Reference 2 carried by a loaded quote, or null when the form was not populated
  // from one. While it is set the loaded value survives, unless the governing
  // customer has the setting on and overrides it. Cleared as soon as the user picks
  // a customer themselves, at which point that customer alone decides the value.
  const loadedOrderReference2Ref = useRef<string | null>(null);

  /** User changed a customer selection — the loaded record no longer applies. */
  const clearLoadedOrderReference2 = () => {
    loadedOrderReference2Ref.current = null;
  };

  /**
   * Origin/destination addresses come from the selected customer's address book, so they
   * no longer apply once the user picks a different customer. Only called from the
   * dropdown handlers (never from quote loads) so prefilled quote addresses survive.
   */
  const resetCustomerLinkedAddresses = () => {
    setAddressSelectedOrigin({});
    setAddressSelected({});
    setOriginAddressList([]);
    setDestAddressList([]);
    setFields((prev) => ({
      ...prev,
      OriginAddress: "",
      OriginAddressId: "",
      OriginAPTSuite: "",
      OriginZipcode: "",
      OriginCity: "",
      OriginState: "",
      OriginCountry: "",
      OriginBusinessLocation: "",
      OriginConsigneeName: "",
      OriginConsigneePhoneNumber: "",
      OriginConsigneePhoneExt: "+1",
      OriginConsigneeContactEmail: "",
      destinationAddress: "",
      destinationAddressId: "",
      destinationAPTSuite: "",
      destinationZipcode: "",
      destinationCity: "",
      destinationState: "",
      destinationCountry: "",
      destinationBusinessLocation: "",
      destinationConsigneeName: "",
      destinationConsigneePhoneNumber: "",
      destinationConsigneePhoneExt: "+1",
      destinationConsigneeContactEmail: "",
    }));
  };

  // Live Order Reference 2 configuration read from the customer endpoint when the
  // form is populated from a quote. Rows selected through a dropdown already carry
  // the setting (see extraFields), so this stays empty for normal order creation.
  const [orderReference2Config, setOrderReference2Config] =
    useState<OrderReference2ConfigMap>({});

  // Quote loads set the customer rows from the quote payload, which has no Order
  // Reference 2 setting. Merge the configuration in as soon as it is available —
  // whichever arrives last — so the prefill effect below can act on it.
  useEffect(() => {
    if (!customerRowData?.id || customerRowData?.isUsedOrderReference2 !== undefined) return;
    const config = orderReference2Config[customerRowData.id];
    if (config) setCustomerRowData({ ...customerRowData, ...config });
  }, [customerRowData?.id, orderReference2Config]);

  useEffect(() => {
    if (!assCustomerRowData?.id || assCustomerRowData?.isUsedOrderReference2 !== undefined) return;
    const config = orderReference2Config[assCustomerRowData.id];
    if (config) setAssCustomerRowData({ ...assCustomerRowData, ...config });
  }, [assCustomerRowData?.id, orderReference2Config]);

  // Customer that governs Order Reference 2 (sub-customer wins over its parent).
  const orderReference2Source = useMemo(
    () =>
      customerRowData
        ? resolveOrderReference2Source(customerRowData, assCustomerRowData)
        : null,
    [customerRowData, assCustomerRowData]
  );

  // When the governing customer has "Use Order Reference 2" enabled, the value is
  // fixed by their configuration and cannot be edited or removed on the order.
  const isReference2Locked = isOrderReference2Locked(orderReference2Source);

  useEffect(() => {
    // Clear field when customer is explicitly removed
    if (customerRowData === null) {
      setMoreReferences((prev) => {
        const updated = [...prev];
        if (updated.length > ORDER_REFERENCE_2_INDEX) {
          updated[ORDER_REFERENCE_2_INDEX] = {
            ...updated[ORDER_REFERENCE_2_INDEX],
            referenceValue: "",
          };
        }
        return updated;
      });
      return;
    }

    // Only act when the customer object carries the setting (isUsedOrderReference2
    // explicitly defined). Edit-load flows set a partial object without this key
    // (undefined), so we skip to avoid wiping the reference2 loaded from the order.
    if (!orderReference2Source) return;

    const prefillValue = getOrderReference2Value(
      orderReference2Source,
      loadedOrderReference2Ref.current
    );
    setMoreReferences((prev) => {
      const updated = [...prev];
      if (updated.length > ORDER_REFERENCE_2_INDEX) {
        updated[ORDER_REFERENCE_2_INDEX] = {
          ...updated[ORDER_REFERENCE_2_INDEX],
          referenceValue: prefillValue,
        };
      }
      return updated;
    });
  }, [customerRowData, assCustomerRowData, orderReference2Source]);

  //Normalize date by converting to utc
  const normalizeDate = (date: any) => {
    return moment(date).format("YYYY-MM-DD");
  };

  const getUTCDate = (dateStr: string, timeStr: string): string => {
    const combinedDateTime = moment(
      `${dateStr} ${timeStr}`,
      "YYYY-MM-DD hh:mm:ss A"
    );
    return combinedDateTime.isValid()
      ? combinedDateTime.toISOString()
      : "Invalid date or time";
    // const newDate = moment.utc(`${dateStr.split("T")[0]} ${timeStr}`).format('YYYY-MM-DDTHH:mm:ss')+"Z";
    // return newDate;
  };

  const getUTCTime = (dateStr: string, timeStr: string): string => {
    let newDate = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    const newTime = moment.utc(`${newDate} ${timeStr}`).format("hh:mm A");
    return newTime;
  };

  const mapLTLItems = (data: any[]) => {
    return data.map((item) => ({
      billedDimension: {
        length: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.length || ""
              : cmToInch(item.itemDimension?.length),
          unit: "IN",
        },
        width: {
          value:
            item.unit === "in" || item.unit.name === "in"
              ? item.itemDimension?.width || ""
              : cmToInch(item.itemDimension?.width),
          unit: "IN",
        },
        height: {
          value:
            item?.unit === "in" || item?.unit?.name === "in"
              ? item.itemDimension?.height || ""
              : cmToInch(item.itemDimension?.height),
          unit: "IN",
        },
      },
      isMixedClass: false,
      isStackable:
        item?.stackable === "Yes" ||
          item?.stackable?.name === "Yes" ||
          item?.stackable?.name === "yes"
          ? true
          : false || false,
      marksAndNumbers: null,
      linearFeet: item?.linearFeet?.toString(),

      packagingType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      quantity: parseInt(item.quantity) || 0,
      referenceList: mapReferences(false),
      shippedItemList: [
        {
          commodityClass: item.itemClass.toString() || "5",
          commodityDescription: item.description || "",
          commodityType: null,
          dimensions: {
            length: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.length || ""
                  : cmToInch(item.itemDimension?.length),
              unit: "IN",
            },
            width: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.width || ""
                  : cmToInch(item.itemDimension?.width),
              unit: "IN",
            },
            height: {
              value:
                item?.unit === "in" || item?.unit?.name === "in"
                  ? item.itemDimension?.height || ""
                  : cmToInch(item.itemDimension?.height),
              unit: "IN",
            },
          },
          isHazMat: item.hazmat,
          hazMatItemInfo: item.hazmat
            ? {
              hazIdentificationNbr: item.hazardMaterial?.unno || "",
              hazProperShippingName: item.hazardMaterial?.itemName || "",
              hazClassType: item.hazardMaterial?.hazmatClass || "",
              hazPackingGroupType: item.hazardMaterial?.packagingGroup || "",
              hazEmergencyPhoneNbr:
                item.hazardMaterial?.phoneNumber.replace(/\D/g, "") || "",
              hazEmergencyName: item.hazardMaterial?.contact || "",
              hazPlacardRequiredFlag: false,
              hazPlacardDetails: "Placard Details",
              hazERR: "ERR Number",
              hazFlashpointTemperature: {
                value: 1,
                unit: "F",
              },
              hazAdditionalDetails: "Additional Haz Details",
            }
            : {},
          name: "",
          NMFCDescription: null,
          NMFCNbr: item.nmfc || "",
          packagingType: item.packageType || "",
          quantity: parseInt(item.quantity) || 0,
          weight: {
            value:
              item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
                ? item.weight || ""
                : kgToLbs(item.weight),
            unit: "LB",
          },
        },
      ],
      sortAndSegregateFlag: true,
      weight: {
        value:
          item?.weightUnit === "lbs" || item?.weightUnit?.name === "lbs"
            ? item.weight || ""
            : kgToLbs(item.weight),
        unit: "LB",
      },
    }));
  };

  const mapReferences = (shipment: boolean) => {
    let references = [
      {
        type: `${shipment ? "Shipment " : ""}Reference 1`,
        value: fields.orderReference,
        isPrintAsBarCode: true,
      },
    ];

    if (moreReferences.length > 0) {
      moreReferences.map((ref, index) => {
        if (ref["referenceValue"] != "") {
          references.push({
            type: `${shipment ? "Shipment " : ""}Reference ${index + 2}`,
            value: ref["referenceValue"],
            isPrintAsBarCode: false,
          });
        }
      });
    }
    return references;
  };

  const getLTLFinalMileQuote = async (body: any) => {
    const apiPayload = {
      quoteDetail: {
        customerId: assCustomerRowData?.id || customerRowData?.id,
        serviceIds: [serviceSelected?.id],
        items: mapItemsDataToPayload(data),
        source: fields?.source,
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
          accessorials: mapAccessorials(dropAccessorialsSelected),
          locationType: dropLimitedAccessSelected?.name || "",
        },
        originAddress: {
          address: fields.OriginAddress,
          aptSuit: fields.OriginAPTSuite,
          zipCode: fields.OriginZipcode,
          city: fields.OriginCity,
          state: fields.OriginState,
          country: fields.OriginCountry,
          businessLocation: fields.OriginBusinessLocation,
          contactName: fields.OriginConsigneeName,
          phoneNumber: fields.OriginConsigneePhoneNumber,
          countryCallingCode: fields.OriginConsigneePhoneExt,
          contactEmail: fields.OriginConsigneeContactEmail,
          accessorials: mapAccessorials(pickupAccessorialsSelected),
          locationType: pickupLimitedAccessSelected?.name || "",
        },
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: getISODate(
            fields.PickUpDockDate || "",
            fields.PickUpDockTime || ""
          ),
          pickUpTime: fields.PickUpDockTime || "",
          note: fields.PickUpDOckNote || "",
          dockOpenTime: fields.PickUpDockOpenTime
            ? convertTo24HourFormat(fields.PickUpDockOpenTime)
            : "",
          dockCloseTime: fields.PickUpDOckCloseTime
            ? convertTo24HourFormat(fields.PickUpDOckCloseTime)
            : "",
          dockOpenTimeInUTC: getDockTimeInUTC(
            fields.PickUpDockDate,
            fields.PickUpDockOpenTime
              ? convertTo24HourFormat(fields.PickUpDockOpenTime)
              : ""
          ),
          dockCloseTimeInUTC: getDockTimeInUTC(
            fields.PickUpDockDate,
            fields.PickUpDOckCloseTime
              ? convertTo24HourFormat(fields.PickUpDOckCloseTime)
              : ""
          ),
          firstComeFirstServe: fields.PickUpFCFS,
        },
        dropDock: {
          dockName: fields.DropDockName || "",
          pickupNumber: fields.DropDOckNumber || "",
          note: fields.DropDOckNote || "",
          dropOpenTime: fields.DropDockOpenTime
            ? convertTo24HourFormat(fields.DropDockOpenTime)
            : "",
          dropCloseTime: fields.DropDOckCloseTime
            ? convertTo24HourFormat(fields.DropDOckCloseTime)
            : "",
          dockOpenTimeInUTC: getDockTimeInUTC(
            fields.DropDockDate,
            fields.DropDockOpenTime
              ? convertTo24HourFormat(fields.DropDockOpenTime)
              : ""
          ),
          dockCloseTimeInUTC: getDockTimeInUTC(
            fields.DropDockDate,
            fields.DropDOckCloseTime
              ? convertTo24HourFormat(fields.DropDOckCloseTime)
              : ""
          ),
          firstComeFirstServe: fields.DropFCFS,
        },
        "quoteFromScreen": "order",
        additionalServices: fields.additionalServices,
      },
      ltlQuoteDetail: body,
    };
    try {
      setLoading(true);
      const apiResponse = await postApi<GetLTLFinalMileQuoteApiResponse>(
        `/order/order-quote/generate-ltlplusfinalmile-quote`,
        apiPayload
      );
      if (apiResponse.success) {

        const data = apiResponse?.data || {};
        const finalMileData = apiResponse?.data?.selectedFinalMileQuote;
        setShowLTLList(true);
        setLfmQuoteId(finalMileData?.service?.quoteId);
        const ltlDetails = mapLtlFinalMileList(data);
        setIsLTLFinalMile(true);
        setLTLList(ltlDetails);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };
  const getLTLQuotesData = async () => {
    if (!runValidation("getLtlQuote")) return;
    resetLocationState();
    setStateQuoteId("");
    setShowLTLData(false);
    setShowFMList(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
    const body = {
      request: {
        productType: "LTL",
        quoteDetail: {
          customerId: assCustomerRowData?.id
            ? assCustomerRowData?.id
            : customerRowData?.id,
        },
        shipment: {
          shipmentDate:
            fields.PickUpDockDate !== "" && fields.PickUpDockTime !== ""
              ? getISODate(fields.PickUpDockDate, fields.PickUpDockTime)
              : fields.PickUpDockDate !== ""
                ? getISODate(fields.PickUpDockDate, moment().format("hh:mm A"))
                : "",
          originAddress: {
            address: {
              addressLineList:
                fields.OriginAddress && fields.OriginAPTSuite
                  ? [fields.OriginAddress, fields.OriginAPTSuite]
                  : fields.OriginAddress
                    ? [fields.OriginAddress]
                    : [],
              locality: fields.OriginCity,
              region: fields.OriginState,
              postalCode: fields.OriginZipcode,
              countryCode: fields.OriginCountry,
              companyName: fields.OriginBusinessLocation,
              phone: fields.OriginConsigneePhoneNumber
                ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                : "",
              contactList: [
                {
                  phone: fields.OriginConsigneePhoneNumber
                    ? `${fields.OriginConsigneePhoneNumber.replace(/\D/g, "")}`
                    : "",
                  firstName: fields.OriginConsigneeName || "",
                },
              ],
              locationType:
                (pickupLimitedAccessSelected &&
                  pickupLimitedAccessSelected?.name) ||
                "",
            },
          },
          destinationAddress: {
            // frontend
            address: {
              addressLineList:
                fields.destinationAddress != "" &&
                  fields.destinationAPTSuite != ""
                  ? [fields.destinationAddress, fields.destinationAPTSuite]
                  : fields.destinationAddress != ""
                    ? [fields.destinationAddress]
                    : [],
              locality: fields.destinationCity,
              region: fields.destinationState,
              postalCode: fields.destinationZipcode,
              countryCode: fields.destinationCountry,
              companyName: fields.destinationBusinessLocation,
              phone: fields.destinationConsigneePhoneNumber
                ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                : "",
              contactList: [
                {
                  phone: fields.destinationConsigneePhoneNumber
                    ? fields.destinationConsigneePhoneNumber.replace(/\D/g, "")
                    : "",
                  firstName: fields.destinationConsigneeName || "",
                },
              ],
              locationType:
                (dropLimitedAccessSelected &&
                  dropLimitedAccessSelected?.name) ||
                "",
            },
          },
          handlingUnitList: mapLTLItems(data),
          totalWeight: {
            value:
              data[0]?.unit === "in"
                ? fields.destinationWeight || ""
                : kgToLbs(fields.destinationWeight),
            unit: "LB",
          },
          insuranceRequestFlag: false,
          totalHandlingUnitCount: fields.destinationHandingUnits,
          shipmentReferenceList: fields.orderReference
            ? mapReferences(true)
            : [],
          appointmentDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Appointment"
          ),
          directDeliveryOnlyFlag: null,
          holdAtTerminalFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Hold"
          ),
          insideDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Inside Delivery"
          ),
          insidePickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Inside Pickup"
          ),
          carrierTerminalPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Airport Pickup"
          ),
          liftgateDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "LiftgateDelivery"
          ),
          liftgatePickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Liftgate"
          ),
          notifyBeforeDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Delivery Notification"
          ),
          protectionFromColdFlag: false,
          residentialDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Residential"
          ),
          residentialPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Residential"
          ),
          sortAndSegregateFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Sort and Segregate"
          ),
          pickupSpecialInstructions: fields.PickUpDOckNote || "",
          deliverySpecialInstructions: fields.DropDOckNote || "",
          tradeshowDeliveryFlag: dropAccessorialsSelected.some(
            (x) => x.name === "Tradeshow Delivery"
          ),
          tradeshowDeliveryName: "",
          tradeshowPickupFlag: pickupAccessorialsSelected.some(
            (x) => x.name === "Tradeshow Pickup"
          ),
          tradeshowPickupName: "",
        },
      },
      correlationId: "WWEX-M2M-shopFlow-ltl",
    };
    if (orderTypeRowData && orderTypeRowData.name != "LTL") {
      getLTLFinalMileQuote(body);
      return;
    }
    try {
      setLoading(true);
      const apiResponse = await postApi<GetLTLQuoteApiResponse>(
        `/order/order-quote/generateltlquote`,
        body
      );
      if (apiResponse.success) {
        const data = apiResponse?.data || { worldWideQuote: [] };
        setShowLTLList(true);
        const ltlDetails = mapLtlList(data.worldWideQuote);
        setIsLTLFinalMile(false);
        setLTLList(ltlDetails);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };
  // const mapLtlFinalMileListOld = (LTLdata: any): any[] => {
  //   if (
  //     LTLdata?.selectedFinalMileQuote &&
  //     LTLdata?.ltlPlusFinalMileQuote.length
  //   ) {
  //     
  //     const hubName = LTLdata?.selectedFinalMileQuote?.hub?.agent?.name || "";
  //     const quotePrice =
  //       LTLdata?.selectedFinalMileQuote?.service?.quotePrice || "0";
  //     const basePrice =
  //       LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.basePrice || "0";
  //     const quoteId = LTLdata?.selectedFinalMileQuote?.service?.quoteId || "0";
  //     const distance =
  //       LTLdata?.selectedFinalMileQuote?.service?.distanceToDestination || "0";
  //     const fuelSurcharge = LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.customerFuelSurcharge ||
  //       LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.fuelSurcharge ||
  //       "0";
  //     const finalMileAdditionalCharges =
  //       LTLdata?.selectedFinalMileQuote?.service?.rateDetails
  //         ?.additionalOtherCharges;
  //     setSelectedHubData({
  //       hubName: LTLdata?.selectedFinalMileQuote?.hub?.name,
  //       operationFrom:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.operationHrsFrom ||
  //         "00:00",
  //       operationTo:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.operationHrsTo ||
  //         "23:59",
  //     });
  //     setFields((prevData: any) => ({
  //       ...prevData,
  //       AgentHub: {
  //         id: LTLdata?.selectedFinalMileQuote?.hub?.id || "",
  //         name: hubName,
  //       },

  //       selectedQuoteId: quoteId,
  //       MidStopAddress:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.address || "",
  //       MidStopAPTSuit:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.aptSuit || "",
  //       MidStopZipcode:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.zipCode || "",
  //       MidStopCity: LTLdata?.selectedFinalMileQuote?.hub?.address?.city || "",
  //       MidStopState:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.state || "",
  //       MidStopCountry:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.country || "",
  //       MidStopContactName:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.contactPerson || "",
  //       MidStopPhoneNumber:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.phoneNumber || "",
  //       MidStopPhoneNumberExt:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.countryCallingCode ||
  //         "",
  //       MidStopContactEmail:
  //         LTLdata?.selectedFinalMileQuote?.hub?.address?.contactEmail || [],
  //       finalMileDistance: distance,
  //       finalMileCost: quotePrice,
  //     }));

  //     return LTLdata?.ltlPlusFinalMileQuote?.map((item: any, index: any) => {
  //       let banyanArr: any[] = [];
  //       if (item?.quoteFrom === "banyan") {
  //         banyanArr = [
  //           { name: "Freight Charge", amount: item?.customerPrice?.freightCharge },
  //           { name: "Fuel Surcharge", amount: item?.customerPrice?.fuelSurcharge },
  //           ...(item?.customerPrice?.charges || []),
  //         ];
  //       }

  //       return {
  //         count: index + 1,
  //         carrierName: item?.internalLTL
  //           ? item?.hub?.agent?.name
  //           : item?.carrierNameToDisplay || `${item?.primaryVendor?.preferredName}`,
  //         finalmileCarrierName: `${hubName}`,
  //         scac: item?.internalLTL
  //           ? item?.hub?.agent?.carrierCode
  //           : item?.scac || item?.primaryVendor?.scac,
  //         basePrice: basePrice,
  //         fuelSurcharge: fuelSurcharge,
  //         additionalOtherCharges: finalMileAdditionalCharges,
  //         price: item?.internalLTL
  //           ? item?.services?.[0]?.rateDetails?.total
  //           : item?.totalOfferPrice?.value || "",
  //         priceWithMarkUp: item?.totalOfferPriceWithMarkUp || item?.totalOfferPriceWithMarkupAndFinalMile
  //           ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2) || item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
  //           : "0",
  //         priceWithFinalMile: item?.totalOfferPriceWithMarkupAndFinalMile
  //           ? parseFloat(item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
  //           : "0",
  //         selectedQuoteId: item?.internalLTL ? item?.services?.[0]?.quoteId : quoteId,
  //         parentQuoteId: item?.quoteId,
  //         internalLTL: item?.internalLTL,
  //         priceWithMarkUpString: `$${item?.totalOfferPriceWithMarkupAndFinalMile
  //           ? parseFloat(item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
  //           : parseFloat(item?.totalOfferPrice?.value.toFixed(2))}`,
  //         quotePrice: parseFloat(quotePrice).toFixed(2),
  //         finalMileData: item?.finalMileData,
  //         customerMarkupType: item?.customerMarkUpType,
  //         markup: item?.customerMarkUpValue || 0,
  //         mode: serviceSelected?.name,
  //         source: item?.source,
  //         additionalCharges: banyanArr.length
  //           ? banyanArr
  //           : item?.offeredProductList?.[0].chargeItemList ||
  //           item?.services?.[0]?.rateDetails?.additionalOtherCharges?.calculatedCharges,
  //         productTransactionId: item?.internalLTL ? item?.services?.[0]?.quoteId : item?.productTransactionId,
  //         offerId: item?.internalLTL ? item?.services?.[0]?.quoteId : item?.offerId,
  //         quoteFrom: item?.source ? item?.source : item?.banyanType ? item?.quoteFrom + " - " + item?.banyanType : item?.quoteFrom || "Internal",
  //         banyanType: item?.banyanType,
  //         loadId: item?.loadId,
  //         miles: item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit?.milesInTransit,
  //         transitTime: item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit?.transitDays || item?.transitDays || item?.transitTime,
  //       };
  //     });
  //   } else {
  //     return [];
  //   }
  // };


  const mapLtlFinalMileList = (LTLdata: any): any[] => {
    if (
      LTLdata?.selectedFinalMileQuote &&
      LTLdata?.ltlPlusFinalMileQuote.length
    ) {

      const hubName = LTLdata?.selectedFinalMileQuote?.hub?.agent?.name || "";
      const selectedQuoteData = LTLdata?.selectedFinalMileQuote;
      const quotePrice =
        LTLdata?.selectedFinalMileQuote?.service?.quotePrice || "0";
      const basePrice =
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.basePrice || "0";
      const quoteId = LTLdata?.selectedFinalMileQuote?.service?.quoteId || "0";
      const distance =
        LTLdata?.selectedFinalMileQuote?.service?.distanceToDestination || "0";
      const fuelSurcharge = LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.customerFuelSurcharge ||
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails?.fuelSurcharge ||
        "0";
      const finalMileAdditionalCharges =
        LTLdata?.selectedFinalMileQuote?.service?.rateDetails
          ?.additionalOtherCharges;

      // Check if selectedFinalMileQuote has a masterHubData
      const fmQuoteMasterHubData = LTLdata?.selectedFinalMileQuote?.masterHubData;
      const fmQuoteHasMasterHub = !!fmQuoteMasterHubData;
      const fmQuoteMasterHubAddress = fmQuoteMasterHubData?.address;
      const fmQuoteMasterHubName = fmQuoteMasterHubData?.name || "";
      if (fmQuoteHasMasterHub && fmQuoteMasterHubAddress) {
        setHasMasterHub(true);
        setMasterHubName(fmQuoteMasterHubName);
        setMasterHubAddress(fmQuoteMasterHubAddress);
      }

      // Use masterHub address if present, otherwise fall back to hub address
      const effectiveFmAddress = fmQuoteHasMasterHub && fmQuoteMasterHubAddress
        ? fmQuoteMasterHubAddress
        : LTLdata?.selectedFinalMileQuote?.hub?.address;

      setSelectedHubData({
        hubName: fmQuoteHasMasterHub ? fmQuoteMasterHubName : LTLdata?.selectedFinalMileQuote?.hub?.name,
        operationFrom:
          effectiveFmAddress?.operationHrsFrom ||
          "00:00",
        operationTo:
          effectiveFmAddress?.operationHrsTo ||
          "23:59",
      });

      setFields((prevData: any) => ({
        ...prevData,
        AgentHub: {
          id: LTLdata?.selectedFinalMileQuote?.hub?.id || "",
          name: LTLdata?.selectedFinalMileQuote?.hub?.name || "",
        },

        selectedQuoteId: quoteId,
        MidStopAddress: effectiveFmAddress?.address || "",
        MidStopAPTSuit: effectiveFmAddress?.aptSuit || "",
        MidStopZipcode: effectiveFmAddress?.zipCode || "",
        MidStopCity: effectiveFmAddress?.city || "",
        MidStopState: effectiveFmAddress?.state || "",
        MidStopCountry: effectiveFmAddress?.country || "",
        MidStopContactName: effectiveFmAddress?.contactPerson || "",
        MidStopPhoneNumber: effectiveFmAddress?.phoneNumber || "",
        MidStopPhoneNumberExt: effectiveFmAddress?.countryCallingCode || "",
        MidStopContactEmail: effectiveFmAddress?.contactEmail || [],
        finalMileDistance: distance,
        finalMileCost: quotePrice,
      }));

      const row = LTLdata?.ltlPlusFinalMileQuote?.map(
        (item: any, index: any) => {
          // Add banyan array logic here
          let banyanArr: any[] = [];
          if (item?.quoteFrom === "banyan") {
            banyanArr = [
              { name: "Freight Charge", amount: item?.customerPrice?.freightCharge },
              { name: "Fuel Surcharge", amount: item?.customerPrice?.fuelSurcharge },
              ...item?.customerPrice?.charges,
            ];
          }

          // Internal LTL rows carry their own mapped Final Mile quote; other rows
          // carry the shared one.
          const rowFm = item?.finalMileData ?? selectedQuoteData;
          const rowFmRate = rowFm?.service?.rateDetails;

          return {
            count: index + 1,
            selectedFinalMileQuote: rowFm,
            carrierName: item?.carrierNameToDisplay ||
              item?.primaryVendor?.preferredName || `${item?.hub?.agent?.name}`,
            finalmileCarrierName: `${rowFm?.hub?.agent?.name || hubName}`,
            internalLTLPopUpDetails: item?.services?.[0] || {},
            scac: item?.scac || item?.primaryVendor?.scac || item?.hub?.agent?.carrierCode,
            basePrice: rowFmRate?.basePrice || basePrice,
            fuelSurcharge: rowFmRate?.customerFuelSurcharge || rowFmRate?.fuelSurcharge || fuelSurcharge,
            additionalOtherCharges: rowFmRate?.additionalOtherCharges ?? finalMileAdditionalCharges,
            price: item?.totalOfferPrice?.value || "",
            priceWithMarkUp: item?.totalOfferPriceWithMarkUp || item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2) || item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
              : "0",
            priceWithFinalMile: item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2))
              : "0",
            finalMileData: item?.finalMileData,
            quoteId: item?.internalLTL ? item?.services?.[0]?.quoteId : quoteId,
            selectedQuoteId: item?.internalLTL ? item?.services?.[0]?.quoteId : quoteId,
            parentQuoteId: item?.quoteId,
            priceWithMarkUpString: `$${item?.totalOfferPriceWithMarkupAndFinalMile
              ? parseFloat(
                item?.totalOfferPriceWithMarkupAndFinalMile.toFixed(2)
              )
              : parseFloat(item?.totalOfferPrice?.value.toFixed(2))
              }`,
            quotePrice: parseFloat(rowFm?.service?.quotePrice || quotePrice).toFixed(2),
            customerMarkupType: item?.customerMarkUpType,
            markup: item?.customerMarkUpValue || 0,
            mode: serviceSelected?.name,
            additionalCharges: banyanArr.length ? banyanArr :
              item?.offeredProductList?.[0].chargeItemList ||
              item?.services?.[0]?.rateDetails?.additionalOtherCharges
                ?.calculatedCharges,
            lineItems: item?.lineItems || [],
            orderFor: item?.quoteFrom,
            carrierQuoteId: item?.carrierQuoteId,
            rateRequestLocator: item?.rateRequestLocator,
            productTransactionId: item?.internalLTL
              ? item?.services?.[0]?.quoteId
              : item?.productTransactionId,
            offerId: item?.internalLTL
              ? item?.services?.[0]?.quoteId
              : item?.offerId,
            miles:
              item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
                ?.milesInTransit,
            transitTime:
              item?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
                ?.transitDays ||
              item?.transitTime ||
              item?.transitDays ||
              "1",
            ltlFMData: rowFm,
            pickupAccessorials: pickupAccessorialsSelected,
            dropAccessorials: dropAccessorialsSelected,
            pickupLimitedAccess: pickupLimitedAccessSelected,
            dropLimitedAccess: dropLimitedAccessSelected,
            internalLTL: item?.internalLTL,
            isBanyan: item?.banyanType,
            isProvidence: item?.quoteFrom?.toLowerCase() === "providence",
            banyanType: item?.banyanType,
            providenceType: item?.quoteFrom?.toLowerCase() === "providence" ? "providence" : undefined,
            loadId: item?.loadId,
            quoteFrom: item?.source ? item?.source : item?.banyanType ? item?.quoteFrom + " - " + item?.banyanType : item?.quoteFrom || "Internal",
            displayQuoteFrom: item?.displayQuoteFrom,
            source: item?.source || "direct",
            finalMileSource: item?.finalMileData?.source,
            // Add banyan array to the returned object
            ...(item?.internalLTL && {
              services: item?.internalLTL,
              quotePrice: item?.services?.[0]?.quotePrice,
              fuelSurcharge: item?.services?.[0]?.rateDetails?.customerFuelSurcharge || 0.0,
              additionalOrderCharges: item?.services?.[0]?.rateDetails
                ?.finalMilePricing
                ? item?.services?.[0]?.rateDetails?.customerOtherChargeDetails
                : item?.services?.[0]?.rateDetails?.additionalOtherCharges,
              basePrice: item?.services?.[0]?.rateDetails?.basePrice || 0.0,
              agentName: item?.hub?.agent?.name,
              serviceId: item?.services?.[0]?.id,
            }),
            ...(item?.quoteFrom?.toLowerCase() === "providence" && {

              ltlBaseRate: item?.baseRate || 0,                  // LTL base rate
              ltlFuel: item?.fuel || 0,                          // LTL fuel surcharge
              ltlAccessorialCharges: item?.accessorialCharges || 0, // LTL accessorials
              ltlTotalRate: item?.totalRate || 0,
              quoteDetailId: item?.quoteDetailId || "",
            }
            )

          };

        }
      );
      return row;
    } else {
      return [];
    }
  };
  const convertTo24HourFormat = (time: string): string => {
    return moment(time, "h:mm A").format("HH:mm");
  };
  const mapLtlList = (LTLdata: any[]): any[] => {
    if (LTLdata) {
      return LTLdata?.map((item, index) => ({
        count: index + 1,
        carrierName: item?.primaryVendor?.preferredName,
        scac: item?.primaryVendor?.scac,
        price: item?.totalOfferPrice?.value || "",
        priceWithMarkUp: item?.totalOfferPriceWithMarkUp
          ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2))
          : parseFloat(item?.totalOfferPrice?.value.toFixed(2)),
        priceWithMarkUpString: `$${item?.totalOfferPriceWithMarkUp
          ? parseFloat(item?.totalOfferPriceWithMarkUp.toFixed(2))
          : parseFloat(item?.totalOfferPrice?.value.toFixed(2))
          }`,
        customerMarkupType: item?.customerMarkUpType,
        markup: item?.customerMarkUpValue || 0,
        mode: serviceSelected.name,
        additionalCharges: item?.offeredProductList[0].chargeItemList,
        productTransactionId: item?.productTransactionId,
        offerId: item?.offerId,
        miles:
          item?.offeredProductList[0]?.shopRQShipment?.timeInTransit
            ?.milesInTransit,
        //distance, offer id, transactionid,
        transitTime:
          item?.offeredProductList[0]?.shopRQShipment?.timeInTransit
            ?.transitDays,
      }));
    } else {
      return [];
    }
  };
  /**
   * Prevents invalid characters from being entered in numeric fields.
   *
   * @param {KeyboardEvent<HTMLInputElement>} e - The keyboard event for the input
   */
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "e" || e.key === "E" || e.key === "-" || e.key === "+") {
      e.preventDefault();
    }
  };

  /**
   * handleAddItemDetails
   *
   * Adds a new row to the item details table.
   */
  const handleAddItemDetails = () => {
    const newRow = {
      id: "",
      serial: data.length + 1,
      description: "",
      handlingUnit: "",
      quantity: "",
      weight: "",
      weightUnit: { id: "lbs", name: "LBS" },
      itemDimension: {
        length: "",
        width: "",
        height: "",
      },
      unit: { id: "IN", name: "IN" },
      itemClass: "",
      comments: "",
      nmfc: "",
      skuNumber: "",
      packageType: "",
      linearFeet: "",
      package: [],
      stackable: { id: "No", name: "No" },
      hazmat: false,
    };
    setData([newRow, ...data]);
  };
  /**
   * handleBack
   *
   * Handles the back navigation logic, checking if there are unsaved changes.
   */
  const handleBack = () => {
    if (isFormChanged) {
      handleNavigation();
    } else {
      navigate(-1);
    }
  };

  const handlePhoneBlur = async (
    phoneNumber: string,
    phoneExt: string,
    fieldName: string
  ): Promise<boolean> => {
    const phoneMessages: Record<string, { required: string; valid: string }> = {
      OriginConsigneePhoneNumber: {
        required: "Phone number is required.",
        valid: "Please enter a valid phone number.",
      },
      destinationConsigneePhoneNumber: {
        required: "Consignee phone number is required.",
        valid: "Please enter a valid consignee phone number.",
      },
    };
    const messages = phoneMessages[fieldName] ?? {
      required: "Phone number is required.",
      valid: "Please enter a valid phone number.",
    };
    const rawPhone = phoneNumber.replace(/\D/g, "");
    if (!rawPhone) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: messages.required,
      }));
      return false;
    }
    const countryData = (countryCode as any[])?.find(
      (item: any) => item.countryCallingCode === phoneExt
    );
    const countryShortName = countryData?.shortName || "US";
    const e164Phone = `${phoneExt}${rawPhone}`;
    const response = await getApi<any>(
      `${PHONE_VALIDATE_API}?phoneNumber=${encodeURIComponent(e164Phone)}&countryCode=${countryShortName}`
    );
    if (!response.success || !response.data?.isValid) {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: messages.valid,
      }));
      return false;
    } else {
      setErrors((prev) => ({
        ...prev,
        [fieldName]: "",
      }));
      return true;
    }
  };

  /**
   * handleFieldChange
   *
   * Handles changes to form input fields, updating the state and checking for errors.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e - The change event object from the input field.
   */
  const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement> | any) => {
    const { name, value } = e.target;
    let updatedValue = value;

    if (name === "destinationConsigneeName" || name === "OriginConsigneeName") {
      // Allow only alphabetic characters and special characters, no numeric values
      updatedValue = value.replace(/[0-9]/g, "");
    }
    if (
      name === "destinationZipcode" ||
      name === "OriginZipcode" ||
      name === "PickUpDockDate" ||
      name === "PickUpDockTime"
    ) {
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
      setFields((prevData) => ({
        ...prevData,
        CarrierName: "",
        serviceCost: "",
        scac: "",
        distance: "",
        offerId: "",
        productTransactionId: "",
      }));
      setGetQuote(false);
    }

    // Prevent leading whitespace
    updatedValue = updatedValue.trimStart();

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    setFields((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    checkInputChange();
    compareExpediteDates({ ...fields, [name]: updatedValue });
    // check error
    let error = "";
    switch (name) {
      case "destinationConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(value, fields.destinationConsigneePhoneExt);
        break;
      case "OriginConsigneePhoneNumber":
        error = validateMaskedPhoneNumber(value, fields.OriginConsigneePhoneExt);
        break;
      case "destinationZipcode":
        error = validateZipCode(value);
        break;
      case "OriginZipcode":
        error = validateZipCode(value);
        break;
      case "PickUpDockDate":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockTime: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.PickUpDockTime != "" &&
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,
            "Pickup Date & Time should be earlier than Drop date & Time",
            `${value} ${fields.PickUpDockTime}:00`
          );
        }

        break;
      case "PickUpDockTime":
        setErrors((prevErrors) => ({
          ...prevErrors,
          PickUpDockDate: "",
          DropDockDate: "",
          DropDockTime: "",
        }));
        if (
          fields.DropDockDate != "" &&
          fields.DropDockTime != ""
        ) {
          error = ValidateDate(
            `${fields.DropDockDate} ${fields.DropDockTime}:00`,

            "Pickup Date & Time should be earlier than Drop date & Time",
            `${fields.PickUpDockDate} ${value}:00`
          );
        }
        break;
      case "DropDockDate":
        let time = fields.DropDockTime != "" ? fields.DropDockTime : "00:00";
        setErrors((prevErrors) => ({
          ...prevErrors,
          DropDockTime: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
        }));
        if (fields.PickUpDockDate === "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time cannnot be earlier than Current Date &Time"
          );
        } else if (fields.PickUpDockDate != "" && fields.PickUpDockTime != "") {
          error = ValidateDate(
            `${value} ${time}:00`,
            "Drop Date & Time should be greater than or equal to the pickup Date & Time",
            `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
          );
        }
        break;
      case "DropDockTime":
        if (fields.DropDockDate != "") {
          setErrors((prevErrors) => ({
            ...prevErrors,
            DropDockDate: "",
            PickUpDockTime: "",
            PickUpDockDate: "",
          }));
          if (fields.PickUpDockDate === "") {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time cannnot be earlier than Current Date &Time"
            );
          } else if (
            fields.PickUpDockDate != "" &&
            fields.PickUpDockTime != ""
          ) {
            error = ValidateDate(
              `${fields.DropDockDate} ${value}:00`,
              "Drop Date & Time should be greater than or equal to the pickup Date & Time",
              `${fields.PickUpDockDate} ${fields.PickUpDockTime}:00`
            );
          }
        }

        break;
      case "destinationConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      case "OriginConsigneeContactEmail":
        error = value ? validateContactEmail(value) : "";
        break;
      default:
        break;
    }
    if (
      !(
        name === "PickupTargetDateFrom" ||
        name === "PickupTargetTimeFrom" ||
        name === "PickupTargetDateTo" ||
        name === "PickupTargetTimeTo" ||
        name === "PickupDeliveryDateFrom" ||
        name === "PickupDeliveryTimeFrom" ||
        name === "PickupDeliveryDateTo" ||
        name === "PickupDeliveryTimeTo"
      )
    ) {
      setErrors((prevErrors) => {
        return {
          ...prevErrors,
          [name]: error,
        };
      });
    }
  };

  const compareExpediteDates = (fieldsVal: any) => {
    if (fieldsVal.PickupTargetDateFrom !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateFrom: "",
      }));
      if (fieldsVal.PickupTargetTimeFrom !== "") {
      }
      if (fieldsVal.PickupTargetDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Target Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeFrom) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateFrom: error,
          }));
        }
      }
    }
    if (fieldsVal.PickupTargetDateTo !== "") {
      setErrors((prevData) => ({
        ...prevData,
        PickupTargetDateTo: "",
      }));
      if (fieldsVal.PickupDeliveryDateFrom !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date From & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Target Date To & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(fieldsVal.PickupTargetTimeTo) || "00:00"
          }:00`
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeTo !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeTo
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
    }

    if (fieldsVal.PickupDeliveryDateFrom !== "") {
      if (fieldsVal.PickupDeliveryDateTo !== "") {
        let error = ValidateExpedietedDate(
          `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeTo) || "00:00"
          }:00`,
          "Pickup Delivery Date From & Time must be equal to and less than Pickup Delivery Date To & Time",
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(fieldsVal.PickupDeliveryTimeFrom) || "00:00"
          }:00`
        );

        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        } else {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: "",
          }));
        }
      }
      if (fieldsVal.PickupTargetTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupTargetDateTo} ${convertTo24HourFormat(
            fieldsVal.PickupTargetTimeFrom
          )}:00`,
          "Pickup target date to and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupTargetDateTo: error,
          }));
        }
      }
      if (fieldsVal.PickupDeliveryTimeFrom !== "") {
        let error = ValidateExpedietedCrrentTime(
          `${fieldsVal.PickupDeliveryDateFrom} ${convertTo24HourFormat(
            fieldsVal.PickupDeliveryTimeFrom
          )}:00`,
          "Pickup delivery date from and time should be greater than current date and time"
        );
        if (error != "") {
          setErrors((prevData) => ({
            ...prevData,
            PickupDeliveryDateFrom: error,
          }));
        }
      }
    }
    if (
      fieldsVal.PickupDeliveryDateTo !== "" &&
      fieldsVal.PickupDeliveryTimeTo !== ""
    ) {
      let error = ValidateExpedietedCrrentTime(
        `${fieldsVal.PickupDeliveryDateTo} ${convertTo24HourFormat(
          fieldsVal.PickupDeliveryTimeTo
        )}:00`,
        "Pickup delivery date to and time should be greater than current date and time"
      );
      if (error != "") {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: error,
        }));
      } else {
        setErrors((prevData) => ({
          ...prevData,
          PickupDeliveryDateTo: "",
        }));
      }
    }
  };

  /**
   * handleTextAreaChange - Updates the textarea field values based on user input.
   *
   * @param {React.ChangeEvent<HTMLTextAreaElement>} e - The textarea change event.
   */
  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    // Prevent leading whitespace
    const trimmedValue = value.trimStart();

    checkInputChange();
    setFields((prev) => ({
      ...prev,
      [name]: trimmedValue,
    }));
  };

  /**
   * mapItemsDataToPayload - Maps the item details from the form data into a format
   * suitable for the API payload.
   *
   * @param {DataTypes[]} data - The array of item details entered by the user.
   * @returns The mapped array of item details for the API payload.
   */
  const mapItemsDataToPayload = (data: any[]) => {
    return data.map((item) => ({
      id: item?.descriptionId || "",
      description: item.description || "",
      handlingUnit: item.handlingUnit || "",
      quantity: item.quantity || "",
      weight: item.weight || "",
      weightUnit: item?.weightUnit?.id || "lbs",
      length: item.itemDimension?.length || "",
      width: item.itemDimension?.width || "",
      height: item.itemDimension?.height || "",
      unit: item.unit.id,
      itemClass: item.itemClass.toString() || "",
      comments: item.comments || "",
      nmfc: item.nmfc || "",
      skuNumber: item.skuNumber || "",
      packageType:
        typeof item.packageType === "object"
          ? item.packageType?.name
          : item.packageType || "",
      package: item.package
        .filter((x: any) => x.quantity !== "" && x.weight !== "")
        .map((x: any) => ({ ...x, id: x.descriptionId || "" })),
      stackable:
        item?.stackable == "Yes" ||
          item?.stackable?.name == "Yes" ||
          item?.stackable?.name == "yes"
          ? true
          : false || false,
      linearFeetStackable: 0,
      linearFeet: item?.linearFeet,
      density: item?.density,
      cbm: item?.cbm,
      hazardMaterial: item.hazmat
        ? {
          itemName: item.hazardMaterial?.itemName || "",
          packageType:
            typeof item.hazardMaterial?.packageType === "object"
              ? item.hazardMaterial?.packageType?.name
              : item.hazardMaterial?.packageType || "",
          hazmatClass: item.hazardMaterial?.hazmatClass || "",
          unno: item.hazardMaterial?.unno || "",
          properShippingName: item.hazardMaterial?.properShippingName || "",
          packagingGroup: item.hazardMaterial?.packagingGroup || "",
          contact: item.hazardMaterial?.contact || "",
          phoneNumber: item.hazardMaterial?.phoneNumber || "",
          countryCallingCode: item.hazardMaterial?.countryCallingCode || "",
          poison: item.hazardMaterial?.poison ? true : false,
          isReportableQuantity: item.hazardMaterial?.isReportableQuantity
            ? true
            : false,
          isMarinePollutant: item.hazardMaterial?.isMarinePollutant
            ? true
            : false,
        }
        : {},
    }));
  };

  /**
   * handleSave - Validates the form fields and saves the customer order by sending
   * the data to the API. If there are errors, it prevents submission.
   */
  const handleSave = async () => {
    if (!runValidation("saveOrder")) return;
    const nonPhoneErrors = Object.entries(errors).filter(
      ([key, value]) =>
        key !== "destinationConsigneePhoneNumber" &&
        key !== "OriginConsigneePhoneNumber" &&
        // runValidation just re-derived these; `errors` here is the stale render closure.
        !isMandatoryMessage(value)
    );
    if (nonPhoneErrors.some(([, value]) => !!value)) {
      return;
    }
    if (orderTypeRowData?.name !== "Expedited" && !isQuote) {
      return;
    }

    // Validate phone per order type:
    // - !notLTL (LTL, Expedited, LTL+FM): origin + destination phone both used
    // - notLTL (Final Mile, etc.): destination phone only
    if (!notLTL) {
      const phoneResults = await Promise.all([
        handlePhoneBlur(fields.OriginConsigneePhoneNumber, fields.OriginConsigneePhoneExt, "OriginConsigneePhoneNumber"),
        handlePhoneBlur(fields.destinationConsigneePhoneNumber, fields.destinationConsigneePhoneExt, "destinationConsigneePhoneNumber"),
      ]);
      if (phoneResults.some((valid) => !valid)) {
        if (!phoneResults[0]) originPhoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        else destinationPhoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }

      if (orderTypeRowData?.name === "Expedited") {
        handleSaveExpediet();
      } else if (orderTypeRowData?.name === "LTL") {
        handleSaveLTL();
      } else {
        handleSaveLTLFinalMile();
      }
      return;
    }

    // notLTL = true (Final Mile, Parcel, etc.) — destination phone only
    const isDestPhoneValid = await handlePhoneBlur(
      fields.destinationConsigneePhoneNumber,
      fields.destinationConsigneePhoneExt,
      "destinationConsigneePhoneNumber"
    );
    if (!isDestPhoneValid) {
      destinationPhoneRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const apiPayload = {
      groupId: groupQuoteId,
      customerId: customerRowData?.id,
      associatedCustomerId: assCustomerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      reference: fields.orderReference,
      reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
      reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
      reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
      reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
      // The service the order is booked for is the one on the quote row the user
      // picked, which handleSelect stores as `selectedServiceId`. `serviceSelected` is
      // only the first entry of the Final Mile multi-select, so with several services
      // quoted at once it names whichever service happens to sort first rather than the
      // one actually chosen. This payload is Final Mile only — Expedited, LTL and
      // LTL + Final Mile are delegated to their own handlers above — and a quote is
      // mandatory to reach it, so the fallback is only for safety.
      serviceId: fields?.selectedServiceId || serviceSelected?.id,
      items: mapItemsDataToPayload(data),
      agentHubId: fields?.agentHubId,
      // selected quote id
      quoteId: fields.selectedQuoteId,
      // all quote id's
      allQuoteIds: fields.allQuoteId,
      destinationAddress: {
        id: fields?.destinationAddressId || "",
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      midStop: {
        address: masterHubAddress?.address ? masterHubAddress.address : fields.MidStopAddress,
        aptSuit: masterHubAddress?.aptSuit ? masterHubAddress.aptSuit : fields.MidStopAPTSuit,
        zipCode: masterHubAddress?.zipCode ? masterHubAddress.zipCode : fields.MidStopZipcode,
        city: masterHubAddress?.city ? masterHubAddress.city : fields.MidStopCity,
        state: masterHubAddress?.state ? masterHubAddress.state : fields.MidStopState,
        country: masterHubAddress?.country ? masterHubAddress.country : fields.MidStopCountry,
        contactName: masterHubAddress?.contactPerson ? masterHubAddress.contactPerson : fields.MidStopContactName,
        contactEmail: masterHubAddress?.contactEmail ? masterHubAddress.contactEmail : fields.MidStopContactEmail,
        phoneNumber: masterHubAddress?.phoneNumber ? masterHubAddress.phoneNumber : fields.MidStopPhoneNumber,
        countryCallingCode: masterHubAddress?.countryCallingCode ? masterHubAddress.countryCallingCode : fields.MidStopPhoneNumberExt,
      },
      document: {
        fileUrl: documentInfo.files[0]?.fileUrl,
        urls: documentInfo.urls,
      },
      orderSwapAndExchange: fields.additionalServices.includes("Order Swap & Exchange"),
      additionalServices: fields.additionalServices,
      removeAndDisposal: fields.additionalServices.includes("Remove & Disposal"),
    };
    try {
      setLoading(true);
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(`/order`, apiPayload);
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(`/order`, apiPayload);
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          // Clear Redux state on successful save
          dispatch(clearTraditionalQuoteData());
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveExpediet = async () => {
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      agentId: agentRowData?.id,
      agentHubId: agentHubRowData?.id,
      serviceId: serviceSelected?.id,
      reference: fields.orderReference,
      reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
      reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
      reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
      reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
      courierProOne: fields.courierPro1,
      courierProTwo: fields.courierPro2,
      originCity: fields.originCity,
      distanceToDestination: "",
      totalPrice: fields.serviceCost,
      items: mapItemsDataToPayload(data),
      originAddress: {
        id: fields?.OriginAddressId || "",
        address: fields.OriginAddress || "",
        aptSuit: fields.OriginAPTSuite || "",
        zipCode: fields.OriginZipcode || "",
        city: fields.OriginCity || "",
        state: fields.OriginState || "",
        country: fields.OriginCountry || "",
        businessLocation: fields.OriginBusinessLocation || "",
        contactName: fields.OriginConsigneeName || "",
        phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.OriginConsigneePhoneExt || "",
        contactEmail: fields.OriginConsigneeContactEmail || "",
        pickUpTargetDateTimeFrom: getISODate(
          fields?.PickupTargetDateFrom,
          fields?.PickupTargetTimeFrom
        ),
        pickUpTargetDateTimeTo: getISODate(
          fields?.PickupTargetDateTo,
          fields?.PickupTargetTimeTo
        ),
        pickUpDeliveryTargetDateTimeFrom: getISODate(
          fields?.PickupDeliveryDateFrom,
          fields?.PickupDeliveryTimeFrom
        ),
        pickUpDeliveryTargetDateTimeTo: getISODate(
          fields?.PickupDeliveryDateTo,
          fields?.PickupDeliveryTimeTo
        ),
      },
      destinationAddress: {
        id: fields?.destinationAddressId,
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      document: {
        fileUrl: documentInfo.files[0]?.fileUrl,
        urls: documentInfo.urls,
      },
    };
    try {
      setLoading(true);

      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );
        if (uploadApiResponse.success) {
          const data = uploadApiResponse?.data;
          apiPayload.document.fileUrl = data.url;
          const apiResponse = await postApi(
            `/order/createexpeditedorder`,
            apiPayload
          );
          if (apiResponse.success) {
            handleUpdateNavigation();
            setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
            navigate("/all-orders");
          } else {
            setMessage(
              apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
              "error"
            );
          }
        } else {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      } else {
        const apiResponse = await postApi(
          `/order/createexpeditedorder`,
          apiPayload
        );
        if (apiResponse.success) {
          handleUpdateNavigation();
          setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
          // Clear Redux state on successful save
          dispatch(clearTraditionalQuoteData());
          navigate("/all-orders");
        } else {
          setMessage(
            apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
        }
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  function fixFormat(dateStr: string): string {
    // If it's already in 'YYYY-MM-DD' format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    // If it's an ISO string (like '2025-07-01T19:00:00.000Z'), extract date
    const date = new Date(dateStr);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split("T")[0]; // Extracts 'YYYY-MM-DD'
    }

    // If invalid date, return empty string to avoid sending bad payload
    return "";
  }

  const handleSaveLTL = async () => {
    let apiPayload = {
      groupId: groupQuoteId,
      request: {
        quoteDetail: {
          serviceId: serviceSelected?.id,
          customerId: customerRowData?.id,
          associatedCustomerId: assCustomerRowData?.id,
          orderTypeId: orderTypeRowData?.id,
          scac: fields?.scac || "",
          reference: fields.orderReference,
          reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
          reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
          reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
          reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
          distanceToDestination: fields.distance || "",
          totalWeight: fields.destinationWeight || "",
          threePLProvider: fields.plProvider || "",
          courierProOne: fields.courierPro1 || "",
          courierProTwo: fields.offerId || "",
          items: mapItemsDataToPayload(data),
          pickUpDock: {
            dockName: fields.PickUpDockName || "",
            pickupNumber: fields.PickUpDOckNumber || "",
            pickUpDate: getISODate(
              fields.PickUpDockDate || "",
              fields.PickUpDockTime || ""
            ),
            pickUpTime: fields.PickUpDockTime || "",
            note: fields.PickUpDOckNote || "",
            dockOpenTime:
              convertTo24HourFormat(fields.PickUpDockOpenTime) || "",
            dockCloseTime:
              convertTo24HourFormat(fields.PickUpDOckCloseTime) || "",
            firstComeFirstServe: fields.PickUpFCFS,
          },
          dropDock: {
            dockName: fields.DropDockName || "",
            pickupNumber: fields.DropDOckNumber || "",
            dropDate: "2024-12-12",
            dropTime: "19:44",
            note: fields.DropDOckNote || "",
            dropOpenTime: convertTo24HourFormat(fields.DropDockOpenTime) || "",
            dropCloseTime:
              convertTo24HourFormat(fields.DropDOckCloseTime) || "",
            firstComeFirstServe: fields.DropFCFS,
          },
          originAddress: {
            id: fields?.OriginAddressId || "",
            address: fields.OriginAddress || "",
            aptSuit: fields.OriginAPTSuite || "",
            zipCode: fields.OriginZipcode || "",
            city: fields.OriginCity || "",
            state: fields.OriginState || "",
            country: fields.OriginCountry || "",
            businessLocation: fields.OriginBusinessLocation || "",
            contactName: fields.OriginConsigneeName || "",
            phoneNumber: fields.OriginConsigneePhoneNumber.replace(/\D/g, ""),
            countryCallingCode: fields.OriginConsigneePhoneExt || "",
            contactEmail: fields.OriginConsigneeContactEmail || "",
            accessorials: pickupAccessorialsSelected,
            locationType: pickupLimitedAccessSelected?.name || "",
          },
          destinationAddress: {
            id: fields?.destinationAddressId || "",
            address: fields.destinationAddress || "",
            aptSuit: fields.destinationAPTSuite || "",
            zipCode: fields.destinationZipcode || "",
            city: fields.destinationCity || "",
            state: fields.destinationState || "",
            country: fields.destinationCountry || "",
            businessLocation: fields.destinationBusinessLocation || "",
            contactName: fields.destinationConsigneeName || "",
            phoneNumber: fields.destinationConsigneePhoneNumber.replace(
              /\D/g,
              ""
            ),
            countryCallingCode: fields.destinationConsigneePhoneExt || "",
            contactEmail: fields.destinationConsigneeContactEmail || "",
            handlingUnit: fields.destinationHandingUnits || "",
            weight: fields.destinationWeight || "",
            pieces: fields.destinationPieces || "",
            accessorials: dropAccessorialsSelected,
            locationType: dropLimitedAccessSelected?.name || "",
          },
        },
        shipmentProductTransactionId: fields.productTransactionId || "",
        shipmentOfferId: fields.offerId || "",
        shipment: {},
        isSelfScheduled: false,
        pickupDate: `${fixFormat(
          fields.PickUpDockDate
        )} ${convertTo24HourFormat(fields.PickUpDockTime)}:00`,
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "",
        shipmentReferenceList: mapReferences(true),
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };
    try {
      setLoading(true);

      const apiResponse = await postApi(`/order/createltlorder`, apiPayload);
      if (apiResponse.success) {
        handleUpdateNavigation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
        // Clear Redux state on successful save
        dispatch(clearTraditionalQuoteData());
        navigate("/all-orders");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLTLFinalMile = async () => {

    const fm = ltlFMData?.finalMileData;
    const fmService = fm?.service;
    const fmHub = ltlFMData?.hub || fm?.hub;
    const fmAddr = fmHub?.address;
    const fmCustomer = fmService?.customer;

    // Prefer form values; otherwise take from ltlFMData root
    const offerId = fields.offerId || ltlFMData?.offerId;
    const transactionId =
      fields.productTransactionId || ltlFMData?.productTransactionId;
    const isArmstrongLtlFm =
      fields?.orderFor === "armstrong" ||
      state?.selectedRow?.orderFor === "armstrong" ||
      state?.selectedData?.orderFor === "armstrong";

    const fallbackStr = (...vals: Array<string | undefined | null>) =>
      (vals.find(
        (v) => typeof v === "string" && v.trim().length > 0
      ) as string) || "";

    const fallbackNum = (
      ...vals: Array<number | string | undefined | null>
    ) => {
      const n = vals.find((v) => {
        if (v === null || v === undefined) return false;
        const num = typeof v === "string" ? Number(v) : v;
        return typeof num === "number" && !Number.isNaN(num);
      });
      return Number(typeof n === "string" ? Number(n) : n) || 0;
    };

    const asEmailArray = (val?: string | string[] | null): string[] => {
      if (Array.isArray(val)) return val.filter(Boolean);
      if (typeof val === "string" && val.trim()) return [val.trim()];
      return [];
    };

    // ---------- SAFE TIME HELPERS (eliminate "Invalid date") ----------
    const isBad = (v?: string | null) =>
      !v ||
      typeof v !== "string" ||
      v.trim() === "" ||
      v.toLowerCase().includes("invalid");

    const safeConvertTo24Hour = (val?: string) => {
      const input = typeof val === "string" ? val : "";
      return typeof convertTo24HourFormat === "function"
        ? convertTo24HourFormat(input)
        : input;
      // if convertTo24HourFormat is missing, the UI should already be giving "HH:mm"
    };

    const coalesceTime = (
      input?: string,
      fallback?: string,
      defaultVal?: string
    ): string => {
      const t = safeConvertTo24Hour(input);
      if (!isBad(t)) return t!;
      const tfb = safeConvertTo24Hour(fallback);
      if (!isBad(tfb)) return tfb!;
      return defaultVal || "00:00";
    };

    const toIsoUTC = (dateTimeStr?: string): string | undefined => {
      if (!dateTimeStr || typeof dateTimeStr !== "string") return undefined;
      const s = dateTimeStr.replace(" ", "T");
      const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
      return isNaN(d.getTime()) ? undefined : d.toISOString();
    };

    const format12h = (dateTimeStr?: string): string | undefined => {
      if (!dateTimeStr || typeof dateTimeStr !== "string") return undefined;
      const s = dateTimeStr.replace(" ", "T");
      const d = new Date(s.endsWith("Z") ? s : `${s}Z`);
      if (isNaN(d.getTime())) return undefined;
      const hh = d.getUTCHours();
      const mm = d.getUTCMinutes().toString().padStart(2, "0");
      const ampm = hh >= 12 ? "PM" : "AM";
      const h12 = ((hh + 11) % 12) + 1;
      return `${h12.toString().padStart(2, "0")}:${mm} ${ampm}`;
    };
    // -----------------------------------------------------------------

    // From offer: SCAC + total price (used as fallbacks)
    const scacFromOffer =
      fields.scac ||
      (ltlFMData as any)?.scac ||
      ltlFMData?.primaryVendor?.scac ||
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit?.scac ||
      "";

    const totalPriceFromOffer =
      typeof ltlFMData?.totalPrice === "number"
        ? ltlFMData.totalPrice
        : undefined;

    const customerMarkupTypeFromFM =
      fields.customerMarkupType ||
      fm?.rateDetails?.customerMarkupType ||
      (ltlFMData as any)?.customerMarkUpType ||
      "percent";

    const distanceToDestination = fallbackNum(
      Number(fields.finalMileDistance),
      fm?.distanceToDestination,
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
        ?.milesInTransit
    );

    // Prefer UI date/time; fallback to offer shipmentDate if UI invalid
    const shipmentDateFromOffer =
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.shipmentDate; // "YYYY-MM-DD HH:mm:ss"

    const uiISO = getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || "");

    const pickUpISODate = !isBad(uiISO)
      ? (uiISO as string)
      : toIsoUTC(shipmentDateFromOffer) || new Date().toISOString();

    const pickUpDisplayTime = fields.PickUpDockTime || format12h(shipmentDateFromOffer) || "08:00 AM";

    // pickup/delivery hours (now safe)
    const pickOpen = coalesceTime(
      fields.PickUpDockOpenTime,
      fmAddr?.operationHrsFrom,
      "00:00"
    );
    const pickClose = coalesceTime(
      fields.PickUpDOckCloseTime,
      fmAddr?.operationHrsTo,
      "23:59"
    );
    const dropOpen = coalesceTime(
      (fields as any).DropDockOpenTime,
      fmAddr?.operationHrsFrom,
      "00:00"
    );
    const dropClose = coalesceTime(
      (fields as any).DropDOckCloseTime,
      fmAddr?.operationHrsTo,
      "23:59"
    );

    // Delivery date: prefer ETA from LTL time-in-transit; else ensure not before pickup date
    const etaDate =
      ltlFMData?.offeredProductList?.[0]?.shopRQShipment?.timeInTransit
        ?.estimatedDeliveryDate; // "YYYY-MM-DD"
    const safeDropDate = (() => {
      if (etaDate && /^\d{4}-\d{2}-\d{2}$/.test(etaDate)) return etaDate;
      const pu = new Date(pickUpISODate);
      if (isNaN(pu.getTime())) return "2024-12-12"; // last-known default from your payloads
      // add a minimal buffer of 1 day if no ETA:
      const d = new Date(pu);
      d.setUTCDate(d.getUTCDate() + 1);
      return d.toISOString().slice(0, 10);
    })();


    const apiPayload = {
      groupId: groupQuoteId || "",

      // ---------------- quoteDetail ----------------
      quoteDetail: {
        ...((state?.selectedData?.internalLTL ||
          fields?.internalLTL ||
          fields?.selectedQuoteId) && {
          internalLTLOrder:
            state?.selectedData?.internalLTL || fields?.internalLTL || false,
          selected_ltl_quote:
            state?.selectedData?.quoteId || fields?.selectedQuoteId || "",
        }),
        source: fields?.source,
        finalMileSource: fields?.finalMileSource ?? state?.selectedData?.finalMileData?.source,
        loadId: fields?.loadId ?? state?.selectedData?.loadId,
        ...((fields?.banyanType ?? state?.selectedData?.banyanType ?? fields?.isProvidence) && { "orderFor": fields?.isProvidence ? "providence" : "banyan", "banyanType": fields?.banyanType ?? state?.selectedData?.banyanType, }),
        ...(isArmstrongLtlFm && { "orderFor": "armstrong" }),
        // selected_ltl_quote: "",
        // customerNumber:
        //   (customerRowData as any)?.customerNumber ||
        //   (customerRowData as any)?.customer?.customerNumber ||
        //   fmCustomer?.customerNumber ||
        //   "",
        customerId: (customerRowData as any)?.id,
        associatedCustomerId: assCustomerRowData?.id,
        orderTypeName:
          (orderTypeRowData as any)?.name ||
          (fields as any)?.orderTypeName ||
          "LTL + Final Mile",

        agentHubName:
          (fields as any)?.AgentHub?.name ||
          (fields as any)?.agentHubName ||
          fmHub?.name ||
          quickQuoteLtlFMData?.hub?.name ||
          hubNameFallBack ||
          "",

        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        serviceId:
          serviceSelected?.id ||
          fmService?.id ||
          quickQuoteLtlFMData?.service?.id ||
          (ltlFMData as any)?.serviceId ||
          "",
        // finalmile_quote_id:
        //   lfmQuoteId ||
        //   state?.quoteId ||
        //   fmService?.quoteId ||
        //   state?.quoteId ||
        //   fields.selectedQuoteId ||
        //   "",
        finalmile_quote_id: fields?.finalmile_quote_id,
        distanceToDestination,
        totalWeight: fallbackNum(
          Number(fields.destinationWeight),
          fm?.totalWeight
        ),

        // Prefer offer total price else FM quote price
        totalPrice:
          typeof totalPriceFromOffer === "number"
            ? totalPriceFromOffer
            : typeof fm?.quotePrice === "number"
              ? fm.quotePrice
              : 0,

        customerMarkupType: customerMarkupTypeFromFM,
        allQuoteIds: [fields?.finalmile_quote_id].filter(Boolean),
        // allQuoteIds: [
        //   lfmQuoteId ||
        //   state?.quoteId ||
        //   fields?.allQuoteId ||
        //   fmService?.quoteId ||
        //   fields.selectedQuoteId ||
        //   "",
        // ].filter(Boolean),
        destinationAddress: {
          id: fields?.destinationAddressId || "",
          address: fields.destinationAddress || "",
          aptSuit: fields.destinationAPTSuite || "",
          zipCode: fields.destinationZipcode || "",
          city: fields.destinationCity || "",
          state: fields.destinationState || "",
          country: fields.destinationCountry || "",
          businessLocation: fields.destinationBusinessLocation || "",
          contactName: fields.destinationConsigneeName || "",
          phoneNumber:
            fields.destinationConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.destinationConsigneePhoneExt || "",
          contactEmail: fields.destinationConsigneeContactEmail || "",
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits || "",
          weight: fields.destinationWeight || "",
          pieces: fields.destinationPieces || "",
          accessorials: mapAccessorials(dropAccessorialsSelected) || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },

        midStop: {
          address:
            masterHubAddress?.address ? masterHubAddress.address :
              (fields.MidStopAddress ||
                fmAddr?.address ||
                quickQuoteLtlFMData?.hub?.address?.address ||
                ""),
          aptSuit:
            masterHubAddress?.aptSuit ? masterHubAddress.aptSuit :
              (fields.MidStopAPTSuit ||
                fmAddr?.aptSuit ||
                quickQuoteLtlFMData?.hub?.address?.aptSuit ||
                ""),
          zipCode:
            masterHubAddress?.zipCode ? masterHubAddress.zipCode :
              (fields.MidStopZipcode ||
                fmAddr?.zipCode ||
                quickQuoteLtlFMData?.hub?.address?.zipCode ||
                ""),
          city:
            masterHubAddress?.city ? masterHubAddress.city :
              (fields.MidStopCity ||
                fmAddr?.city ||
                quickQuoteLtlFMData?.hub?.address?.city ||
                ""),
          state:
            masterHubAddress?.state ? masterHubAddress.state :
              (fields.MidStopState ||
                fmAddr?.state ||
                quickQuoteLtlFMData?.hub?.address?.state ||
                ""),
          country:
            masterHubAddress?.country ? masterHubAddress.country :
              (fields.MidStopCountry ||
                fmAddr?.country ||
                quickQuoteLtlFMData?.hub?.address?.country ||
                ""),
          contactName:
            masterHubAddress?.contactPerson ? masterHubAddress.contactPerson :
              (fields.MidStopContactName ||
                fmHub?.agent?.name ||
                fmHub?.name ||
                quickQuoteLtlFMData?.hub?.address?.name ||
                ""),
          contactEmail:
            masterHubAddress?.contactEmail ? masterHubAddress.contactEmail :
              asEmailArray(
                fields.MidStopContactEmail ||
                fmHub?.operationEmail ||
                quickQuoteLtlFMData?.hub?.address?.operationEmail
              ),
          phoneNumber:
            masterHubAddress?.phoneNumber ? masterHubAddress.phoneNumber :
              (fields.MidStopPhoneNumber ||
                fmAddr?.phoneNumber ||
                quickQuoteLtlFMData?.hub?.address?.phoneNumber ||
                ""),
          countryCallingCode:
            masterHubAddress?.countryCallingCode ? masterHubAddress.countryCallingCode :
              (fields.MidStopPhoneNumberExt ||
                fmAddr?.countryCallingCode ||
                quickQuoteLtlFMData?.hub?.address?.countryCallingCode ||
                "+1"),
        },

        document: { fileUrl: "", urls: documentInfo.urls },
        additionalServices: [],
        items: mapItemsDataToPayload(data),
      },

      // ---------------- ltlQuoteDetail ----------------
      ltlQuoteDetail: {
        threePLProvider: fields.plProvider || "",
        courierProOne: fields.courierPro1 || "",
        scac: scacFromOffer,
        agentName: !scacFromOffer && (fields?.internalLTL || state?.selectedData?.internalLTL) ? (fmHub?.agent?.name || fields?.CarrierName || "") : "",
        courierProtwo: fields.courierPro2 || "",
        customerMarkupType: customerMarkupTypeFromFM,

        shipmentProductTransactionId: (fields?.banyanType) ?? state?.selectedData?.banyanType ? fields?.parentQuoteId ?? state?.selectedData?.quoteId : fields?.isProvidence ? fields?.quoteDetailId : isArmstrongLtlFm ? fields?.carrierQuoteId || state?.selectedRow?.carrierQuoteId || state?.selectedData?.carrierQuoteId || "" : transactionId || "",
        shipmentOfferId: (fields?.banyanType) ?? state?.selectedData?.banyanType ? fields?.parentQuoteId ?? state?.selectedData?.quoteId : fields?.isProvidence ? fields?.quoteDetailId : isArmstrongLtlFm ? fields?.rateRequestLocator || state?.selectedRow?.rateRequestLocator || state?.selectedData?.rateRequestLocator || "" : offerId || "",
        ...(fields?.isProvidence && { quoteDetailId: fields?.quoteDetailId ?? "" }),
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: pickUpISODate,
          pickUpTime: pickUpDisplayTime,
          note: fields.PickUpDOckNote || "",
          dockOpenTime: pickOpen,
          dockCloseTime: pickClose,
          firstComeFirstServe: !!fields.PickUpFCFS,
        },

        dropDock: {
          dockName: (selectedHubData as any)?.hubName || fmHub?.name || "",
          pickupNumber: fields.DropDOckNumber || "",
          // ensure drop is not before pickup; prefer ETA if available
          dropDate: safeDropDate,
          dropTime: "10:00",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropOpen,
          dropCloseTime: dropClose,
          firstComeFirstServe: !!fields.DropFCFS,
        },

        originAddress: {
          id: fields?.OriginAddressId || "",
          address: fields.OriginAddress || "",
          aptSuit: fields.OriginAPTSuite || "",
          zipCode: fields.OriginZipcode || "",
          city: fields.OriginCity || "",
          state: fields.OriginState || "",
          country: fields.OriginCountry || "",
          businessLocation: fields.OriginBusinessLocation || "",
          contactName: fields.OriginConsigneeName || "",
          phoneNumber:
            fields.OriginConsigneePhoneNumber?.replace(/\D/g, "") || "",
          countryCallingCode: fields.OriginConsigneePhoneExt || "",
          contactEmail: fields.OriginConsigneeContactEmail || "",
          accessorials: mapAccessorials(pickupAccessorialsSelected) || [],
          locationType: pickupLimitedAccessSelected?.name || "",
        },

        shipment: {},
        isSelfScheduled: false,

        pickupDate: `${fixFormat(
          fields.PickUpDockDate
        )} ${convertTo24HourFormat(fields.PickUpDockTime)}:00`,
        closeTime: fields.PickUpDOckCloseTime
          ? `${convertTo24HourFormat(fields.PickUpDOckCloseTime)}:00`
          : "23:59:00",
        readyTime: fields?.PickUpDockOpenTime
          ? `${convertTo24HourFormat(fields.PickUpDockOpenTime)}:00`
          : "00:00:00",

        shipmentReferenceList: fields.orderReference ? mapReferences(true) : [],
        pickupSpecialInstructions: fields.PickUpDOckNote || "",
        deliverySpecialInstructions: fields.DropDOckNote || "",
        specialInstructions: "updated main instr",
      },
    };

    try {
      setLoading(true);

      // Upload doc if present
      if (documentInfo.files?.length && documentInfo.files[0]?.fileUrl) {
        const formData = new FormData();
        formData.append("file", documentInfo.files[0]?.fileUrl);
        const uploadApiResponse = await postApi<any>(
          `/order/upload-doc`,
          formData
        );

        if (!uploadApiResponse.success) {
          setMessage(
            uploadApiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
            "error"
          );
          setLoading(false);
          return;
        }
        apiPayload.quoteDetail.document.fileUrl =
          uploadApiResponse?.data?.url || "";
      }

      const apiResponse = await postApi(
        `/order/ltl-plus-finalmile`,
        apiPayload
      );
      if (apiResponse.success) {
        handleUpdateNavigation();
        setMessage(ORDER_MANAGEMENT_MESSAGES.CUSTOMER_ORDER_CREATE_SUCCESS, "success");
        // Clear Redux state on successful save
        dispatch(clearTraditionalQuoteData());
        navigate("/all-orders");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  const mapAccessorials = (accessorialsData: any[]): any[] => {
    return accessorialsData.map((item) => ({
      id: item?.id || "",
      name: item?.name || "",
    }));
  };

  /**
   * getZipcodeDetails - Fetches and updates city, state, and country information
   * based on the provided zipcode.
   */
  const getZipcodeDetails = async () => {
    if ((fields.destinationZipcode?.length ?? 0) < 5) {
      setDestinationCities([]);
      setFields((prev) => ({ ...prev, destinationCity: "", destinationState: "", destinationCountry: "" }));
    }
    if (fields.destinationZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.destinationZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setDestinationCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          destinationCity: primaryCity,
          destinationState: data?.state || "",
          destinationCountry: data?.country || "",
        }));
      }
    }
    if ((fields.OriginZipcode?.length ?? 0) < 5) {
      setOriginCities([]);
      setFields((prev) => ({ ...prev, OriginCity: "", OriginState: "", OriginCountry: "" }));
    }
    if (fields.OriginZipcode?.length >= 5) {
      const url = `${GET_DATA_FROM_ZIPCODE}${fields.OriginZipcode}`;
      const apiResponse = await getApi<any>(url);
      if (apiResponse?.success) {
        const data = apiResponse?.data;
        const cities: string[] = (data?.cities || []).map((c: any) => c.city || "").filter(Boolean);
        const primaryCity: string = data?.city || "";
        const orderedCities = primaryCity
          ? [primaryCity, ...cities.filter((c) => c.toLowerCase() !== primaryCity.toLowerCase())]
          : cities;
        setOriginCities(orderedCities);
        setFields((prev) => ({
          ...prev,
          OriginCity: primaryCity,
          OriginState: data?.state || "",
          OriginCountry: data?.country || "",
        }));
      }
    }
  };

  /**
   * getQuotesData - Fetches quotes for the entered data, and updates the state
   * with the returned quotes. If the quotes are successfully fetched, it maps the
   * data into a usable format for the application.
   */
  const getQuotesData = async () => {
    if (!runValidation("getQuote")) return;
    setGetQuote(true);
    setStateQuoteId("");
    // Clear the whole previous quote before the request goes out, so nothing from the
    // last click can survive into this one; the response below repopulates it and the
    // loader covers the gap. This is the only place that reliably runs on every click:
    // `getQuote` is already true on a repeat click, so setGetQuote(true) is a no-op and
    // the reset effect keyed on it never fires again.
    SetQuoteData([]);
    setHasMasterHub(false);
    setMasterHubName("");
    setMasterHubAddress({});
    // empty mid stop fields value
    setFields((prev: any) => ({
      ...prev,
      MidStopAddress: "",
      MidStopAPTSuit: "",
      MidStopZipcode: "",
      MidStopCity: "",
      MidStopState: "",
      MidStopCountry: "",
      MidStopContactName: "",
      MidStopPhoneNumber: "",
      MidStopPhoneNumberExt: "",
      MidStopContactEmail: [],
    }));
    // set api payload data
    const apiPayload = {
      customerId: assCustomerRowData?.id || customerRowData?.id,
      serviceIds: isMultiServiceQuote
        ? serviceSelectedList.map((service: any) => service?.id)
        : [serviceSelected?.id],
      // Opt-in flag: tells the backend to return every agent with all of its services,
      // ordered closest agent first and cheapest service first within each agent.
      ...(isMultiServiceQuote ? { multiServiceQuote: true } : {}),
      items: mapItemsDataToPayload(data),
      destinationAddress: {
        address: fields.destinationAddress,
        aptSuit: fields.destinationAPTSuite,
        zipCode: fields.destinationZipcode,
        city: fields.destinationCity,
        state: fields.destinationState,
        country: fields.destinationCountry,
        businessLocation: fields.destinationBusinessLocation,
        contactName: fields.destinationConsigneeName,
        phoneNumber: fields.destinationConsigneePhoneNumber.replace(/\D/g, ""),
        countryCallingCode: fields.destinationConsigneePhoneExt,
        contactEmail: fields.destinationConsigneeContactEmail,
        extraMen: fields.destinationExtramen,
        waitTime: fields.destinationWaitTimeMinutes,
        stairsNumber: fields.destinationNoOfFlights,
        storage: fields.destinationStorage,
        handlingUnit: fields.destinationHandingUnits,
        weight: fields.destinationWeight,
        pieces: fields.destinationPieces,
        specialInstructions: fields.destinationSpecialInstructions,
      },
      additionalServices: fields.additionalServices,
    };
    try {
      setLoading(true);
      const apiResponse = await postApi<GetQuoteApiResponse[]>(
        `/order/order-quote/generate`,
        apiPayload
      );
      if (apiResponse.success) {
        const data = apiResponse?.data || [];

        const mappedData = data.map((item) => {
          const itemHasMasterHub = !!item.hub.masterHubId;
          const itemMasterHubName = item.masterHub?.name || "";
          const itemMasterHubAddress = item.masterHub?.address;
          if (itemHasMasterHub) {
            setHasMasterHub(true);
            setMasterHubName(itemMasterHubName);
            // Never store undefined: the `getQuoteBox` below reads masterHubAddress.city
            // unguarded once hasMasterHub is true, so an id without a populated
            // masterHub would take the render down.
            setMasterHubAddress(itemMasterHubAddress || {});
          }
          // Collect all quoteIds for the current item
          const allQuoteIds = item.services
            .map((service) => service?.quoteId)
            .filter((quoteId) => quoteId != null);

          const effectiveAddress = itemHasMasterHub ? itemMasterHubAddress : item.hub.address;
          const effectiveHubName = itemHasMasterHub ? itemMasterHubName : item.hub?.name || "";

          const services = item.services.map((service, serviceIndex) => ({
            id: service?.id,
            serial: serviceIndex + 1,
            serviceName: service?.name || "",
            description: service?.description || "",
            serviceLevel: service?.serviceType?.name,
            quotePrice: service?.quotePrice || 0.0,
            fuelSurcharge: orderTypeRowData?.name === "Final Mile"
              ? service?.rateDetails?.customerFuelSurcharge : service?.rateDetails?.fuelSurcharge || 0.0,
            additionalOrderCharges:
              service?.rateDetails?.additionalOtherCharges,
            basePrice: service?.rateDetails?.basePrice || 0.0,
            totalWeight: service?.totalWeight || 0,
            deliverWithin: service?.deliverWithin || 0,
            distanceToDestination: service?.distanceToDestination || 0,
            shortName: service?.shortName || "",
            longName: service?.longName || "",
            useOver150Lbs: service?.useOver150Lbs || false,
            address: effectiveAddress,
            operationHrsFrom: effectiveAddress?.operationHrsFrom
              || item.hub?.address?.operationHrsFrom || "",
            operationHrsTo: effectiveAddress?.operationHrsTo
              || item.hub?.address?.operationHrsTo || "",
            hubName: effectiveHubName,
            agentHubId: item.hub?.id || "",
            addressName: effectiveAddress?.address
              || item.hub?.address?.address || "",
            agentName: item.hub?.agent?.name || "",
            quoteId: service?.quoteId,
            allQuoteId: allQuoteIds,
          }));
          const info: QuoteInfo = {
            agentName: item.hub?.agent?.name || "",
            operationHrsFrom: effectiveAddress?.operationHrsFrom || "",
            operationHrsTo: effectiveAddress?.operationHrsTo || "",
            hubName: effectiveHubName,
            addressName: effectiveAddress?.address || "",
            address: effectiveAddress,
            agentHubId: item.hub?.id || "",
            // Carry each agent's own master hub. With several agents on screen the
            // shared masterHubName/masterHubAddress state holds only the last one,
            // which would otherwise be rendered on every agent's header.
            hasMasterHub: itemHasMasterHub,
            masterHubName: itemHasMasterHub ? itemMasterHubName : undefined,
            masterHubAddress: itemHasMasterHub
              ? itemMasterHubAddress
              : undefined,
            distanceToDestination:
              item.services?.[0]?.distanceToDestination || 0,
          };

          return { info, services };
        });

        SetQuoteData(mappedData);
      } else {
        SetQuoteData([]);
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
      // Anything thrown while mapping escapes here with quoteData untouched, which
      // leaves the previous quote's agents on screen as though they were this
      // request's result. Clear the list and say so rather than showing stale hubs.
      SetQuoteData([]);
      setMessage(GENERAL_ERROR_MESSAGE, "error");
      console.error(GENERAL_ERROR_MESSAGE, error);
    } finally {
      setLoading(false);
    }
  };

  /** Red outline for any field currently carrying a validation message. */
  const fieldErrorClass = (key: string): string =>
    errors?.[key] ? " form__input--error" : "";

  /**
   * Same, for controls wrapped in `form__groupField` (the phone number's country-code
   * group). The wrapper draws the border there, so the input's own modifier is invisible.
   */
  const groupFieldErrorClass = (key: string): string =>
    errors?.[key] ? " form__groupField--error" : "";

  /**
   * A quote must be picked before an order can be saved. Expedited orders skip the quote
   * step entirely, and an LTL order additionally needs the carrier the quote supplies.
   */
  const isQuoteSelected = (): boolean => {
    if (orderTypeRowData?.name === "Expedited") return true;
    const isBanyanOrProvidence = ["providence", "banyan"].includes(
      state?.selectedRow?.orderFor
    );
    const picked =
      isSelectedParent ||
      (state?.selectedRow &&
        Object?.keys(state?.selectedRow || {})?.length > 0 &&
        !isBanyanOrProvidence);
    if (!picked) return false;
    return !(orderTypeRowData?.name === "LTL" && !fields.CarrierName);
  };

  /**
   * Clear handling-unit highlights as soon as the values arrive — typed in, or filled in
   * bulk by picking a saved item from the Description dropdown.
   */
  useEffect(() => {
    setInvalidCells((prev) => pruneItemIssues(prev, data));
    setInvalidPackageCells((prev) => prunePackageIssues(prev, data));
    if (data?.length > 0) setItemsEmpty(false);
  }, [data]);

  /**
   * runValidation - Checks everything the Get Quote and Save buttons used to check
   * silently through their `disabled` expressions, and turns any gap into visible
   * feedback: a message under each blank field plus scroll-and-focus on the first one.
   *
   * @returns true when the caller may proceed.
   */
  const runValidation = (
    intent: "saveOrder" | "getQuote" | "getLtlQuote"
  ): boolean => {
    const result = validateOrderForm({
      intent,
      fields,
      data,
      errors,
      orderTypeName: orderTypeRowData?.name,
      notLTL,
      customerId: customerRowData?.id,
      orderTypeId: orderTypeRowData?.id,
      serviceId: serviceSelected?.id,
      agent: agentRowData,
      agentHub: agentHubRowData,
      quoteGateChecksErrors: intent === "getLtlQuote",
    });

    // Re-derived fresh on every Get Quote / Save click, exactly like the missing-field
    // checks above, so a stale saved-quote date is caught even if a later edit to a
    // sibling dock field already cleared whatever handleFieldChange had set on it.
    const pickupDateIsPast = isPastPickupDate(fields.PickUpDockDate);
    if (pickupDateIsPast) {
      setErrors((prev) => ({ ...prev, PickUpDockDate: PICKUP_DATE_PAST_ERROR }));
      result.invalid.push({ key: "PickUpDockDate", label: "Pickup Date" });
      result.isValid = false;
    } else if (errors.PickUpDockDate === PICKUP_DATE_PAST_ERROR) {
      setErrors((prev) => ({ ...prev, PickUpDockDate: "" }));
    }

    const cells: Record<number, string[]> = {};
    const packageCells: Record<number, Record<number, string[]>> = {};
    result.itemIssues.forEach((issue) => {
      if (issue.columnKeys.length > 0) cells[issue.rowIndex] = issue.columnKeys;
      issue.packageIssues.forEach((pkg) => {
        packageCells[issue.rowIndex] = {
          ...packageCells[issue.rowIndex],
          [pkg.packageIndex]: pkg.columnKeys,
        };
      });
    });
    setInvalidCells(cells);
    setInvalidPackageCells(packageCells);
    setItemsEmpty(result.itemsEmpty);

    const { ok, focusItemCell } = applyValidation(result);
    setFocusCell(focusItemCell);
    return ok;
  };

  /**
   * Updates `parentCustomerId` when `customerRowData.id` changes to keep them in sync.
   */
  useEffect(() => {
    if (customerRowData && customerRowData?.id && !saveQuotationPage) {
      clearLTLQuoteDetails();
      setParentCustomerId(customerRowData.id);
      setFields((prevData) => ({
        ...prevData,
        serviceCost: "",
      }));
    }
  }, [customerRowData]);

  useEffect(() => {
    if (!saveQuotationPage) {

      setFields((prevData) => ({
        ...prevData,
        serviceCost: "",
      }));
      setIsSelectedParent(false);
      setShowLTLList(false);
      setShowLTLData(false);
      setShowFMList(false);
      clearFinalMileQuoteDetails();
    }
  }, [serviceSelected, assCustomerRowData]);

  const clearLTLQuoteDetails = () => {
    setShowLTLList(false);
    // setIsSelectedParent(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  const clearFinalMileQuoteDetails = () => {
    setShowLTLList(false);
    // setIsSelectedParent(false);
    setGetQuote(false);
    setIsQuote(false);
    setFields((prevData) => ({
      ...prevData,
      CarrierName: "",
      serviceCost: "",
      scac: "",
      distance: "",
      offerId: "",
      productTransactionId: "",
    }));
  };
  useEffect(() => {

    if (!saveQuotationPage) {

      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
      setShowLTLList(false);
      setIsSelectedParent(false);
    }
  }, [
    data,
    dropAccessorialsSelected,
    dropLimitedAccessSelected,
    pickupAccessorialsSelected,
    pickupLimitedAccessSelected,
    assCustomerRowData,
    customerRowData,
    serviceSelected,
    // Changing the Final Mile service selection must invalidate a stale quote too.
    serviceSelectedList,
  ]);

  // Added to handle save button able and diuable din respect of ectamen and all other ratled fields and rmeoval of location state

  useEffect(() => {
    // Skip clearing when quote fields are being populated from Quick Quote / Traditional Quote
    if (skipQuoteClearRef.current) {
      skipQuoteClearRef.current = false;
      return;
    }

    const isFromQuotePage =
      state?.page === "Quick Quote" ||
      state?.page === "Traditional Quote" ||
      state?.page === "Save Quotation";

    // For quote pages: only clear after initial load is complete (user made manual changes)
    // For non-quote pages: clear when values are set (original behavior)
    const shouldClear = isFromQuotePage
      ? initialQuoteLoadCompleteRef.current
      : (fields?.additionalServices.length > 0 ||
        fields?.destinationNoOfFlights ||
        fields?.destinationExtramen ||
        fields?.destinationStorage ||
        fields?.destinationWaitTimeMinutes);

    if (shouldClear) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
      setShowLTLList(false);
      setIsSelectedParent(false);
      resetLocationState();
    }
  }, [
    fields?.additionalServices.length,
    fields?.destinationNoOfFlights,
    fields?.destinationExtramen,
    fields?.destinationStorage,
    fields?.destinationWaitTimeMinutes,
    state?.page,
  ]);

  /**
   * Keeps the Final Mile multi-select and the legacy single `serviceSelected` in step so
   * the existing single-service consumers (validation, the disabled-button memo, the LTL
   * payloads) keep working unchanged.
   *
   * Deliberately driven from the change handler rather than an effect: an effect keyed on
   * the order type would also fire when a saved quote or a returning navigation restores
   * `serviceSelected` directly, and would overwrite that restore with an empty selection.
   */
  const handleServiceSelectionChange = (rows: any) => {
    const nextRows = Array.isArray(rows) ? rows : [];
    setServiceSelectedList(nextRows);
    setServiceSelected(nextRows[0] ?? {});
    clearMandatory(DROPDOWN_FIELD_KEYS.service);
  };

  useEffect(() => {

    if (!saveQuotationPage) {
      setIsQuote(false);
      setServiceSelected([]);
      setServiceSelectedList([]);
      // Service Type always starts at Delivery when the order type changes.
      serviceTypeRef.current = LOGISTICS_TYPE.DELIVERY;
      setServiceType(LOGISTICS_TYPE.DELIVERY);
    }
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
    }
    if (
      (orderTypeRowData &&
        orderTypeRowData.id &&
        orderTypeRowData?.name &&
        orderTypeRowData?.name?.toLowerCase() === "ltl") ||
      orderTypeRowData?.name?.toLowerCase() === "expedited" ||
      orderTypeRowData?.name?.toLowerCase() === "ltl + final mile"
    ) {
      getAccessorialsList();
      getServiceList(orderTypeRowData.id || "");
      setNotLTL(false);
      // Additional services (Remove & Disposal / Order Swap & Exchange) only apply
      // to Final Mile. Clear any previously selected values so hidden checkboxes
      // are not sent in the quote/order payload after switching order type.
      if (fields.additionalServices.length) {
        setFields((prev) => ({ ...prev, additionalServices: [] }));
      }
    } else {
      if (orderTypeRowData && orderTypeRowData.id) {
        getServiceList(
          orderTypeRowData?.id,
          isFinalMileOrderType && !saveQuotationPage
            ? LOGISTICS_TYPE.DELIVERY
            : undefined,
        );
      }
      setNotLTL(true);
    }
    if (data.length && !saveQuotationPage) {
      const updatedData: any = data.map((item) => {
        item.packageType = "";
        return item;
      });
      setData([...updatedData]);
    }
  }, [orderTypeRowData]);

  const getAccessorialsList = async () => {
    try {
      setLoading(true);
      let url = `/order/ltl-accessorial/wwex`;
      const agentResponse = await getApi<any>(url);
      if (agentResponse.success) {
        const data = agentResponse.data || [];
        const pickup = data.filter((item: any) => item?.isPickup);
        setPickupAccessorialDropdownData(pickup);
        const drop = data.filter((item: any) => item?.isDelivery);
        setDropAccessorialDropdownData(drop);
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getServiceList = async (orderType: string, logisticsType?: string) => {
    try {
      setLoading(true);
      orderType = orderType.split(" + ").join(",");
      let url = `/agent/service/?limit=15&page=1&orderTypeId=${orderType}`;
      if (logisticsType) {
        url += `&logisticsType=${logisticsType}`;
      }
      const agentResponse = await getApi<any>(url);
      // Final Mile only: drop a response for a Service Type the user has since switched away from.
      if (logisticsType && logisticsType !== serviceTypeRef.current) {
        return [];
      }
      if (agentResponse.success) {
        setServiceList(agentResponse?.data);
        return agentResponse?.data || [];
      }
    } catch (error) {
      console.log(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
    return [];
  };

  /**
   * Final Mile Service Type change. The Service Level list is filtered solely by the
   * service's `logisticsType` (delivery | pickup) — never by service name. When Pickup has
   * exactly one service level, it is selected automatically.
   */
  const handleServiceTypeChange = async (_: string, item: any) => {
    // The choice is required: ignore a null selection so the field can never be cleared.
    if (!item?.id || item.id === serviceType) return;
    serviceTypeRef.current = item.id;
    setServiceType(item.id);
    setIsQuote(false);
    handleServiceSelectionChange([]);
    const services = await getServiceList(orderTypeRowData?.id || "", item.id);
    if (item.id === LOGISTICS_TYPE.PICKUP && services.length === 1) {
      handleServiceSelectionChange([{ id: services[0].id, name: services[0].name }]);
    }
  };
  /**
   * useEffect Hook - Fetches and updates the destination address details
   * (city, state, country) whenever the user changes the zipcode.
   */
  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
    }
  }, [fields?.destinationZipcode]);

  useEffect(() => {
    getZipcodeDetails();
    if (!saveQuotationPage) {
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
    }
  }, [fields?.OriginZipcode]);
  /**
   * useEffect Hook - Runs on component mount to fetch country codes, agent service
   * list, and updates the customer data based on user role.
   */
  useEffect(() => {

    if (!isUserOfOmniMove && userRole === "customer") {
      setCustomerRowData({
        id: localStorage.getItem(CUSTOMER_ID) ?? "",
        name: "",
        customerNumber: "",
        customerLevel: "",
      });
    }

    if (state?.page === "Save Quotation") {
      setSaveQuotationPage(true);
      getQuotationDetails(state.id, state?.quoteId);
    }

    if (state?.page === "Quick Quote" || state?.page === "Traditional Quote") {

      setSaveQuotationPage(true);
      setHubNameFallBack(state?.selectedRow?.ltlFMData?.hub?.name);
      // Skip the next quote clear triggered by field changes from populateQuoteFromRawData
      skipQuoteClearRef.current = true;
      // Remember the quote's reference2 and read the customer's current Order
      // Reference 2 configuration, so the prefill effect can decide between the two.
      loadedOrderReference2Ref.current = state?.data?.reference2 ?? "";
      fetchOrderReference2Config(
        state?.data?.customer?.id,
        state?.data?.associatedCustomer?.id
      ).then(setOrderReference2Config);
      populateQuoteFromRawData(state.data);
      // populateQuoteFromRawData only receives state.data, so it can't see
      // state.ltlAccessorialData; restore these here unconditionally so they
      // aren't lost when state.isSelected is false (e.g. Banyan/Providence quotes).
      if (state?.data?.originAddress?.accessorials?.length > 0) {
        setPickupAccessorialsSelected(state.data.originAddress.accessorials);
      }
      if (state?.ltlAccessorialData?.dropAccessorialsSelected?.length > 0) {
        setDropAccessorialsSelected(
          state.ltlAccessorialData.dropAccessorialsSelected
        );
      }
      // Prefer ltlAccessorialData (carries the real id), but originAddress.locationType
      // travels through the same formData channel that already proves reliable for
      // Pickup Accessorials above — fall back to it (reconstructing a {id, name} pair
      // from the name string) so a quote row that doesn't carry ltlAccessorialData
      // cleanly still restores the pickup selection instead of coming back blank.
      const restoredPickupLimitedAccess =
        state?.ltlAccessorialData?.pickupLimitedAccessSelected ||
        (state?.data?.originAddress?.locationType
          ? {
            id: state.data.originAddress.locationType,
            name: state.data.originAddress.locationType,
          }
          : undefined);
      if (restoredPickupLimitedAccess) {
        setPickupLimitedAccesssSelected(restoredPickupLimitedAccess);
      }
      if (state?.ltlAccessorialData?.dropLimitedAccessSelected) {
        setDropLimitedAccesssSelected(
          state.ltlAccessorialData.dropLimitedAccessSelected
        );
      }
      setLfmQuoteId(state?.quoteId);
      const shouldShowLTLList = (state?.selectedRow?.isBanyan || state?.selectedRow?.isProvidence) ?? state?.selectedData?.banyanType ?? state?.selectedData?.orderFor?.toLowerCase() === "providence";

      if (!(shouldShowLTLList)) {
        setShowLTLData(true);
        setShowLTLList(false);
      }

      setShowFMList(true);
      setIsQuote(true);
      setIsQuickQuoteDataReady(true);
      initialQuoteLoadCompleteRef.current = true;
    }
  }, []);

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const getQuotationDetails = async (quoteGroupId: string, quoteId: string) => {
    try {
      setLoading(true);
      let url = `/order/order-quote/detailsavedquotation?quoteGroupById=${quoteGroupId}&quoteId=${quoteId}`;
      const quoteResponse = await getApi<any>(url);
      if (quoteResponse.success) {
        const data = quoteResponse?.data?.[quoteGroupId]?.[0] || [];
        setSavedData(data);
        setSavedQuoteData && setSavedQuoteData(data);
        if (data?.masterHub?.name) {
          setHasMasterHub(true);
          setMasterHubName(data?.masterHub?.name);
        }
        if (data?.masterHub?.address) {
          setMasterHubAddress(data?.masterHub?.address);
        }
        // Remember the quote's reference2 and read the customer's current Order
        // Reference 2 configuration from the API — the quote only holds a snapshot,
        // so a value changed since the quote was saved would otherwise be missed.
        // Both ids are fetched explicitly: the parent has to be configured too, so
        // that removing the sub-customer hands Order Reference 2 back to it.
        const isThirdLevel = customerLevel === "first" && data?.customer?.customerLevel === "third";
        loadedOrderReference2Ref.current = data?.reference2 ?? "";
        setOrderReference2Config(
          await fetchOrderReference2Config(
            data?.customer?.id,
            data?.customer?.parentCustomer?.id
          )
        );
        if (isThirdLevel) {
          const parentId = data?.customer?.parentCustomer?.id ?? "";
          setCustomerRowData({
            id: parentId,
            name: data?.customer?.parentCustomer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.parentCustomer?.customerLevel ?? "",
          });
          setParentCustomerId(parentId);
          setTimeout(() => {
            setAssCustomerRowData({
              id: data?.customer?.id ?? "",
              name: data?.customer?.name ?? "",
              customerNumber: "",
              customerLevel: data?.customer?.customerLevel ?? "",
            });
          }, 300);
        } else {
          setCustomerRowData({
            id: data?.customer?.id ?? "",
            name: data?.customer?.name ?? "",
            customerNumber: "",
            customerLevel: data?.customer?.customerLevel ?? "",
          });
          // For second-level customers, set parentCustomerId so the sub-customer dropdown
          // fetches its list. saveQuotationPage=true blocks the normal useEffect that does this.
          if (data?.customer?.customerLevel === "second") {
            setParentCustomerId(data?.customer?.id ?? "");
          }
        }
        setOrderTypeRowData({
          id: data?.orderType?.id ?? "",
          name: data?.orderType?.name ?? "",
          description: data?.orderType?.description ?? "",
        });
        setServiceSelected({
          id: data?.service?.id ?? "",
          name: data?.service?.name ?? "",
        });
        // Keep the Final Mile multi-select in step with the restored service so the
        // chip is rendered; other order types never read this list.
        setServiceSelectedList(
          data?.service?.id
            ? [{ id: data.service.id, name: data.service.name ?? "" }]
            : []
        );
        setServiceType(
          data?.service?.logisticsType === LOGISTICS_TYPE.PICKUP
            ? LOGISTICS_TYPE.PICKUP
            : LOGISTICS_TYPE.DELIVERY
        );
        let refArray = [
          {
            referenceCount: 2,
            referenceValue: data?.reference2 ?? "",
          },
        ];
        if (data?.reference3) {
          refArray.push({
            referenceCount: 3,
            referenceValue: data?.reference3 ?? "",
          });
        }
        if (data?.reference4) {
          refArray.push({
            referenceCount: 4,
            referenceValue: data?.reference4 ?? "",
          });
        }
        if (data?.reference5) {
          refArray.push({
            referenceCount: 5,
            referenceValue: data?.reference5 ?? "",
          });
        }
        setMoreReferences(refArray);

        setFields((prevData) => ({
          ...prevData,
          destinationAddress: data?.destinationAddress?.address ?? "",
          destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
          destinationZipcode: data?.destinationAddress?.zipCode ?? "",
          destinationCity: data?.destinationAddress?.city ?? "",
          destinationState: data?.destinationAddress?.state ?? "",
          destinationCountry: data?.destinationAddress?.country ?? "",
          destinationBusinessLocation:
            data?.destinationAddress?.businessLocation ?? "",
          destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
          destinationConsigneePhoneExt:
            data?.destinationAddress?.countryCallingCode ?? "+1",
          destinationConsigneePhoneNumber:
            data?.destinationAddress?.phoneNumber ?? "",
          destinationConsigneeContactEmail:
            data?.destinationAddress?.contactEmail ?? "",
          destinationExtramen: data?.destinationAddress?.extraMen ?? "",
          destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
          destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
          destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
          destinationWeight: data?.destinationAddress?.weight ?? "",
          destinationSpecialInstructions:
            data?.destinationAddress?.specialInstructions ?? "",
          orderReference: data?.reference ?? "",
          orderReference2: data?.reference2 ?? "",
          orderReference3: data?.reference3 ?? "",
          orderReference4: data?.reference4 ?? "",
          orderReference5: data?.reference5 ?? "",
          additionalServices: data?.additionalServices ?? [],
          selectedServiceId: data?.service?.id ?? "",
          OriginAddress: data?.originAddress?.address ?? "",
          OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
          OriginZipcode: data?.originAddress?.zipCode ?? "",
          OriginCity: data?.originAddress?.city ?? "",
          OriginState: data?.originAddress?.state ?? "",
          OriginCountry: data?.originAddress?.country ?? "",
          OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
          OriginConsigneeName: data?.originAddress?.contactName ?? "",
          OriginConsigneePhoneExt:
            data?.originAddress?.countryCallingCode ?? "+1",
          OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
          OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
          PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
            : "",
          PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
            : "",
          PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
            : "",
          PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
            ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
            : "",
          PickUpDockDate: data?.lTLPickUpDock?.pickUpDate
            ? normalizeDate(data?.lTLPickUpDock?.pickUpDate)
            : moment().format("YYYY-MM-DD"),
          PickUpDockTime: data?.lTLPickUpDock?.pickUpDate
            ? extractTime(data?.lTLPickUpDock?.pickUpDate)
            : "08:00 AM",
          PickUpDockName: data?.lTLPickUpDock?.dockName ?? "",
          PickUpDOckNumber: data?.lTLPickUpDock?.pickupNumber ?? "",
          PickUpDockOpenTime:
            data?.lTLPickUpDock?.dockOpenTime === "Invalid date"
              ? DEFAULT_DOCK_OPEN_TIME
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockOpenTime) || DEFAULT_DOCK_OPEN_TIME,
          PickUpDOckCloseTime:
            data?.lTLPickUpDock?.dockCloseTime === "Invalid date"
              ? DEFAULT_DOCK_CLOSE_TIME
              : formatTimeForDisplay(data?.lTLPickUpDock?.dockCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
          PickUpDOckNote: data?.lTLPickUpDock?.note ?? "",
          PickUpFCFS: data?.lTLPickUpDock?.firstComeFirstServe ?? "",
          LTLDropAccessorials: [],
          DropDockName: data?.ltlDropDock?.dockName ?? "",
          DropDOckNumber: data?.ltlDropDock?.pickupNumber ?? "",
          DropDockOpenTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropOpenTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropOpenTime) || DEFAULT_DOCK_OPEN_TIME,
          DropDOckCloseTime:
            data?.orderType?.name === "LTL + Final Mile"
              ? data?.ltlDropDock?.dropCloseTime ?? ""
              : formatTimeForDisplay(data?.ltlDropDock?.dropCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
          DropDOckNote: data?.ltlDropDock?.note ?? "",
          DropFCFS: data?.ltlDropDock?.firstComeFirstServe ?? false,
        }));

        // Add this right after the setFields block:
        setAddressSelectedOrigin({
          id: data?.originAddress?.id || "",
          address: data?.originAddress?.address || "",
          aptSuit: data?.originAddress?.aptSuit || "",
          zipCode: data?.originAddress?.zipCode || "",
          city: data?.originAddress?.city || "",
          state: data?.originAddress?.state || "",
          country: data?.originAddress?.country || "",
          businessLocation: data?.originAddress?.businessLocation || "",
          contactPerson: data?.originAddress?.contactName || "",
          phoneNumber: data?.originAddress?.phoneNumber || "",
          countryCallingCode: data?.originAddress?.countryCallingCode || "+1",
          contactEmail: data?.originAddress?.contactEmail || "",
        });

        setAddressSelected({
          id: data?.destinationAddress?.id || "",
          address: data?.destinationAddress?.address || "",
          aptSuit: data?.destinationAddress?.aptSuit || "",
          zipCode: data?.destinationAddress?.zipCode || "",
          city: data?.destinationAddress?.city || "",
          state: data?.destinationAddress?.state || "",
          country: data?.destinationAddress?.country || "",
          businessLocation: data?.destinationAddress?.businessLocation || "",
          contactPerson: data?.destinationAddress?.contactName || "",
          phoneNumber: data?.destinationAddress?.phoneNumber || "",
          countryCallingCode: data?.destinationAddress?.countryCallingCode || "+1",
          contactEmail: data?.destinationAddress?.contactEmail || "",
        });
        const mapOrderData = (userData: any) =>
          userData.map((user: any, index: number) => ({
            id: user?.id || "",
            serial: userData.length - index,
            density: user?.density || "",
            cbm: user?.cbm || "",
            description: user?.description || "",
            handlingUnit: user?.handlingUnit || "",
            quantity: user?.quantity || "",
            weight: user?.weight || "",

            itemDimension: {
              length: user?.length || "",
              width: user?.width || "",
              height: user?.height || "",
            },
            unit: user?.unit
              ? { name: user?.unit, id: user?.unit }
              : user?.weightUnit === "kgs"
                ? { name: "CM", id: "CM" }
                : { name: "IN", id: "IN" },
            itemClass: user?.itemClass || "",
            comments: user?.comments || "",
            nmfc: user?.nmfc || "",
            skuNumber: user?.skuNumber || "",
            packageType: user?.packageType || "",
            package: user?.package || [],
            stackable:
              user?.stackable === true
                ? { name: "Yes", id: "Yes" }
                : { name: "No", id: "No" },
            weightUnit: { name: user?.weightUnit, id: user?.weightUnit },
            hazmat: user?.hazmats?.unno ? true : false,
            linearFeet: user?.linearFeet,
            hazardMaterial: user?.hazmats?.unno
              ? {
                itemName: user.hazmats?.itemName || "",
                packageType:
                  typeof user.hazmats?.packageType === "object"
                    ? user.hazmats?.packageType?.name
                    : user.hazmats?.packageType || "",
                hazmatClass: user.hazmats?.hazmatClass || "",
                unno: user.hazmats?.unno || "",
                properShippingName: user.hazmats?.properShippingName || "",
                packagingGroup: user.hazmats?.packagingGroup || "",
                contact: user.hazmats?.contact || "",
                phoneNumber: user.hazmats?.phoneNumber || "",
                countryCallingCode: user.hazmats?.countryCallingCode || "",
                poison: user.hazmats?.poison ? true : false,
                isReportableQuantity: user.hazmats?.isReportableQuantity
                  ? true
                  : false,
                isMarinePollutant: user.hazmats?.isMarinePollutant
                  ? true
                  : false,
              }
              : {},
          }));
        setData(mapOrderData([...(data?.orderItem || [])].reverse()));
        if (
          data?.orderType?.name === "Final Mile" &&
          expiredData(data?.createdAt) &&
          !data?.isExpiry
        ) {
          let QuoteDataArr = [];
          const mappedData = (qData: any) => {
            const services = quoteResponse?.data?.[quoteGroupId].map(
              (item: any, serviceIndex: number) => ({
                id: item?.id,
                serial: serviceIndex + 1,
                serviceId: item?.service?.id,
                serviceName: item?.service?.name || "",
                description: item?.service?.description || "",
                serviceLevel: item?.serviceType?.name,
                quotePrice: item?.pricing?.total || 0.0,
                fuelSurcharge: item?.rateDetails?.fuelSurcharge || 0.0,
                additionalOrderCharges:
                  item?.rateDetails?.additionalOtherCharges,
                basePrice: item?.rateDetails?.basePrice || 0.0,
                totalWeight: item?.totalWeight || 0,
                deliverWithin: item?.deliverWithin || 0,
                distanceToDestination: item?.distanceToDestination || 0,
                shortName: item?.service?.shortName || "",
                longName: item?.service?.longName || "",
                useOver150Lbs: item?.service?.useOver150Lbs || false,
                address: hasMasterHub
                  ? masterHubAddress
                  : item?.agentHub?.address || "",
                operationHrsFrom: hasMasterHub
                  ? masterHubAddress?.operationHrsFrom
                  : item.agentHub?.address?.operationHrsFrom || 0,
                operationHrsTo: hasMasterHub
                  ? masterHubAddress?.operationHrsTo
                  : item.agentHub?.address?.operationHrsTo || 0,
                hubName: hasMasterHub
                  ? masterHubName
                  : item.agentHub?.name || "",
                agentHubId: item.agentHub?.id || "",
                addressName: hasMasterHub
                  ? masterHubAddress.address
                  : item.agentHub?.address || "",
                agentName: item.agentHub?.agent?.name || "",
                quoteId: item?.id,
                allQuoteId: [item?.id],
              })
            );
            const info: QuoteInfo = {
              agentName: qData.agentHub?.agent?.name || "",
              operationHrsFrom: hasMasterHub
                ? masterHubAddress?.operationHrsFrom
                : qData.agentHub?.operationHrsFrom || "",
              operationHrsTo: hasMasterHub
                ? masterHubAddress?.operationHrsTo
                : qData.agentHub?.operationHrsTo || "",
              hubName: hasMasterHub
                ? masterHubName
                : qData.agentHub?.name || "",
              addressName: hasMasterHub
                ? masterHubAddress?.address
                : qData.agentHub?.address || "",
              address: hasMasterHub
                ? masterHubAddress?.address
                : qData?.agentHub?.address || "",
              distanceToDestination: qData?.distanceToDestination || 0,
            };

            return { info, services };
          };
          QuoteDataArr.push(mappedData(data));
          SetQuoteData(QuoteDataArr);
          setGetQuote(true);
        }
        setErrors((prevErrors) => ({
          ...prevErrors,
          destinationZipcode: "",
          OriginZipcode: "",
          DropDockDate: "",
          PickUpDockTime: "",
          PickUpDockDate: "",
          destinationConsigneePhoneNumber: "",
          OriginConsigneePhoneNumber: "",
        }));
        if (
          data?.orderType?.name === "LTL" ||
          data?.orderType?.name === "LTL + Final Mile"
        ) {
          setPickupAccessorialsSelected(data?.originAddress?.accessorials);
          setDropAccessorialsSelected(data?.destinationAddress?.accessorials);
          // setPickupLimitedAccesssSelected({id: data?.originAddress?.locationType, name: data?.originAddress?.locationType});
          // setDropLimitedAccesssSelected({id: data?.destinationAddress?.locationType, name: data?.destinationAddress?.locationType});
          setPickupLimitedAccesssSelected(data?.originAddress?.locationType);
          setDropLimitedAccesssSelected(data?.destinationAddress?.locationType);
        }
      }
    } catch (error) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const populateQuoteFromRawData = (data: any) => {

    setFields((prevData: any) => ({
      ...prevData,
      destinationAddress: data?.destinationAddress?.address ?? "",
      destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
      destinationZipcode: data?.destinationAddress?.zipCode ?? "",
      destinationCity: data?.destinationAddress?.city ?? "",
      destinationState: data?.destinationAddress?.state ?? "",
      destinationCountry: data?.destinationAddress?.country ?? "",
      destinationBusinessLocation:
        data?.destinationAddress?.businessLocation ?? "",
      destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
      destinationConsigneePhoneExt:
        data?.destinationAddress?.countryCallingCode ?? "+1",
      destinationConsigneePhoneNumber:
        data?.destinationAddress?.phoneNumber ?? "",
      destinationConsigneeContactEmail:
        data?.destinationAddress?.contactEmail ?? "",
      destinationExtramen: data?.destinationAddress?.extraMen ?? "",
      destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
      destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
      destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
      destinationWeight: data?.destinationAddress?.weight ?? "",
      destinationSpecialInstructions:
        data?.destinationAddress?.specialInstructions ?? "",
      orderReference: data?.reference ?? "",
      orderReference2: data?.reference2 ?? "",
      orderReference3: data?.reference3 ?? "",
      orderReference4: data?.reference4 ?? "",
      orderReference5: data?.reference5 ?? "",
      additionalServices: data?.additionalServices ?? [],
      selectedServiceId: data?.service?.id ?? "",
      OriginAddress: data?.originAddress?.address ?? "",
      OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
      OriginZipcode: data?.originAddress?.zipCode ?? "",
      OriginCity: data?.originAddress?.city ?? "",
      OriginState: data?.originAddress?.state ?? "",
      OriginCountry: data?.originAddress?.country ?? "",
      OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
      OriginConsigneeName: data?.originAddress?.contactName ?? "",
      OriginConsigneePhoneExt: data?.originAddress?.countryCallingCode ?? "+1",
      OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
      OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
      PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
        ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
        : "",
      PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
        ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
        : "",
      PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
        ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
        : "",
      PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
        ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
        : "",
      PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
        ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
        : "",
      PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
        ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
        : "",
      PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
        ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
        : "",
      PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
        ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
        : "",
      PickUpDockDate: data?.pickUpDock?.pickUpDate
        ? normalizeDate(data?.pickUpDock?.pickUpDate)
        : moment().format("YYYY-MM-DD"),
      PickUpDockTime: data?.pickUpDock?.pickUpDate
        ? extractTime(data?.pickUpDock?.pickUpDate)
        : "08:00 AM",
      PickUpDockName: data?.pickUpDock?.dockName,
      PickUpDOckNumber: data?.pickUpDock?.pickupNumber,
      PickUpDockOpenTime: formatTimeForDisplay(data?.pickUpDock?.dockOpenTime) || DEFAULT_DOCK_OPEN_TIME,
      PickUpDOckCloseTime: formatTimeForDisplay(data?.pickUpDock?.dockCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
      PickUpDOckNote: data?.pickUpDock?.note ?? "",
      PickUpFCFS: data?.pickUpDock?.firstComeFirstServe,
      LTLDropAccessorials: [],
      DropDockName: "",
      DropDOckNumber: "",
      DropDockOpenTime:
        data?.orderType?.name === "LTL + Final Mile"
          ? ""
          : formatTimeForDisplay(data?.dropDock?.dropOpenTime) || DEFAULT_DOCK_OPEN_TIME,
      DropDOckCloseTime:
        data?.orderType?.name === "LTL + Final Mile"
          ? ""
          : formatTimeForDisplay(data?.dropDock?.dropCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
      DropDOckNote: "",
      DropFCFS: false,
      MidStopAddress: data?.MidStopAddress || "",
      MidStopAPTSuit: data?.MidStopAPTSuit || "",
      MidStopZipcode: data?.MidStopZipcode || "",
      MidStopCity: data?.MidStopCity || "",
      MidStopState: data?.MidStopState || "",
      MidStopCountry: data?.MidStopCountry || "",
      MidStopContactName: data?.MidStopContactName || "",
      MidStopPhoneNumber: data?.MidStopPhoneNumber || "",
      MidStopPhoneNumberExt: data?.MidStopPhoneNumberExt || "+1",
      MidStopContactEmail: data?.MidStopContactEmail || [],
      agentHubId: data?.agentHubId,
      // selectedServiceId: data?.selectedServiceId,
      selectedQuoteId: data?.selectedQuoteId,
      internalLTL: data?.internalLTL,
      allQuoteId: data?.allQuoteId,
      CarrierName: data?.CarrierName,
      serviceCost: data?.serviceCost,
      scac: data?.scac,
      distance: data?.distance,
      offerId: data?.offerId,
      // costDetails: additionalCharges(row),
      markup: data?.markup,
      productTransactionId: data?.productTransactionId,
      customerMarkupType: data?.customerMarkupType,
      plProvider: "",
      courierPro1: "",
      courierPro2: "",
      //Final mile box details
      agentName: data?.agentName,
      hubName: data?.hubName,
      city: data?.city,
      zipCode: data?.zipCode,
      country: data?.country,
      AgentHubId: data?.AgentHubId,
      distanceToDestination: data?.distanceToDestination || 0,
      operationHrsFrom: data?.operationHrsFrom || "00:00",
      operationHrsTo: data?.operationHrsTo || "23:59",
      quotePrice: data?.quotePrice || 0,
      finalmile_quote_id: data?.finalmile_quote_id,
      source: state?.selectedData?.source ?? state?.selectedRow?.source,
      finalMileSource: state?.selectedRow?.finalMileSource,
      orderFor: data?.orderFor ?? state?.selectedData?.orderFor ?? state?.selectedRow?.orderFor,
      carrierQuoteId: data?.carrierQuoteId ?? state?.selectedData?.carrierQuoteId ?? state?.selectedRow?.carrierQuoteId,
      rateRequestLocator: data?.rateRequestLocator ?? state?.selectedData?.rateRequestLocator ?? state?.selectedRow?.rateRequestLocator,
    }));
    setAddressSelectedOrigin({
      id: data?.originAddress?.id || "",
      address: data?.originAddress?.address || "",
      aptSuit: data?.originAddress?.aptSuit || "",
      zipCode: data?.originAddress?.zipCode || "",
      city: data?.originAddress?.city || "",
      state: data?.originAddress?.state || "",
      country: data?.originAddress?.country || "",
      businessLocation: data?.originAddress?.businessLocation || "",
      contactPerson: data?.originAddress?.contactName || "",
      phoneNumber: data?.originAddress?.phoneNumber || "",
      countryCallingCode: data?.originAddress?.countryCallingCode || "+1",
      contactEmail: data?.originAddress?.contactEmail || "",
    });
    setAddressSelected({
      id: data?.destinationAddress?.id || "",
      address: data?.destinationAddress?.address || "",
      aptSuit: data?.destinationAddress?.aptSuit || "",
      zipCode: data?.destinationAddress?.zipCode || "",
      city: data?.destinationAddress?.city || "",
      state: data?.destinationAddress?.state || "",
      country: data?.destinationAddress?.country || "",
      businessLocation: data?.destinationAddress?.businessLocation || "",
      contactPerson: data?.destinationAddress?.contactName || "",
      phoneNumber: data?.destinationAddress?.phoneNumber || "",
      countryCallingCode: data?.destinationAddress?.countryCallingCode || "+1",
      contactEmail: data?.destinationAddress?.contactEmail || "",
    })

    setErrors((prev) => ({
      ...prev,
      destinationConsigneePhoneNumber: "",
      OriginConsigneePhoneNumber: "",
    }));

    // Set customer, associated customer, order type, and service
    if (data?.customer) {
      setCustomerRowData(data?.customer);
      // saveQuotationPage=true blocks the normal useEffect that sets parentCustomerId,
      // so set it explicitly here so the sub-customer dropdown can fetch its list.
      if (data?.customer?.id) setParentCustomerId(data?.customer?.id);
    }
    // Delay setting the sub-customer so the MutliDropdown's parentCustomerId useEffect
    // (which calls handleRemoveRowData to clear the selection) fires first.
    if (data?.associatedCustomer?.id) {
      setTimeout(() => {
        setAssCustomerRowData(data?.associatedCustomer);
      }, 300);
    }
    if (data?.orderType) setOrderTypeRowData(data?.orderType);
    if (data?.service) {
      setServiceSelected(data?.service);
      // Mirror onto the Final Mile multi-select so the chip renders on this restore too.
      setServiceSelectedList(data?.service?.id ? [data.service] : []);
      // Quick Quote passes `serviceType`; other senders may only carry service.logisticsType.
      setServiceType(
        (data?.serviceType ?? data?.service?.logisticsType) === LOGISTICS_TYPE.PICKUP
          ? LOGISTICS_TYPE.PICKUP
          : LOGISTICS_TYPE.DELIVERY
      );
    }
    if (data?.pickupAccessorials?.length > 0)
      setPickupAccessorialsSelected(data?.pickupAccessorials);
    if (data?.dropAccessorials?.length > 0)
      setDropAccessorialsSelected(data?.dropAccessorials);
    if (data?.pickupLimitedAccess)
      setPickupLimitedAccesssSelected(data?.pickupLimitedAccess);
    if (data?.dropLimitedAccess)
      setDropLimitedAccesssSelected(data?.dropLimitedAccess);
    // Map items
    const mapOrderData = (items: any[]) =>
      items.map((item, index) => ({
        id: item?.id || item?.descriptionId || "",
        descriptionId: item?.id || "",
        serial: items.length - index,
        density: item?.density || "",
        cbm: item?.cbm || "",
        description: item?.description || "",
        handlingUnit: item?.handlingUnit || "",
        quantity: item?.quantity || "",
        weight: item?.weight || "",
        itemDimension: {
          length: item?.length || "",
          width: item?.width || "",
          height: item?.height || "",
        },
        unit: item?.unit
          ? { name: item.unit, id: item.unit }
          : item?.weightUnit === "kgs"
            ? { name: "CM", id: "CM" }
            : { name: "IN", id: "IN" },
        itemClass: item?.itemClass || "",
        comments: item?.comments || "",
        nmfc: item?.nmfc || "",
        skuNumber: item?.skuNumber || "",
        packageType: item?.packageType || "",
        package: item?.package || [],
        stackable:
          item?.stackable === true
            ? { name: "Yes", id: "Yes" }
            : { name: "No", id: "No" },
        weightUnit: { name: item?.weightUnit, id: item?.weightUnit },
        hazmat: item?.hazardMaterial?.itemName ? true : false,
        linearFeet: item?.linearFeet,
        hazardMaterial: item?.hazardMaterial || {},
      }));
    setData(mapOrderData(data?.items || []));
    // Set quoteData if pricing and service info exist
    if (data?.pricing && data?.service) {
      const quoteInfo: QuoteInfo = {
        agentName: data?.agentHub?.agent?.name || "",
        operationHrsFrom: hasMasterHub
          ? masterHubAddress?.operationHrsFrom
          : data?.agentHub?.operationHrsFrom || "",
        operationHrsTo: hasMasterHub
          ? masterHubAddress?.operationHrsTo
          : data?.agentHub?.operationHrsTo || "",
        hubName: hasMasterHub ? masterHubName : data?.agentHub?.name || "",
        addressName: hasMasterHub
          ? masterHubAddress?.address
          : data?.agentHub?.address?.address || "",
        address: hasMasterHub
          ? masterHubAddress
          : data?.agentHub?.address || {},
        distanceToDestination: data?.distanceToDestination || 0,
      };

      const services = [
        {
          id: data?.id,
          serial: 1,
          serviceId: data?.service?.id,
          serviceName: data?.service?.name || "",
          description: data?.service?.description || "",
          serviceLevel: data?.serviceType?.name || "",
          quotePrice: data?.pricing?.total || 0.0,
          fuelSurcharge: data?.rateDetails?.fuelSurcharge || 0.0,
          additionalOrderCharges: data?.rateDetails?.additionalOtherCharges,
          basePrice: data?.rateDetails?.basePrice || 0.0,
          totalWeight: data?.totalWeight || 0,
          deliverWithin: data?.deliverWithin || 0,
          distanceToDestination: data?.distanceToDestination || 0,
          shortName: data?.service?.shortName || "",
          longName: data?.service?.longName || "",
          useOver150Lbs: data?.service?.useOver150Lbs || false,
          address: hasMasterHub
            ? masterHubAddress
            : data?.agentHub?.address || {},
          operationHrsFrom: hasMasterHub
            ? masterHubAddress?.operationHrsFrom
            : data?.agentHub?.address?.operationHrsFrom || "",
          operationHrsTo: hasMasterHub
            ? masterHubAddress?.operationHrsTo
            : data?.agentHub?.address?.operationHrsTo || "",
          hubName: hasMasterHub ? masterHubName : data?.agentHub?.name || "",
          agentHubId: data?.agentHub?.id || "",
          addressName: hasMasterHub
            ? masterHubAddress?.address
            : data?.agentHub?.address?.address || "",
          agentName: data?.agentHub?.agent?.name || "",
          quoteId: data?.id,
          allQuoteId: [data?.id],
        },
      ];
      ;
      SetQuoteData([{ info: quoteInfo, services }]);
      setGetQuote(true);
    }
  };

  const getApiPackageData = async () => {
    const url = `/service/packagetype?from=order&finalMileLTL=both`;
    const apiResponse = await getApi<any>(url);

    if (!apiResponse?.success) return;

    let packageData
    if (orderTypeRowData?.name === "LTL + Final Mile" || orderTypeRowData?.name === "LTL") {
      packageData = apiResponse?.data?.ltl
    } else {
      packageData = apiResponse?.data?.finalMile
    }
    packageData = packageData.filter((item: any) => item.isActive && !item.isDeleted)
      .map((item: any) => ({
        id: item.id,
        name: item.packageCode || "",
      }));
    setApiPackageType(packageData);
  };

  useEffect(() => {

    if (orderTypeRowData?.name === "LTL + Final Mile") {
      setDropAccessorialsSelected([]);
      setDropLimitedAccesssSelected({ id: "", name: "" });
      // The drop dock inputs are only masked in the JSX for this order type, so any
      // value left behind (typed, or pre-filled from the address book) would still
      // beat the 00:00 default in coalesceTime and reach the payload. Clear it.
      setFields((prev) => ({
        ...prev,
        DropFCFS: false,
        DropDockOpenTime: "",
        DropDOckCloseTime: "",
      }));
    } else {
      // Restore the default dock hours cleared above when switching away from LTL + Final Mile.
      setFields((prev) => ({
        ...prev,
        DropDockOpenTime: prev.DropDockOpenTime || DEFAULT_DOCK_OPEN_TIME,
        DropDOckCloseTime: prev.DropDOckCloseTime || DEFAULT_DOCK_CLOSE_TIME,
      }));
    }
    if (
      orderTypeRowData?.name === "Expedited" ||
      orderTypeRowData?.name === "Final Mile" ||
      orderTypeRowData?.name === "LTL" ||
      orderTypeRowData?.name === "LTL + Final Mile" ||
      orderTypeRowData?.name === ""
    ) {
      getApiPackageData();
    }
  }, [orderTypeRowData?.name]);

  useEffect(() => {

    if (state?.page !== "Save Quotation") {
      setMasterHubName("");
      setMasterHubAddress({});
    }
  }, [getQuote]);

  /**
   * Adds a new reference object to the `moreReferences` array if the current
   * number of references is less than 4. Each new reference object will
   * have a `referenceCount` that increments based on the current length
   * of the `moreReferences` array and a default `referenceValue` set to an
   * empty string.
   *
   * The function uses optional chaining to safely access the length of
   * `moreReferences`. The `referenceCount` for the new reference is set
   * to one more than the current length of the array (plus one to adjust
   * for zero-based indexing).
   *
   * @function addMoreReferences
   * @returns {void} This function does not return a value. It updates the
   * state of `moreReferences` directly by calling `setMoreReferences`.
   */
  const addMoreReferences = () => {
    if (moreReferences?.length < 4) {
      let references = [...moreReferences];
      references.push({
        referenceCount: 1 + moreReferences?.length + 1,
        referenceValue: "",
      });
      setMoreReferences(references);
    }
  };

  /**
   * Removes a reference from the moreReferences array at the specified index.
   *
   * This function updates the state of moreReferences by removing the
   * reference at the given index, and it adjusts the referenceCount for
   * the remaining references to ensure they are sequentially numbered
   * starting from 2.
   *
   * @param index - The index of the reference to be removed from the moreReferences array.
   *
   * @returns {void} This function does not return a value. It updates the state of moreReferences.
   *
   * @throws {Error} Throws an error if moreReferences is undefined or not an array.
   */
  const removeReferences = (index: number) => {
    if (moreReferences?.length > 1) {
      let newRefArray: MoreReferencesType[] = [];
      let refIndex = 0;
      moreReferences.map((item, mIndex) => {
        if (index !== mIndex) {
          newRefArray.push({
            referenceCount: refIndex + 2,
            referenceValue: item.referenceValue,
          });
          refIndex++;
        }
      });
      setMoreReferences(newRefArray);
    }
  };

  /**
   * Handles changes to a reference field in a list of more references.
   *
   * This function updates the value of a specific reference based on the provided
   * index and data. It creates a new array of references with the updated value
   * while keeping the other references unchanged.
   *
   * @param {string} data - The new value to set for the reference at the specified index.
   * @param {number} index - The index of the reference to be updated.
   *
   * @returns {void} - This function does not return a value. It updates the state
   *                   of moreReferences using the setMoreReferences function.
   */
  const handleRefFieldChange = (data: string, index: number) => {
    let newRefArray: MoreReferencesType[] = [];
    moreReferences.map((item, mIndex) => {
      newRefArray.push({
        referenceCount: item.referenceCount,
        referenceValue: index === mIndex ? data : item.referenceValue,
      });
    });
    setMoreReferences(newRefArray);
  };

  /**
   * Handles changes in checkbox fields and updates the form values state.
   *
   * @param e - The checkbox change event.
   */
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target as HTMLInputElement;
    if (!notLTL) {
      setFields((prevData: any) => ({ ...prevData, [id]: !prevData[id] }));
    } else {
      // Reset quote when additional services change
      clearLTLQuoteDetails();
      clearFinalMileQuoteDetails();
      setShowLTLData(false);
      setShowFMList(false);
      setShowLTLList(false);
      setIsSelectedParent(false);
      setSavedData(null);
      resetLocationState();

      if (checked) {
        // Mutually exclusive: selecting one removes the other
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: [id],
        }));
      } else {
        // Uncheck: remove this option
        setFields((prevValues) => ({
          ...prevValues,
          additionalServices: prevValues.additionalServices.filter(
            (x) => x !== id
          ),
        }));
      }
    }
  };


  const handleSaveQuotation = async (quoteId: string | string[]) => {
    try {

      setLoading(true);
      const apiPayload = {
        // Final Mile saves every agent and service as one group and sends the full id
        // list; LTL + Final Mile still sends a single id and keeps the original payload.
        ...(Array.isArray(quoteId)
          ? { quoteIds: quoteId }
          : { quoteId: quoteId }),
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          specialInstructions: fields.destinationSpecialInstructions,
        },
        originAddress: null,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        additionalServices: fields.additionalServices,
      };
      const apiResponse = await postApi(SAVE_QUOTATION, apiPayload);
      if (apiResponse.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        // Clear Redux state on successful save
        dispatch(clearTraditionalQuoteData());
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Add (or reuse) this constant near your other endpoint constants
  const SAVE_QUOTATION_FOR_LTL = "/order/order-quote/savedquotationforltl";

  // ---- Optional small helpers (co-locate with your other utils/helpers) ----
  const asNum = (v: any, fallback = 0) => {
    const n = typeof v === "string" ? Number(v) : v;
    return Number.isFinite(n) ? n : fallback;
  };
  const asStr = (v: any, fallback = "") =>
    (typeof v === "string" ? v : v?.toString?.() || "") || fallback;
  const onlyDigits = (s?: string) => asStr(s).replace(/\D/g, "");

  // If you already have mapItemsDataToPayload, keep it; else use this LTL-specific mapper:
  const mapItemsDataToPayloadForLTL = (items: any[]) =>
    (items || []).map((it) => ({
      description: asStr(it.description),
      handlingUnit: asStr(it.handlingUnit ?? it.handlingUnitAmt ?? it.hu),
      quantity: asStr(it.quantity ?? it.qty),
      weight: asStr(it.weight),
      weightUnit: asStr(it.weightUnit ?? "lbs"),
      length: asStr(it.length),
      width: asStr(it.width),
      height: asStr(it.height),
      itemClass: asStr(it.itemClass),
      comments: asStr(it.comments),
      nmfc: asStr(it.nmfc),
      skuNumber: asStr(it.skuNumber),
      packageType: asStr(it.packageType ?? it.package_type ?? it.package),
      package: Array.isArray(it.package) ? it.package : [],
      stackable: Boolean(it.stackable),
      linearFeetStackable: asNum(it.linearFeetStackable, 0),
      linearFeet: asNum(it.linearFeet),
      density: asNum(it.density),
      cbm: asNum(it.cbm),
      hazardMaterial: it.hazardMaterial ?? {}, // keep structure if present
    }));

  // If you keep multiple quotes in state, adapt this to your source of truth.
  // Fallbacks: fields.quoteArray or an empty array.
  // const buildQuoteArray = () => {
  //   console.log("LTLIST", TLList);
  //   if (Array.isArray(TLList) && TLList.length > 0) {

  //     return TLList.map((q: any) => ({

  //       orderTypeId: orderTypeRowData?.id || "", // global state
  //       serviceId: fields?.selectedServiceId || "", // from form field
  //       shipmentOfferId: q.offerId || "",
  //       productTransactionId: q.productTransactionId || "",
  //       pricing: {
  //         total: parseFloat(
  //           q.priceWithFinalMile || q.priceWithMarkUp || q.price || 0
  //         ),
  //         carrierName: q.carrierName || "",
  //         carrierCode: q.scac || "",
  //         estimatedLTLTransitDay:  asNum(
  //         q?.quoteFrom === "banyan"
  //           ? q?.transitTime
  //           : q?.quoteFrom === "worldwide"
  //             ? offered?.shopRQShipment?.timeInTransit?.transitDays
  //             : q?.transitDays,
  //         0
  //       );//q.transitTime || 0,
  //         finalmileCarrierName: q.finalmileCarrierName || "",
  //       },
  //     }));
  //   }
  //   return [];
  // };
  const buildQuoteArray = () => {
    if (!Array.isArray(TLList) || TLList.length === 0) return [];

    // De-duplicate by offerId (shipmentOfferId)
    const seen = new Set<string>();

    return TLList.map((q: any) => {
      const offered =
        q?.quoteFrom === "worldwide"
          ? Array.isArray(q.offeredProductList)
            ? q.offeredProductList[0]
            : null
          : null;

      // Prefer the most customer-facing total; fall back sensibly
      const total = asNum(
        q.orderTypeName === 'LTL + Final Mile'
          ?
          q.totalOfferPriceWithMarkupAndFinalMile ??
          offered?.offerPrice?.value ??
          q.totalOfferPrice?.value ??
          0
          :
          q.totalPrice ??
          offered?.offerPrice?.value ??
          q.totalOfferPrice?.value ??
          0
      );

      // Alliance carries its own id pair and spells transit days `TransitDays`,
      // and matches none of the branches below, so without these it falls to the
      // internal-LTL defaults: a blank shipmentOfferId drops the whole quote at
      // the guard further down, and carrier name/code save empty.
      const isAlliance = q?.quoteFrom === "alliance";

      const transitDays = asNum(
        q?.quoteFrom === "banyan"
          ? q?.transitTime
          : q?.quoteFrom === "worldwide"
            ? offered?.shopRQShipment?.timeInTransit?.transitDays
            : isAlliance
              ? q?.TransitDays
              : q?.transitDays,
        0
      );

      const carrierName = asStr(
        q?.quoteFrom === "banyan" || q?.quoteFrom === "armstrong" || isAlliance
          ? q?.carrierName
          : q?.quoteFrom === "worldwide"
            ? q.primaryVendor?.preferredName
            : q?.hub?.agent?.name
      );

      const carrierCode = asStr(
        q?.quoteFrom === "banyan" || q?.quoteFrom === "armstrong" || isAlliance
          ? q?.scac
          : q?.quoteFrom === "worldwide"
            ? q.primaryVendor?.scac
            : q?.hub?.agent?.carrierCode,
        ""
      );

      const shipmentOfferId = asStr(
        q?.quoteFrom === "banyan"
          ? q?.quoteId
          : q?.quoteFrom === "worldwide"
            ? q.offerId
            : q?.quoteFrom === "armstrong"
              ? q?.rateRequestLocator
              : isAlliance
                ? q?.shipmentOfferId
                : q?.services?.[0]?.quoteId
      ); // ← becomes shipmentOfferId in payload
      const productTransactionId = asStr(
        q?.quoteFrom === "banyan"
          ? q?.quoteId
          : q?.quoteFrom === "worldwide"
            ? q?.productTransactionId
            : q?.quoteFrom === "armstrong"
              ? q?.carrierQuoteId
              : isAlliance
                ? q?.shipmentProductTransactionId
                : q?.services?.[0]?.quoteId
      );
      // Top-level order/service from your quoteData (preferred) with safe fallbacks
      const orderTypeId = asStr(q.orderId || orderTypeRowData?.id || "");
      const serviceId = asStr(q.serviceId || fields?.selectedServiceId || "");

      if (!shipmentOfferId) return null;

      // Armstrong shares rateRequestLocator across carriers; deduplicate by carrierQuoteId instead
      const dedupeKey = q?.quoteFrom === "armstrong"
        ? asStr(q?.carrierQuoteId)
        : shipmentOfferId;
      if (!dedupeKey || seen.has(dedupeKey)) return null;
      seen.add(dedupeKey);

      return {
        orderTypeId,
        serviceId,
        shipmentOfferId,
        productTransactionId,
        pricing: {
          total,
          carrierName,
          carrierCode,
          estimatedLTLTransitDay: transitDays,
          finalmileCarrierName: q?.finalMileData?.hub?.agent?.name || "", // LTL only; intentionally blank
        },
      };
    }).filter(Boolean);
  };
  const handleSaveQuotationForLTL = async () => {
    try {

      setLoading(true);
      const customerId = asStr(customerRowData?.id) || "";
      const orderTypeId = asStr(orderTypeRowData?.id, "");
      const items = mapItemsDataToPayload(data);
      const destinationAddress = {
        address: asStr(fields.destinationAddress),
        aptSuit: asStr(fields.destinationAPTSuite),
        zipCode: asStr(fields.destinationZipcode),
        city: asStr(fields.destinationCity),
        state: asStr(fields.destinationState),
        country: asStr(fields.destinationCountry),
        businessLocation: asStr(fields.destinationBusinessLocation),
        contactName: asStr(fields.destinationConsigneeName),
        phoneNumber: onlyDigits(fields.destinationConsigneePhoneNumber),
        countryCallingCode: asStr(fields.destinationConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.destinationConsigneeContactEmail),
        extraMen: asStr(fields.destinationExtramen),
        waitTime: asStr(fields.destinationWaitTimeMinutes),
        stairsNumber: asStr(fields.destinationNoOfFlights),
        storage: asStr(fields.destinationStorage),
        handlingUnit: asNum(fields.destinationHandingUnits),
        weight: asNum(fields.destinationWeight),
        pieces: asNum(fields.destinationPieces),
        accessorials: dropAccessorialsSelected,
        locationType: dropLimitedAccessSelected,
      };

      const originAddress = {
        address: asStr(fields.OriginAddress),
        aptSuit: asStr(fields.OriginAPTSuite),
        zipCode: asStr(fields.OriginZipcode),
        city: asStr(fields.originCity),
        state: asStr(fields.OriginState),
        country: asStr(fields.OriginCountry),
        businessLocation: asStr(fields.OriginBusinessLocation),
        contactName: asStr(fields.OriginConsigneeName),
        phoneNumber: onlyDigits(fields.OriginConsigneePhoneNumber),
        countryCallingCode: asStr(fields.OriginConsigneePhoneExt, "+1"),
        contactEmail: asStr(fields.OriginConsigneeContactEmail),
        accessorials: pickupAccessorialsSelected,
        locationType: pickupLimitedAccessSelected,
      };
      const quoteArray = buildQuoteArray();
      const pickUpDock = {
        dockName: asStr(fields.PickUpDockName),
        pickupNumber: asStr(fields.PickUpDOckNumber),
        pickUpDate: getISODate(fields.PickUpDockDate || "", fields.PickUpDockTime || ""),
        pickUpTime: asStr(fields.PickUpDockTime),
        note: asStr(fields.PickUpDOckNote),
        dockOpenTime: fields.PickUpDockOpenTime ? convertTo24HourFormat(fields.PickUpDockOpenTime) : "",
        dockCloseTime: fields.PickUpDOckCloseTime ? convertTo24HourFormat(fields.PickUpDOckCloseTime) : "",
        firstComeFirstServe: false,
      };

      const dropDock = {
        dockName: asStr(fields.DropDockName),
        pickupNumber: asStr(fields.DropDOckNumber),
        note: asStr(fields.DropDOckNote),
        dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
        dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
        firstComeFirstServe: false,
      };
      const payload = {
        groupId: asStr(fields.selectedQuoteId),
        customerId,
        totalWeight:
          asNum(fields.destinationWeight) ||
          asNum(fields.destinationWeight) ||
          items.reduce((sum: number, it: any) => sum + asNum(it.weight), 0),
        distanceToDestination: asStr(fields.distanceToDestination),
        orderTypeId,
        reference: asStr(fields.orderReference),
        reference2: asStr(moreReferences?.[0]?.referenceValue),
        reference3: asStr(moreReferences?.[1]?.referenceValue),
        reference4: asStr(moreReferences?.[2]?.referenceValue),
        reference5: asStr(moreReferences?.[3]?.referenceValue),
        items,
        destinationAddress,
        originAddress,
        quoteArray,
        pickUpDock,
        dropDock,
        additionalServices: Array.isArray(fields.additionalServices)
          ? fields.additionalServices
          : [],
      };

      const apiResponse = await postApi(SAVE_QUOTATION_FOR_LTL, payload);
      if (apiResponse?.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        // Clear Redux state on successful save
        dispatch(clearTraditionalQuoteData());
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse?.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (err) {
      setMessage(GENERAL_ERROR_MESSAGE, "error");
    } finally {
      setLoading(false);
    }
  };

  const getLTLQuoteDetails = () => {
    let newArr: any = [];
    TLList.map((item: any) => {
      newArr.push({
        shipmentOfferId: item?.offerId,
        productTransactionId: item?.productTransactionId,
        pricing: {
          total: orderTypeRowData?.name === "LTL"
            ? item?.priceWithMarkUp || item?.price || "0.0"
            : item?.priceWithFinalMile !== "0" ? String(item?.priceWithFinalMile) : item?.price ?? "0.0",
          carrierName: item?.carrierName,
          carrierCode: item?.scac,
          estimatedLTLTransitDay: item?.transitTime,
          finalmileCarrierName: item?.finalmileCarrierName,
        },
      });
    });
    return newArr;
  };

  const handleSaveLTLQuotation = async (quoteId: string) => {
    try {
      setLoading(true);
      const apiPayload = {
        groupId: groupQuoteId,
        serviceId: serviceSelected?.id,
        customerId: assCustomerRowData?.id
          ? assCustomerRowData?.id
          : customerRowData?.id,
        totalWeight: fields?.destinationWeight,
        distanceToDestination: fields.distance,
        orderTypeId: orderTypeRowData?.id,
        reference: fields.orderReference,
        reference2: moreReferences?.[0]?.["referenceValue"] ?? "",
        reference3: moreReferences?.[1]?.["referenceValue"] ?? "",
        reference4: moreReferences?.[2]?.["referenceValue"] ?? "",
        reference5: moreReferences?.[3]?.["referenceValue"] ?? "",
        items: mapItemsDataToPayload(data),
        destinationAddress: {
          address: fields.destinationAddress,
          aptSuit: fields.destinationAPTSuite,
          zipCode: fields.destinationZipcode,
          city: fields.destinationCity,
          state: fields.destinationState,
          country: fields.destinationCountry,
          businessLocation: fields.destinationBusinessLocation,
          contactName: fields.destinationConsigneeName,
          phoneNumber: fields.destinationConsigneePhoneNumber.replace(
            /\D/g,
            ""
          ),
          countryCallingCode: fields.destinationConsigneePhoneExt,
          contactEmail: fields.destinationConsigneeContactEmail,
          extraMen: fields.destinationExtramen,
          waitTime: fields.destinationWaitTimeMinutes,
          stairsNumber: fields.destinationNoOfFlights,
          storage: fields.destinationStorage,
          handlingUnit: fields.destinationHandingUnits,
          weight: fields.destinationWeight,
          pieces: fields.destinationPieces,
          accessorials: mapAccessorials(dropAccessorialsSelected) || [],
          locationType: dropLimitedAccessSelected?.name || "",
        },
        originAddress: {
          address: fields.OriginAddress,
          aptSuit: fields.OriginAPTSuite,
          zipCode: fields.OriginZipcode,
          city: fields.OriginCity,
          state: fields.OriginState,
          country: fields.OriginCountry,
          businessLocation: fields.OriginBusinessLocation,
          contactName: fields.OriginConsigneeName,
          phoneNumber: fields.OriginConsigneePhoneNumber,
          countryCallingCode: fields.OriginConsigneePhoneExt,
          contactEmail: fields.OriginConsigneeContactEmail,
          accessorials: mapAccessorials(pickupAccessorialsSelected),
          locationType: pickupLimitedAccessSelected?.name || "",
        },
        quoteArray: getLTLQuoteDetails(),
        pickUpDock: {
          dockName: fields.PickUpDockName || "",
          pickupNumber: fields.PickUpDOckNumber || "",
          pickUpDate: getISODate(
            fields.PickUpDockDate || "",
            fields.PickUpDockTime || ""
          ),
          pickUpTime: fields.PickUpDockTime || "",
          note: fields.PickUpDOckNote || "",
          dockOpenTime: fields.PickUpDockOpenTime
            ? convertTo24HourFormat(fields.PickUpDockOpenTime)
            : "",
          dockCloseTime: fields.PickUpDOckCloseTime
            ? convertTo24HourFormat(fields.PickUpDOckCloseTime)
            : "",
          firstComeFirstServe: fields.PickUpFCFS,
        },
        dropDock: {
          dockName: fields.DropDockName || "",
          pickupNumber: fields.DropDOckNumber || "",
          note: fields.DropDOckNote || "",
          dropOpenTime: dropTimeForPayload(fields.DropDockOpenTime),
          dropCloseTime: dropTimeForPayload(fields.DropDOckCloseTime),
          firstComeFirstServe: fields.DropFCFS,
        },
        additionalServices: fields.additionalServices,
      };
      const apiResponse = await postApi(SAVE_LTL_QUOTATION, apiPayload);
      if (apiResponse.success) {
        setMessage(ORDER_MANAGEMENT_MESSAGES.QUOTE_SAVE_SUCCESS, "success");
        // Clear Redux state on successful save
        dispatch(clearTraditionalQuoteData());
        navigate("/saved-quotes");
      } else {
        setMessage(
          apiResponse.error?.message ?? GENERAL_ERROR_MESSAGE,
          "error"
        );
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    if (saveQuotationPage) {
      setTimeout(() => {
        setSaveQuotationPage(false);
      }, 3000);
    }
  }, [saveQuotationPage]);

  const DROPDOWN_TO_DEST_FIELD: Record<string, string> = {
    address: "destinationAddress",
    aptSuit: "destinationAPTSuite",
    zipCode: "destinationZipcode",
    city: "destinationCity",
    state: "destinationState",
    country: "destinationCountry",
    businessLocation: "destinationBusinessLocation",
    contactPerson: "destinationConsigneeName",
    phoneNumber: "destinationConsigneePhoneNumber",
    countryCallingCode: "destinationConsigneePhoneExt",
    contactEmail: "destinationConsigneeContactEmail",
  };

  const DROPDOWN_TO_ORIGIN_FIELD: Record<string, string> = {
    address: "OriginAddress",
    aptSuit: "OriginAPTSuite",
    zipCode: "OriginZipcode",
    city: "OriginCity",
    state: "OriginState",
    country: "OriginCountry",
    businessLocation: "OriginBusinessLocation",
    contactPerson: "OriginConsigneeName",
    phoneNumber: "OriginConsigneePhoneNumber",
    countryCallingCode: "OriginConsigneePhoneExt",
    contactEmail: "OriginConsigneeContactEmail",
  };

  // Cache last seen calling code per scope so formatting works even if the dropdown
  // sends phone first and code later (or vice versa)
  const phoneCCRef = useRef<{ dest: string; origin: string }>({
    dest: fields?.destinationConsigneePhoneExt || "+1",
    origin: fields?.OriginConsigneePhoneExt || "+1",
  });


  useEffect(() => {

    if (
      (state?.page === "Quick Quote" || state?.page === "Traditional Quote") &&
      state?.isSelected
    ) {


      // 
      const data = state?.data
      setFields((prevData: any) => ({
        ...prevData,
        destinationAddress: data?.destinationAddress?.address ?? "",
        destinationAPTSuite: data?.destinationAddress?.aptSuit ?? "",
        destinationZipcode: data?.destinationAddress?.zipCode ?? "",
        destinationCity: data?.destinationAddress?.city ?? "",
        destinationState: data?.destinationAddress?.state ?? "",
        destinationCountry: data?.destinationAddress?.country ?? "",
        destinationBusinessLocation:
          data?.destinationAddress?.businessLocation ?? "",
        destinationConsigneeName: data?.destinationAddress?.contactName ?? "",
        destinationConsigneePhoneExt:
          data?.destinationAddress?.countryCallingCode ?? "+1",
        destinationConsigneePhoneNumber:
          data?.destinationAddress?.phoneNumber ?? "",
        destinationConsigneeContactEmail:
          data?.destinationAddress?.contactEmail ?? "",
        destinationExtramen: data?.destinationAddress?.extraMen ?? "",
        destinationNoOfFlights: data?.destinationAddress?.stairsNumber ?? "",
        destinationWaitTimeMinutes: data?.destinationAddress?.waitTime ?? "",
        destinationHandingUnits: data?.destinationAddress?.handlingUnit ?? "",
        destinationWeight: data?.destinationAddress?.weight ?? "",
        destinationSpecialInstructions:
          data?.destinationAddress?.specialInstructions ?? "",
        orderReference: data?.reference ?? "",
        orderReference2: data?.reference2 ?? "",
        orderReference3: data?.reference3 ?? "",
        orderReference4: data?.reference4 ?? "",
        orderReference5: data?.reference5 ?? "",
        additionalServices: data?.additionalServices ?? [],
        selectedServiceId: data?.service?.id ?? "",
        OriginAddress: data?.originAddress?.address ?? "",
        OriginAPTSuite: data?.originAddress?.aptSuit ?? "",
        OriginZipcode: data?.originAddress?.zipCode ?? "",
        OriginCity: data?.originAddress?.city ?? "",
        OriginState: data?.originAddress?.state ?? "",
        OriginCountry: data?.originAddress?.country ?? "",
        OriginBusinessLocation: data?.originAddress?.businessLocation ?? "",
        OriginConsigneeName: data?.originAddress?.contactName ?? "",
        OriginConsigneePhoneExt: data?.originAddress?.countryCallingCode ?? "+1",
        OriginConsigneePhoneNumber: data?.originAddress?.phoneNumber ?? "",
        OriginConsigneeContactEmail: data?.originAddress?.contactEmail ?? "",
        PickupTargetDateFrom: data?.originAddress?.pickUpTargetDateTimeFrom
          ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeFrom)
          : "",
        PickupTargetTimeFrom: data?.originAddress?.pickUpTargetDateTimeFrom
          ? extractTime(data?.originAddress?.pickUpTargetDateTimeFrom)
          : "",
        PickupTargetDateTo: data?.originAddress?.pickUpTargetDateTimeTo
          ? normalizeDate(data?.originAddress?.pickUpTargetDateTimeTo)
          : "",
        PickupTargetTimeTo: data?.originAddress?.pickUpTargetDateTimeTo
          ? extractTime(data?.originAddress?.pickUpTargetDateTimeTo)
          : "",
        PickupDeliveryDateFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
          ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
          : "",
        PickupDeliveryTimeFrom: data?.originAddress?.pickUpDeliveryTargetDateTimeFrom
          ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeFrom)
          : "",
        PickupDeliveryDateTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
          ? normalizeDate(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
          : "",
        PickupDeliveryTimeTo: data?.originAddress?.pickUpDeliveryTargetDateTimeTo
          ? extractTime(data?.originAddress?.pickUpDeliveryTargetDateTimeTo)
          : "",
        PickUpDockDate: data?.pickUpDock?.pickUpDate
          ? normalizeDate(data?.pickUpDock?.pickUpDate)
          : moment().format("YYYY-MM-DD"),
        PickUpDockTime: data?.pickUpDock?.pickUpDate
          ? extractTime(data?.pickUpDock?.pickUpDate)
          : "08:00 AM",
        PickUpDockName: data?.pickUpDock?.dockName,
        PickUpDOckNumber: data?.pickUpDock?.pickupNumber,
        PickUpDockOpenTime: formatTimeForDisplay(data?.pickUpDock?.dockOpenTime) || DEFAULT_DOCK_OPEN_TIME,
        PickUpDOckCloseTime: formatTimeForDisplay(data?.pickUpDock?.dockCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
        PickUpDOckNote: data?.pickUpDock?.note ?? "",
        PickUpFCFS: data?.pickUpDock?.firstComeFirstServe,
        LTLDropAccessorials: [],
        DropDockName: "",
        DropDOckNumber: "",
        DropDockOpenTime:
          data?.orderType?.name === "LTL + Final Mile"
            ? ""
            : formatTimeForDisplay(data?.dropDock?.dropOpenTime) || DEFAULT_DOCK_OPEN_TIME,
        DropDOckCloseTime:
          data?.orderType?.name === "LTL + Final Mile"
            ? ""
            : formatTimeForDisplay(data?.dropDock?.dropCloseTime) || DEFAULT_DOCK_CLOSE_TIME,
        DropDOckNote: "",
        DropFCFS: false,
        MidStopAddress: data?.MidStopAddress || "",
        MidStopAPTSuit: data?.MidStopAPTSuit || "",
        MidStopZipcode: data?.MidStopZipcode || "",
        MidStopCity: data?.MidStopCity || "",
        MidStopState: data?.MidStopState || "",
        MidStopCountry: data?.MidStopCountry || "",
        MidStopContactName: data?.MidStopContactName || "",
        MidStopPhoneNumber: data?.MidStopPhoneNumber || "",
        MidStopPhoneNumberExt: data?.MidStopPhoneNumberExt || "+1",
        MidStopContactEmail: data?.MidStopContactEmail || [],
        agentHubId: data?.agentHubId,
        // selectedServiceId: data?.selectedServiceId,
        selectedQuoteId: data?.selectedQuoteId,
        internalLTL: data?.internalLTL,
        allQuoteId: data?.allQuoteId,
        CarrierName: data?.CarrierName,
        serviceCost: data?.serviceCost,
        scac: data?.scac,
        distance: data?.distance,
        offerId: data?.offerId,
        // costDetails: additionalCharges(row),
        markup: data?.markup,
        productTransactionId: data?.productTransactionId,
        customerMarkupType: data?.customerMarkupType,
        plProvider: "",
        courierPro1: "",
        courierPro2: "",
        //Final mile box details
        agentName: data?.agentName,
        hubName: data?.hubName,
        city: data?.city,
        zipCode: data?.zipCode,
        country: data?.country,
        AgentHubId: data?.AgentHubId,
        distanceToDestination: data?.distanceToDestination || 0,
        operationHrsFrom: data?.operationHrsFrom || "00:00",
        operationHrsTo: data?.operationHrsTo || "23:59",
        quotePrice: data?.quotePrice || 0,
        finalmile_quote_id: data?.finalmile_quote_id,
      }));
      setErrors((prev) => ({
        ...prev,
        destinationConsigneePhoneNumber: "",
        OriginConsigneePhoneNumber: "",
      }));
      setShowLTLList(true);
      setShowLTLData(false);
      setQuickQuoteLtlData(state?.selectedData);
      setQuickQuoteLtlFMData(state?.quoteData?.finalMileData);
      setLtlFMData(state?.quoteData);
      setPickupAccessorialsSelected(state?.data?.originAddress?.accessorials);
      setDropAccessorialsSelected(
        state?.ltlAccessorialData?.dropAccessorialsSelected
      );
      // This effect runs after the earlier state-mount restore above (later in file =
      // later in effect order), so it's the one that actually wins. Unconditionally
      // setting it straight from ltlAccessorialData used to blank out a value that
      // earlier effect had already restored correctly whenever ltlAccessorialData
      // didn't carry it — fall back to originAddress.locationType (the same proven
      // channel Pickup Accessorials uses above) instead of overwriting with undefined.
      setPickupLimitedAccesssSelected(
        state?.ltlAccessorialData?.pickupLimitedAccessSelected ||
        (state?.data?.originAddress?.locationType
          ? {
            id: state.data.originAddress.locationType,
            name: state.data.originAddress.locationType,
          }
          : undefined)
      );
      setDropLimitedAccesssSelected(
        state?.ltlAccessorialData?.dropLimitedAccessSelected
      );
      if (state?.selectedData?.orderTypeName === "LTL + Final Mile") {
        setOrderTypeRowData({
          id: ltlFMOrderType?.id,
          name: "LTL + Final Mile",
          description: "Less than Truck Load + Final Mile",
        });
        setServiceSelected(state?.quoteData?.finalMileData?.service);
        setLtlFMQuoteSelected(false);
      }
    }
    //NOTES::Specially for banyanType order from quick quote to customer order component(LTL+FINALMILE||NON TRADITIONAL)
    if ((state?.page === "Quick Quote" || state?.page === "Traditional Quote") && (state?.selectedData?.banyanType || state?.selectedData?.orderFor === "providence")) {
      if (state?.selectedData?.orderTypeName === "LTL + Final Mile") {
        setOrderTypeRowData({
          id: ltlFMOrderType?.id,
          name: "LTL + Final Mile",
          description: "Less than Truck Load + Final Mile",
        });
        setServiceSelected(state?.quoteData?.finalMileData?.service);
        setLtlFMQuoteSelected(false);
      }
    }
  }, [state]);



  return (
    <>
      <div ref={containerRef}>
        {loading && <Loader />}
        {/* <div className="details" id="top"> */}
        <div>
          <div className="form form--innersec">
            <section className="form__section">
              {!isUserOfOmniMove && customerLevel === "first" ? (
                <>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={customerNameDropdown}
                        setData={setCustomerNameDropdown}
                        captionTitles="Select Customer"
                        rowData={customerRowData}
                        setRowData={(row: any) => {
                          if (row?.id !== customerRowData?.id) {
                            resetCustomerLinkedAddresses();
                          }
                          setCustomerRowData(row);
                          clearLoadedOrderReference2();
                          clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.customer}
                        error={errors[DROPDOWN_FIELD_KEYS.customer]}
                        displayColumn="name"
                        apiUrl={"/customer"}
                        labelTitle="Customer Name"
                        mandatory={true}
                        enableRemove={true}
                        type={"order"}
                        status={3}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                        extraFields={["isUsedOrderReference2","usedOrderReference2"]}
                      />
                    </div>
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={assCustomerNameDropdown}
                        setData={setAssCustomerNameDropdown}
                        captionTitles="Select Sub Customer"
                        rowData={
                          customerRowData?.customerLevel !== "second"
                            ? ""
                            : assCustomerRowData
                        }
                        setRowData={(row: any) => {
                          // Only a real selection counts as the user taking over:
                          // MutliDropdown also clears this row programmatically when
                          // parentCustomerId changes, which happens during a quote load.
                          if (row) {
                            if (row?.id !== assCustomerRowData?.id) {
                              resetCustomerLinkedAddresses();
                            }
                            clearLoadedOrderReference2();
                          }
                          setAssCustomerRowData(row);
                        }}
                        displayColumn="name"
                        enableRemove={true}
                        apiUrl={parentCustomerId ? "/customer" : ""}
                        labelTitle="Sub Customer Name"
                        mandatory={associatedCusReqired}
                        parentCustomerId={parentCustomerId}
                        disable={customerRowData?.customerLevel !== "second"}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                         extraFields={["isUsedOrderReference2","usedOrderReference2"]}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={customerOrderColumns}
                        data={customerNameDropdown}
                        setData={setCustomerNameDropdown}
                        captionTitles="Select Customer"
                        rowData={customerRowData}
                        setRowData={(row: any) => {
                          if (row?.id !== customerRowData?.id) {
                            resetCustomerLinkedAddresses();
                          }
                          setCustomerRowData(row);
                          clearLoadedOrderReference2();
                          clearMandatory(DROPDOWN_FIELD_KEYS.customer);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.customer}
                        error={errors[DROPDOWN_FIELD_KEYS.customer]}
                        displayColumn="name"
                        enableRemove={true}
                        apiUrl={"/customer"}
                        labelTitle="Customer Name"
                        mandatory={true}
                        type={"order"}
                        extendedUrl={{
                          requestFrom: "new",
                        }}
                         extraFields={["isUsedOrderReference2","usedOrderReference2"]}
                      />
                    </div>
                  </div>
                </>
              )}
              <div className="formRow formRow__2">
                <div className="formCol">
                  <MutliDropdown
                    mandatory={true}
                    columns={orderTypeColumns}
                    data={orderTypeDropdown}
                    enableRemove={true}
                    setData={setOrderTypeDropdown}
                    captionTitles="Select Order Type"
                    rowData={orderTypeRowData}
                    setRowData={(row: any) => {
                      setOrderTypeRowData(row);
                      clearMandatory(DROPDOWN_FIELD_KEYS.orderType);
                    }}
                    fieldKey={DROPDOWN_FIELD_KEYS.orderType}
                    error={errors[DROPDOWN_FIELD_KEYS.orderType]}
                    displayColumn="name"
                    apiUrl={"/order/order-type"}
                    extendedUrl={{ from: "order" }}
                    labelTitle="Order Type"
                  />
                </div>
                {isFinalMileOrderType && (
                  <div className="formCol">
                    <SingleDropdown
                      fieldName="serviceType"
                      selectedItem={
                        FINAL_MILE_SERVICE_TYPE_OPTIONS.find(
                          (option) => option.id === serviceType,
                        ) ?? null
                      }
                      setSelectedData={handleServiceTypeChange}
                      staticData={FINAL_MILE_SERVICE_TYPE_OPTIONS}
                      labelTitle="Service Type"
                      placeholder="-Select-"
                      mandatory={true}
                    />
                  </div>
                )}
                {/* Final Mile shows Service Type here and moves Service Level to the next row. */}
                {!isFinalMileOrderType && (
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="serviceLevel" className="form__label">
                        Service Level <span className="mandatory-mark">*</span>
                      </label>
                      <MutliDropdown
                        columns={serviceColumns}
                        data={serviceList}
                        setData={setServiceList}
                        captionTitles="Service Level"
                        rowData={serviceSelected}
                        setRowData={(row: any) => {
                          setServiceSelected(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.service);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.service}
                        error={errors[DROPDOWN_FIELD_KEYS.service]}
                        extendedUrl={{
                          orderTypeId: orderTypeRowData?.id || "",
                        }}
                        displayColumn="name"
                        apiUrl={`/agent/service/`}
                        type={"dropdown"}
                        mandatory={true}
                        disable={!orderTypeRowData?.id}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className="formRow formRow__2 mb_0">
                {isFinalMileOrderType && (
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="serviceLevel" className="form__label">
                        Service Level <span className="mandatory-mark">*</span>
                      </label>
                      {/* Final Mile quotes several services at once. */}
                      <MultiSelectChips
                        dropdownData={serviceList}
                        setDropdownData={setServiceList}
                        selectedData={serviceSelectedList}
                        setSelectedData={handleServiceSelectionChange}
                        keyBindName="name"
                        searchPlaceholder="Service Level"
                        extendedUrl={{
                          orderTypeId: orderTypeRowData?.id || "",
                          logisticsType: serviceType,
                        }}
                        apiUrl={`/agent/service/`}
                        fieldKey={DROPDOWN_FIELD_KEYS.service}
                        error={errors[DROPDOWN_FIELD_KEYS.service]}
                        // The field already has its own "Service Level *" label above;
                        // blanking this suppresses the component's built-in one.
                        labelTitle=""
                        disable={!orderTypeRowData?.id}
                      />
                    </div>
                  </div>
                )}
                <div className="formCol">
                  <div className="form__Field">
                    <label htmlFor="orderReference" className="form__label">
                      Order Reference 1{" "}
                      <span className="mandatory-mark">*</span>
                    </label>
                    <input
                      type="text"
                      id="orderReference"
                      name="orderReference"
                      data-field="orderReference"
                      className={`form__input form__input--sm${fieldErrorClass("orderReference")}`}
                      placeholder="Order Reference"
                      autoComplete="off"
                      value={fields.orderReference}
                      onChange={handleFieldChange}
                    />
                    {errors.orderReference && (
                      <span className="form__error">{errors.orderReference}</span>
                    )}
                  </div>
                </div>
                {moreReferences.map((item, index) => {
                  // Reference 2 is driven by the customer's configuration when locked.
                  const isLocked =
                    isReference2Locked && index === ORDER_REFERENCE_2_INDEX;
                  return moreReferences.length < referenceLimit &&
                    index + 1 === moreReferences.length &&
                    moreReferences.length + 1 !== referenceLimit ? (
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="orderReference" className="form__label">
                          Order Reference {`${item?.referenceCount}`}
                        </label>
                        <div className={`form__groupField${isLocked ? " form__groupField--disabled" : ""}`}>
                          <input
                            type="text"
                            id="orderReference"
                            name={`orderReference${item?.referenceCount}`}
                            className="form__input form__input--sm"
                            placeholder="Order Reference"
                            autoComplete="off"
                            value={item.referenceValue}
                            disabled={isLocked}
                            onChange={(e) =>
                              handleRefFieldChange(e?.target?.value, index)
                            }
                          />
                          {moreReferences?.length > 1 && !isLocked && (
                            <button onClick={() => removeReferences(index)}>
                              <DeleteIcon />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="btn__group btn__group--end mt_8 mb_0">
                        <button
                          className={
                            moreReferences[moreReferences?.length - 1]
                              .referenceValue === ""
                              ? "addMore addMore__disable"
                              : "addMore"
                          }
                          disabled={
                            moreReferences[moreReferences?.length - 1]
                              .referenceValue === ""
                          }
                          onClick={() => addMoreReferences()}
                        >
                          +Add More References
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="orderReference" className="form__label">
                          Order Reference {`${item?.referenceCount}`}
                          {/* {item?.referenceCount === 2 ? (
                          <span className="mandatory-mark"> *</span>
                        ) : (
                          ""
                        )} */}
                        </label>
                        <div className={`form__groupField${isLocked ? " form__groupField--disabled" : ""}`}>
                          <input
                            type="text"
                            id="orderReference"
                            name={`orderReference${item?.referenceCount}`}
                            className="form__input form__input--sm"
                            placeholder="Order Reference"
                            autoComplete="off"
                            value={item.referenceValue}
                            disabled={isLocked}
                            onChange={(e) =>
                              handleRefFieldChange(e?.target?.value, index)
                            }
                          />
                          {!isLocked && (
                            <button
                              className="btn"
                              onClick={() => removeReferences(index)}
                            >
                              <DeleteIcon />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            {!notLTL ? (
              <div className="form__sectionGrid">
                <section className="form__section">
                  <h1 className="form__section__title">Origin Address</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      {/* <div className="form__Field">
                      <label htmlFor="OriginAddress" className="form__label">
                        Address <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="OriginAddress"
                        name="OriginAddress"
                        data-field="OriginAddress"
                        className={`form__input form__input--sm${fieldErrorClass("OriginAddress")}`}
                        placeholder="Address"
                        autoComplete="off"
                        value={fields.OriginAddress}
                        onChange={handleFieldChange}
                      />
                      {errors.OriginAddress && (
                        <span className="form__error">
                          {errors.OriginAddress}
                        </span>
                      )}
                    </div> */}
                      <div className="form__Field">
                        <label htmlFor="serviceLevel" className="form__label">
                          Address <span className="mandatory-mark">*</span>
                        </label>
                        <AddressDropDown
                          fieldKey="OriginAddress"
                          error={errors.OriginAddress}
                          columns={addressColumns}
                          data={originAddressList}
                          setData={setOriginAddressList}
                          captionTitles="Address"
                          rowData={addressSelectedOrigin}
                          setRowData={(full) => {
                            // full is the complete object from your backend OR a manual entry like:
                            // { address: "typed text", isManual: true }

                            setAddressSelectedOrigin(full);

                            // Always update these
                            const updatedFields: any = {
                              OriginAddress: full?.address || "",
                              OriginAddressId: full?.id || "",
                            };

                            // Only hydrate the remaining fields if this is a REAL selection (not free-text)
                            if (!full?.isManual) {
                              updatedFields.OriginAPTSuite =
                                full?.aptSuit || "";
                              updatedFields.OriginZipcode = full?.zipCode || "";
                              updatedFields.OriginCity = full?.city || "";
                              updatedFields.OriginState = full?.state || "";
                              updatedFields.OriginCountry = full?.country || "";
                              updatedFields.OriginBusinessLocation =
                                full?.businessLocation || "";
                              updatedFields.OriginConsigneeName =
                                full?.contactPerson || "";
                              updatedFields.OriginConsigneePhoneNumber =
                                full?.phoneNumber || "";
                              updatedFields.OriginConsigneePhoneExt =
                                full?.countryCallingCode || "+1";
                              updatedFields.OriginConsigneeContactEmail =
                                Array.isArray(full?.contactEmail)
                                  ? full?.contactEmail[0] || ""
                                  : full?.contactEmail || "";
                              // Pickup dock window saved against this address
                              updatedFields.PickUpDockOpenTime =
                                storedTimeToPicker(full?.pickupDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                              updatedFields.PickUpDOckCloseTime =
                                storedTimeToPicker(full?.pickupDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                            }
                            // If isManual === true, we intentionally DO NOT touch the other fields,
                            // so they keep their previous values instead of becoming "".

                            setFields((prev) => ({
                              ...prev,
                              ...updatedFields,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              OriginConsigneePhoneNumber: "",
                            }));
                          }}
                          displayColumn="address"
                          apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                          type="dropdown"
                          mandatory={true}
                          overwriteWithFreeText={true}
                          customerId={assCustomerRowData?.id || customerRowData?.id}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="OriginAPTSuite" className="form__label">
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="OriginAPTSuite"
                          name="OriginAPTSuite"
                          className="form__input form__input--sm"
                          autoComplete="off"
                          placeholder="APT/Suite"
                          value={fields.OriginAPTSuite}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2 formRowMFull">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="OriginZipcode" className="form__label">
                          Zip Code <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="OriginZipcode"
                          name="OriginZipcode"
                          data-field="OriginZipcode"
                          className={`form__input form__input--sm${fieldErrorClass("OriginZipcode")}`}
                          placeholder="Zip Code"
                          autoComplete="off"
                          value={fields.OriginZipcode}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginZipcode && (
                          <span className="form__error">
                            {errors.OriginZipcode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label htmlFor="OriginCity" className="form__label">
                              City
                            </label>
                            {originCities.length > 0 ? (
                              <SingleDropdown
                                fieldName="OriginCity"
                                selectedItem={fields.OriginCity ? { id: fields.OriginCity, name: fields.OriginCity } : null}
                                setSelectedData={(_, item) => setFields((prev) => ({ ...prev, OriginCity: item?.name || "" }))}
                                staticData={originCities.map((c) => ({ id: c, name: c }))}
                                placeholder="City"
                              />
                            ) : (
                              <input
                                type="text"
                                id="OriginCity"
                                className="form__input form__input--sm form__input--disabled"
                                placeholder="City"
                                autoComplete="off"
                                value={fields.OriginCity}
                                onChange={handleFieldChange}
                                disabled={true}
                              />
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="OriginState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="OriginState"
                              name="OriginState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={fields.OriginState}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="OriginCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="OriginCountry"
                              name="OriginCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={fields.OriginCountry}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginBusinessLocation"
                          className="form__label"
                        >
                          Business Location Name
                          {!notLTL && (
                            <span className="mandatory-mark"> *</span>
                          )}
                        </label>
                        <input
                          type="text"
                          id="OriginBusinessLocation"
                          name="OriginBusinessLocation"
                          data-field="OriginBusinessLocation"
                          autoComplete="off"
                          className={`form__input form__input--sm${fieldErrorClass("OriginBusinessLocation")}`}
                          placeholder="Business Location Name"
                          value={fields.OriginBusinessLocation}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginBusinessLocation && (
                          <span className="form__error">{errors.OriginBusinessLocation}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginConsigneeName"
                          className="form__label"
                        >
                          Contact Name <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="OriginConsigneeName"
                          name="OriginConsigneeName"
                          data-field="OriginConsigneeName"
                          className={`form__input form__input--sm${fieldErrorClass("OriginConsigneeName")}`}
                          placeholder="Contact Name"
                          autoComplete="off"
                          value={fields.OriginConsigneeName}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginConsigneeName && (
                          <span className="form__error">{errors.OriginConsigneeName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="OriginConsigneePhoneNumber"
                          className="form__label"
                        >
                          Phone Number <span className="mandatory-mark">*</span>
                        </label>
                        <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("OriginConsigneePhoneNumber")}`}>
                          <select
                            className="form__select form__select--sm"
                            value={fields.OriginConsigneePhoneExt}
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                OriginConsigneePhoneExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={item?.countryCallingCode}
                              >
                                {item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            ref={originPhoneRef}
                            type="text"
                            id="OriginConsigneePhoneNumber"
                            name="OriginConsigneePhoneNumber"
                            data-field="OriginConsigneePhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Phone Number"
                            maxLength={
                              fields?.OriginConsigneePhoneExt === "+1" ? 16 : 10
                            }
                            value={onInputPhNumFormat(
                              fields.OriginConsigneePhoneNumber,
                              fields.OriginConsigneePhoneExt
                            )}
                            autoComplete="off"
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            onBlur={() =>
                              handlePhoneBlur(
                                fields.OriginConsigneePhoneNumber,
                                fields.OriginConsigneePhoneExt,
                                "OriginConsigneePhoneNumber"
                              )
                            }
                          />
                        </div>
                        {errors.OriginConsigneePhoneNumber && (
                          <span className="form__error">
                            {errors.OriginConsigneePhoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="email" className="form__label">
                          Contact Email{" "}
                          {/* {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )} */}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="OriginConsigneeContactEmail"
                          className={`form__input form__input--sm`}
                          placeholder="Contact Email"
                          autoComplete="off"
                          value={fields.OriginConsigneeContactEmail}
                          onChange={handleFieldChange}
                        />
                        {errors.OriginConsigneeContactEmail && (
                          <span className="form__error">
                            {errors.OriginConsigneeContactEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {orderTypeRowData?.name === "Expedited" ? (
                    <>
                      <div className="formCol">
                        <div className="formRow formRow__4 mb_0">
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Target Date From{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <input
                                type="date"
                                id="PickupTargetDateFrom"
                                name="PickupTargetDateFrom"
                                data-field="PickupTargetDateFrom"
                                className={`form__date form__date--sm${fieldErrorClass("PickupTargetDateFrom")}`}
                                placeholder="DD/MM/YYYY"
                                autoComplete="off"
                                onChange={handleFieldChange}
                                value={normalizeDate(
                                  fields.PickupTargetDateFrom
                                )}
                                min={normalizeDate(new Date())}
                              />
                              {errors.PickupTargetDateFrom && (
                                <span className="form__error">
                                  {errors.PickupTargetDateFrom}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Target Time From{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <CustomTimePicker
                                value={fields.PickupTargetTimeFrom}
                                onChange={handleTimeChange}
                                property="PickupTargetTimeFrom"
                                fieldKey="PickupTargetTimeFrom"
                                error={errors.PickupTargetTimeFrom}
                              />
                              {errors.PickupTargetTimeFrom && (
                                <span className="form__error">
                                  {errors.PickupTargetTimeFrom}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Target Date To{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <input
                                type="date"
                                id="PickupTargetDateTo"
                                name="PickupTargetDateTo"
                                data-field="PickupTargetDateTo"
                                className={`form__date form__date--sm${fieldErrorClass("PickupTargetDateTo")}`}
                                placeholder="DD/MM/YYYY"
                                autoComplete="off"
                                onChange={handleFieldChange}
                                value={normalizeDate(fields.PickupTargetDateTo)}
                                min={
                                  fields.PickupTargetDateFrom
                                    ? normalizeDate(fields.PickupTargetDateFrom)
                                    : normalizeDate(new Date())
                                }
                              />
                              {errors.PickupTargetDateTo && (
                                <span className="form__error">
                                  {errors.PickupTargetDateTo}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Target Time To{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <CustomTimePicker
                                value={fields.PickupTargetTimeTo}
                                onChange={handleTimeChange}
                                property="PickupTargetTimeTo"
                                fieldKey="PickupTargetTimeTo"
                                error={errors.PickupTargetTimeTo}
                              />
                              {errors.PickupTargetTimeTo && (
                                <span className="form__error">
                                  {errors.PickupTargetTimeTo}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="formRow formRow__4">
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Delivery Date From{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <input
                                type="date"
                                id="PickupDeliveryDateFrom"
                                name="PickupDeliveryDateFrom"
                                data-field="PickupDeliveryDateFrom"
                                className={`form__date form__date--sm${fieldErrorClass("PickupDeliveryDateFrom")}`}
                                placeholder="DD/MM/YYYY"
                                autoComplete="off"
                                onChange={handleFieldChange}
                                value={normalizeDate(
                                  fields.PickupDeliveryDateFrom
                                )}
                                min={
                                  fields.PickupTargetDateTo
                                    ? normalizeDate(fields.PickupTargetDateTo)
                                    : fields.PickupTargetDateFrom
                                      ? normalizeDate(fields.PickupTargetDateFrom)
                                      : normalizeDate(new Date())
                                }
                              />
                              {errors.PickupDeliveryDateFrom && (
                                <span className="form__error">
                                  {errors.PickupDeliveryDateFrom}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Delivery Time From{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <CustomTimePicker
                                value={fields.PickupDeliveryTimeFrom}
                                onChange={handleTimeChange}
                                property="PickupDeliveryTimeFrom"
                                fieldKey="PickupDeliveryTimeFrom"
                                error={errors.PickupDeliveryTimeFrom}
                              />
                              {errors.PickupDeliveryTimeFrom && (
                                <span className="form__error">
                                  {errors.PickupDeliveryTimeFrom}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Delivery Date To{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <input
                                type="date"
                                id="PickupDeliveryDateTo"
                                name="PickupDeliveryDateTo"
                                data-field="PickupDeliveryDateTo"
                                className={`form__date form__date--sm${fieldErrorClass("PickupDeliveryDateTo")}`}
                                placeholder="DD/MM/YYYY"
                                autoComplete="off"
                                onChange={handleFieldChange}
                                value={normalizeDate(
                                  fields.PickupDeliveryDateTo
                                )}
                                min={
                                  fields.PickupDeliveryDateFrom
                                    ? normalizeDate(
                                      fields.PickupDeliveryDateFrom
                                    )
                                    : fields.PickupTargetDateTo
                                      ? normalizeDate(fields.PickupTargetDateTo)
                                      : fields.PickupTargetDateFrom
                                        ? normalizeDate(fields.PickupTargetDateFrom)
                                        : normalizeDate(new Date())
                                }
                              />
                              {errors.PickupDeliveryDateTo && (
                                <span className="form__error">
                                  {errors.PickupDeliveryDateTo}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label className="form__label">
                                Pickup Delivery Time To{" "}
                                <span className="mandatory-mark">*</span>
                              </label>
                              <CustomTimePicker
                                value={fields.PickupDeliveryTimeTo}
                                onChange={handleTimeChange}
                                property="PickupDeliveryTimeTo"
                                fieldKey="PickupDeliveryTimeTo"
                                error={errors.PickupDeliveryTimeTo}
                              />
                              {errors.PickupDeliveryTimeTo && (
                                <span className="form__error">
                                  {errors.PickupDeliveryTimeTo}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    ""
                  )}
                  {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                    <div className="formRow formRow__2">
                      <div className="formCol">
                        <div className="form__Field">
                          <MultiSelectChips
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            dropdownData={pickupAccessorials}
                            selectedData={pickupAccessorialsSelected}
                            setDropdownData={setPickupAccessorialDropdownData}
                            setSelectedData={setPickupAccessorialsSelected}
                            searchPlaceholder="Search"
                            extendedUrl={{ isPickup: "true" }}
                            labelTitle="LTL Pickup Accessorials"
                            keyBindName="name"
                            mandatory={false}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <MutliDropdown
                            columns={accessorialsColumn}
                            data={pickupLimitedAccess}
                            setData={setPickupLimitedAccessDropdownData}
                            captionTitles="Select Limited Access Type"
                            rowData={pickupLimitedAccessSelected}
                            setRowData={setPickupLimitedAccesssSelected}
                            displayColumn="name"
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            labelTitle="Limited Access Type"
                            extendedUrl={{
                              isPickup: "true",
                              isLimitAccess: "true",
                            }}
                            enableRemove={true}
                            mandatory={false}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </section>
                <section className="form__section">
                  <h1 className="form__section__title">Destination Address</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      {/* <div className="form__Field">
                      <label
                        htmlFor="destinationAddress"
                        className="form__label"
                      >
                        Address<span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationAddress"
                        name="destinationAddress"
                        data-field="destinationAddress"
                        className={`form__input form__input--sm${fieldErrorClass("destinationAddress")}`}
                        placeholder="Address"
                        autoComplete="off"
                        value={fields.destinationAddress}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationAddress && (
                        <span className="form__error">
                          {errors.destinationAddress}
                        </span>
                      )}
                    </div> */}
                      <div className="form__Field">
                        <label htmlFor="serviceLevel" className="form__label">
                          Address <span className="mandatory-mark">*</span>
                        </label>
                        <AddressDropDown
                          fieldKey="destinationAddress"
                          error={errors.destinationAddress}
                          columns={addressColumns}
                          data={destAddressList}
                          setData={setDestAddressList}
                          captionTitles="Address"
                          rowData={addressSelected}
                          setRowData={(full) => {
                            // Save the selected/manual entry
                            setAddressSelected(full);

                            // Always update the address text and addressId
                            const updatedFields: any = {
                              destinationAddress: full?.address || "",
                              destinationAddressId: full?.id || "",
                            };

                            // Only hydrate the rest if it's a real selection (not manual free-text)
                            if (!full?.isManual) {
                              updatedFields.destinationAPTSuite =
                                full?.aptSuit || "";
                              updatedFields.destinationZipcode =
                                full?.zipCode || "";
                              updatedFields.destinationCity = full?.city || "";
                              updatedFields.destinationState =
                                full?.state || "";
                              updatedFields.destinationCountry =
                                full?.country || "";
                              updatedFields.destinationBusinessLocation =
                                full?.businessLocation || "";
                              updatedFields.destinationConsigneeName =
                                full?.contactPerson || "";
                              updatedFields.destinationConsigneePhoneNumber =
                                full?.phoneNumber || "";
                              updatedFields.destinationConsigneePhoneExt =
                                full?.countryCallingCode || "+1";
                              updatedFields.destinationConsigneeContactEmail =
                                Array.isArray(full?.contactEmail)
                                  ? full?.contactEmail[0] || ""
                                  : full?.contactEmail || "";
                              // Drop dock window saved against this address. LTL + Final Mile
                              // forces this window to 00:00 and disables it, so skip it there.
                              if (
                                orderTypeRowData?.name !== "LTL + Final Mile"
                              ) {
                                updatedFields.DropDockOpenTime =
                                  storedTimeToPicker(full?.dropDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                                updatedFields.DropDOckCloseTime =
                                  storedTimeToPicker(full?.dropDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                              }
                            }
                            // If isManual, we intentionally leave other fields untouched.

                            setFields((prev) => ({
                              ...prev,
                              ...updatedFields,
                            }));
                            setErrors((prev) => ({
                              ...prev,
                              destinationConsigneePhoneNumber: "",
                            }));
                          }}
                          displayColumn="address"
                          apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                          type="dropdown"
                          mandatory={true}
                          overwriteWithFreeText={true}
                          customerId={assCustomerRowData?.id || customerRowData?.id}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationAPTSuite"
                          className="form__label"
                        >
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="destinationAPTSuite"
                          name="destinationAPTSuite"
                          className="form__input form__input--sm"
                          placeholder="APT/Suite"
                          autoComplete="off"
                          value={fields.destinationAPTSuite}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2 formRowMFull">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationZipcode"
                          className="form__label"
                        >
                          Zip Code <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="destinationZipcode"
                          name="destinationZipcode"
                          data-field="destinationZipcode"
                          className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                          placeholder="Zip Code"
                          autoComplete="off"
                          value={fields.destinationZipcode}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationZipcode && (
                          <span className="form__error">
                            {errors.destinationZipcode}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationCity"
                              className="form__label"
                            >
                              City
                            </label>
                            {destinationCities.length > 0 ? (
                              <SingleDropdown
                                fieldName="destinationCity"
                                selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                                setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                                staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                                placeholder="City"
                              />
                            ) : (
                              <input
                                type="text"
                                id="destinationCity"
                                className="form__input form__input--sm form__input--disabled"
                                placeholder="City"
                                autoComplete="off"
                                value={fields.destinationCity}
                                onChange={handleFieldChange}
                                disabled={true}
                              />
                            )}
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="destinationState"
                              name="destinationState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={fields.destinationState}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="destinationCountry"
                              name="destinationCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={fields.destinationCountry}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationBusinessLocation"
                          className="form__label"
                        >
                          Business Location {!notLTL && "Name "}
                          {!notLTL && <span className="mandatory-mark">*</span>}
                        </label>
                        <input
                          type="text"
                          id="destinationBusinessLocation"
                          name="destinationBusinessLocation"
                          data-field="destinationBusinessLocation"
                          autoComplete="off"
                          className={`form__input form__input--sm${fieldErrorClass("destinationBusinessLocation")}`}
                          placeholder={
                            notLTL
                              ? "Business Location"
                              : "Business Location Name"
                          }
                          value={fields.destinationBusinessLocation}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationBusinessLocation && (
                          <span className="form__error">{errors.destinationBusinessLocation}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneeName"
                          className="form__label"
                        >
                          Consignee Name{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="text"
                          id="destinationConsigneeName"
                          name="destinationConsigneeName"
                          data-field="destinationConsigneeName"
                          className={`form__input form__input--sm${fieldErrorClass("destinationConsigneeName")}`}
                          placeholder="Consignee Name"
                          autoComplete="off"
                          value={fields.destinationConsigneeName}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationConsigneeName && (
                          <span className="form__error">{errors.destinationConsigneeName}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneePhoneNumber"
                          className="form__label"
                        >
                          Consignee Phone Number{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("destinationConsigneePhoneNumber")}`}>
                          <select
                            className="form__select form__select--sm"
                            value={fields.destinationConsigneePhoneExt}
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                destinationConsigneePhoneExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={item?.countryCallingCode}
                              >
                                {item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            ref={destinationPhoneRef}
                            type="text"
                            id="destinationConsigneePhoneNumber"
                            name="destinationConsigneePhoneNumber"
                            data-field="destinationConsigneePhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Consignee Phone Number"
                            maxLength={
                              fields?.destinationConsigneePhoneExt === "+1"
                                ? 16
                                : 10
                            }
                            value={onInputPhNumFormat(
                              fields.destinationConsigneePhoneNumber,
                              fields.destinationConsigneePhoneExt
                            )}
                            autoComplete="off"
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            onPasteCapture={handleFieldChange}
                            onBlur={() =>
                              handlePhoneBlur(
                                fields.destinationConsigneePhoneNumber,
                                fields.destinationConsigneePhoneExt,
                                "destinationConsigneePhoneNumber"
                              )
                            }
                          />
                        </div>
                        {errors.destinationConsigneePhoneNumber && (
                          <span className="form__error">
                            {errors.destinationConsigneePhoneNumber}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="email" className="form__label">
                          Consignee Contact Email{" "}
                          {/* {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )} */}
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="destinationConsigneeContactEmail"
                          className={`form__input form__input--sm`}
                          placeholder="Consignee Contact Email"
                          autoComplete="off"
                          value={fields.destinationConsigneeContactEmail}
                          onChange={handleFieldChange}
                        />
                        {errors.destinationConsigneeContactEmail && (
                          <span className="form__error">
                            {errors.destinationConsigneeContactEmail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* <div className="formRow formRow__2"> */}
                  {(notLTL ||
                    orderTypeRowData?.name === "Expedited" ||
                    orderTypeRowData?.name === "LTL + Final Mile") && (
                      <div className="formCol">
                        <div className="formRow formRow__3">
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationExtramen"
                                className="form__label"
                              >
                                Extra Men
                              </label>
                              <input
                                type="number"
                                id="destinationExtramen"
                                name="destinationExtramen"
                                className="form__input form__input--sm"
                                placeholder="Extra Men"
                                autoComplete="off"
                                value={
                                  fields.destinationExtramen !== ""
                                    ? Math.abs(Number(fields.destinationExtramen))
                                    : fields.destinationExtramen
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationNoOfFlights"
                                className="form__label"
                              >
                                No. of Flights
                              </label>
                              <input
                                type="number"
                                id="destinationNoOfFlights"
                                name="destinationNoOfFlights"
                                className="form__input form__input--sm"
                                placeholder="No. of Flights"
                                autoComplete="off"
                                value={
                                  fields.destinationNoOfFlights !== ""
                                    ? Math.abs(
                                      Number(fields.destinationNoOfFlights)
                                    )
                                    : fields.destinationNoOfFlights
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationWaitTimeMinutes"
                                className="form__label"
                              >
                                Wait Time
                              </label>
                              <input
                                type="number"
                                id="destinationWaitTimeMinutes"
                                name="destinationWaitTimeMinutes"
                                className="form__input form__input--sm"
                                placeholder="Wait Time"
                                autoComplete="off"
                                value={
                                  fields.destinationWaitTimeMinutes !== ""
                                    ? Math.abs(
                                      Number(fields.destinationWaitTimeMinutes)
                                    )
                                    : fields.destinationWaitTimeMinutes
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          {/* <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationStorage"
                            className="form__label"
                          >
                            Storage
                          </label>
                          <input
                            type="number"
                            id="destinationStorage"
                            name="destinationStorage"
                            className="form__input form__input--sm"
                            placeholder="Storage"
                            value={fields.destinationStorage}
                            onChange={handleFieldChange}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div> */}
                        </div>
                      </div>
                    )}
                  <div className="formCol">
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationHandingUnits"
                            className="form__label"
                          >
                            Handling Units
                          </label>
                          <input
                            type="number"
                            id="destinationHandingUnits"
                            name="destinationHandingUnits"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Handling Units"
                            autoComplete="off"
                            value={fields.destinationHandingUnits}
                            onChange={handleFieldChange}
                            disabled={true}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationWeight"
                            className="form__label"
                          >
                            Total Weight(LBS)
                          </label>
                          <input
                            type="number"
                            id="destinationWeight"
                            name="destinationWeight"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Total Weight"
                            autoComplete="off"
                            value={fields.destinationWeight}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationPieces"
                            className="form__label"
                          >
                            Total Items/Pieces
                          </label>
                          <input
                            type="number"
                            id="destinationPieces"
                            name="destinationPieces"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Pieces"
                            autoComplete="off"
                            value={fields.destinationPieces}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* </div> */}
                  {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                    <div className="formRow formRow__2 mb_0">
                      <div className="formCol">
                        <div className="form__Field">
                          <MultiSelectChips
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            dropdownData={dropAccessorials}
                            selectedData={dropAccessorialsSelected}
                            setDropdownData={setDropAccessorialDropdownData}
                            setSelectedData={setDropAccessorialsSelected}
                            searchPlaceholder="Search"
                            extendedUrl={{ isPickup: "false" }}
                            labelTitle="LTL Delivery Accessorials"
                            keyBindName="name"
                            mandatory={false}
                            disable={
                              orderTypeRowData?.name === "LTL + Final Mile"
                            }
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <MutliDropdown
                            columns={accessorialsColumn}
                            data={dropLimitedAccess}
                            setData={setDropLimitedAccessDropdownData}
                            captionTitles="Select Limited Access Type"
                            rowData={dropLimitedAccessSelected}
                            setRowData={setDropLimitedAccesssSelected}
                            displayColumn="name"
                            apiUrl={"/order/ltl-accessorial/wwex"}
                            extendedUrl={{
                              isPickup: "false",
                              isLimitAccess: "true",
                            }}
                            labelTitle="Limited Access Type"
                            enableRemove={true}
                            mandatory={false}
                            disable={
                              orderTypeRowData?.name === "LTL + Final Mile"
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(notLTL || orderTypeRowData?.name === "Expedited") && (
                    <>
                      <div className="formRow mb_0">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="destinationSpecialInstructions"
                              className="form__label"
                            >
                              Special Instructions
                            </label>
                            <textarea
                              id="destinationSpecialInstructions"
                              name="destinationSpecialInstructions"
                              className="form__textarea form__textarea--xs"
                              placeholder="Special Instructions"
                              autoComplete="off"
                              value={fields.destinationSpecialInstructions}
                              onChange={handleTextAreaChange}
                              maxLength={255}
                            ></textarea>
                          </div>
                        </div>
                      </div>
                      {/* <div className="formRow formRow--gapNone">
                      <h1 className="form__section__title form__section__title--flex">
                        Add Item Details
                        <div className="btn__group">
                          <button
                            className="btn btn__white"
                            onClick={handleAddItemDetails}
                          >
                            <AddIcon />
                            Add New Item
                          </button>
                        </div>
                      </h1>
                      <div className="tableWrap tableWrap--order p_0">
                        <ItemDetailsTable
                          invalidCells={invalidCells}
                          fieldKey={HANDLING_UNITS_FIELD_KEY}
                          focusCell={focusCell}
                          columns={
                            orderTypeRowData?.name === "Expedited"
                              ? tableConfig.columnsExpediet
                              : tableConfig.columns
                          }
                          data={data}
                          setData={setData}
                          setLoading={setLoading}
                          onDeleteConfig={tableConfig.delete}
                          showDelete={true}
                          isEditable={true}
                          setFields={setFields}
                          isLTL={true}
                        />
                      </div>
                    </div> */}
                    </>
                  )}
                </section>
              </div>
            ) : (
              <section className="form__section">
                <h1 className="form__section__title">
                  {isFinalMilePickup ? "Origin Address" : "Destination Address"}
                </h1>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    {/* <div className="form__Field">
                    <label htmlFor="destinationAddress" className="form__label">
                      Address 123 <span className="mandatory-mark">*</span>
                    </label>
                    <input
                      type="text"
                      id="destinationAddress"
                      name="destinationAddress"
                      data-field="destinationAddress"
                      className={`form__input form__input--sm${fieldErrorClass("destinationAddress")}`}
                      placeholder="Address"
                      autoComplete="off"
                      value={fields.destinationAddress}
                      onChange={handleFieldChange}
                    />
                    {errors.destinationAddress && (
                      <span className="form__error">
                        {errors.destinationAddress}
                      </span>
                    )}
                  </div> */}
                    <div className="form__Field">
                      <label htmlFor="serviceLevel" className="form__label">
                        Address <span className="mandatory-mark">*</span>
                      </label>
                      <AddressDropDown
                        fieldKey="destinationAddress"
                        error={errors.destinationAddress}
                        columns={addressColumns}
                        data={destAddressList}
                        setData={setDestAddressList}
                        captionTitles="Address"
                        rowData={addressSelected}
                        setRowData={(full) => {
                          // Store selection/manual entry
                          setAddressSelected(full);

                          // Always update the visible address and (optionally) the ID
                          const updatedFields: any = {
                            destinationAddress: full?.address || "",
                            destinationAddressId: full?.id || "",
                          };

                          // Only hydrate the rest if this is NOT a manual free-text entry
                          if (!full?.isManual) {
                            updatedFields.destinationAPTSuite =
                              full?.aptSuit || "";
                            updatedFields.destinationZipcode =
                              full?.zipCode || "";
                            updatedFields.destinationCity = full?.city || "";
                            updatedFields.destinationState = full?.state || "";
                            updatedFields.destinationCountry =
                              full?.country || "";
                            updatedFields.destinationBusinessLocation =
                              full?.businessLocation || "";
                            updatedFields.destinationConsigneeName =
                              full?.contactPerson || "";
                            updatedFields.destinationConsigneePhoneNumber =
                              full?.phoneNumber || "";
                            updatedFields.destinationConsigneePhoneExt =
                              full?.countryCallingCode || "+1";
                            updatedFields.destinationConsigneeContactEmail =
                              Array.isArray(full?.contactEmail)
                                ? full?.contactEmail[0] || ""
                                : full?.contactEmail || "";
                            // Drop dock window saved against this address. LTL + Final Mile
                            // forces this window to 00:00 and disables it, so skip it there.
                            if (orderTypeRowData?.name !== "LTL + Final Mile") {
                              updatedFields.DropDockOpenTime =
                                storedTimeToPicker(full?.dropDockOpenTime) || DEFAULT_DOCK_OPEN_TIME;
                              updatedFields.DropDOckCloseTime =
                                storedTimeToPicker(full?.dropDockCloseTime) || DEFAULT_DOCK_CLOSE_TIME;
                            }
                          }
                          // If isManual, leave other destination* fields untouched.

                          setFields((prev) => ({
                            ...prev,
                            ...updatedFields,
                          }));
                          setErrors((prev) => ({
                            ...prev,
                            destinationConsigneePhoneNumber: "",
                          }));
                        }}
                        displayColumn="address"
                        apiUrl={(assCustomerRowData?.id || customerRowData?.id) ? `/customer/saveddetails/addressbook` : ""}
                        type="dropdown"
                        mandatory={true}
                        overwriteWithFreeText={true}
                        customerId={assCustomerRowData?.id || customerRowData?.id}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationAPTSuite"
                        className="form__label"
                      >
                        APT / Suite
                      </label>
                      <input
                        type="text"
                        id="destinationAPTSuite"
                        name="destinationAPTSuite"
                        className="form__input form__input--sm"
                        placeholder="APT/Suite"
                        autoComplete="off"
                        value={fields.destinationAPTSuite}
                        onChange={handleFieldChange}
                      />
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationZipcode"
                        className="form__label"
                      >
                        Zip Code <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationZipcode"
                        name="destinationZipcode"
                        data-field="destinationZipcode"
                        className={`form__input form__input--sm${fieldErrorClass("destinationZipcode")}`}
                        placeholder="Zip Code"
                        autoComplete="off"
                        value={fields.destinationZipcode}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationZipcode && (
                        <span className="form__error">
                          {errors.destinationZipcode}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCity"
                            className="form__label"
                          >
                            City
                          </label>
                          {destinationCities.length > 0 ? (
                            <SingleDropdown
                              fieldName="destinationCity"
                              selectedItem={fields.destinationCity ? { id: fields.destinationCity, name: fields.destinationCity } : null}
                              setSelectedData={(_, item) => setFields((prev) => ({ ...prev, destinationCity: item?.name || "" }))}
                              staticData={destinationCities.map((c) => ({ id: c, name: c }))}
                              placeholder="City"
                            />
                          ) : (
                            <input
                              type="text"
                              id="destinationCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={fields.destinationCity}
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          )}
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationState"
                            className="form__label"
                          >
                            State
                          </label>
                          <input
                            type="text"
                            id="destinationState"
                            name="destinationState"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="State"
                            autoComplete="off"
                            value={fields.destinationState}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationCountry"
                            className="form__label"
                          >
                            Country
                          </label>
                          <input
                            type="text"
                            id="destinationCountry"
                            name="destinationCountry"
                            className="form__input form__input--sm form__input--disabled"
                            placeholder="Country"
                            autoComplete="off"
                            value={fields.destinationCountry}
                            onChange={handleFieldChange}
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationBusinessLocation"
                        className="form__label"
                      >
                        Business Location {!notLTL && "Name "}
                        {!notLTL && <span className="mandatory-mark">*</span>}
                      </label>
                      <input
                        type="text"
                        id="destinationBusinessLocation"
                        name="destinationBusinessLocation"
                        data-field="destinationBusinessLocation"
                        className={`form__input form__input--sm${fieldErrorClass("destinationBusinessLocation")}`}
                        autoComplete="off"
                        placeholder={
                          notLTL
                            ? "Business Location"
                            : "Business Location Name"
                        }
                        value={fields.destinationBusinessLocation}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationBusinessLocation && (
                        <span className="form__error">{errors.destinationBusinessLocation}</span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneeName"
                        className="form__label"
                      >
                        Consignee Name <span className="mandatory-mark">*</span>
                      </label>
                      <input
                        type="text"
                        id="destinationConsigneeName"
                        name="destinationConsigneeName"
                        data-field="destinationConsigneeName"
                        className={`form__input form__input--sm${fieldErrorClass("destinationConsigneeName")}`}
                        placeholder="Consignee Name"
                        autoComplete="off"
                        value={fields.destinationConsigneeName}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationConsigneeName && (
                        <span className="form__error">{errors.destinationConsigneeName}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="formRow formRow__2">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Consignee Phone Number{" "}
                        <span className="mandatory-mark">*</span>
                      </label>
                      <div className={`form__groupField form__groupField--phonenumber${groupFieldErrorClass("destinationConsigneePhoneNumber")}`}>
                        <select
                          className="form__select form__select--sm"
                          value={fields.destinationConsigneePhoneExt}
                          onChange={(e) => {
                            setFields(() => ({
                              ...fields,
                              destinationConsigneePhoneExt: e.target.value,
                            }));
                          }}
                        >
                          {countryCode?.map((item: any) => (
                            <option
                              key={item?.countryCallingCode}
                              value={item?.countryCallingCode}
                            >
                              {item?.countryCallingCode}
                            </option>
                          ))}
                        </select>
                        <input
                          ref={destinationPhoneRef}
                          type="text"
                          id="destinationConsigneePhoneNumber"
                          name="destinationConsigneePhoneNumber"
                          data-field="destinationConsigneePhoneNumber"
                          className="form__input form__input--sm"
                          placeholder="Consignee Phone Number"
                          maxLength={
                            fields?.destinationConsigneePhoneExt === "+1"
                              ? 16
                              : 10
                          }
                          value={onInputPhNumFormat(
                            fields.destinationConsigneePhoneNumber,
                            fields.destinationConsigneePhoneExt
                          )}
                          onChange={handleFieldChange}
                          onSelect={handleFieldChange}
                          autoComplete="off"
                          onBlur={() =>
                            handlePhoneBlur(
                              fields.destinationConsigneePhoneNumber,
                              fields.destinationConsigneePhoneExt,
                              "destinationConsigneePhoneNumber"
                            )
                          }
                        />
                      </div>
                      {errors.destinationConsigneePhoneNumber && (
                        <span className="form__error">
                          {errors.destinationConsigneePhoneNumber}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label htmlFor="email" className="form__label">
                        Consignee Contact Email{" "}
                        {notLTL ? (
                          ""
                        ) : (
                          <span className="mandatory-mark">*</span>
                        )}
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="destinationConsigneeContactEmail"
                        className={`form__input form__input--sm`}
                        placeholder="Consignee Contact Email"
                        autoComplete="off"
                        value={fields.destinationConsigneeContactEmail}
                        onChange={handleFieldChange}
                      />
                      {errors.destinationConsigneeContactEmail && (
                        <span className="form__error">
                          {errors.destinationConsigneeContactEmail}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="formRow">
                  {(notLTL ||
                    orderTypeRowData?.name === "Expedited" ||
                    orderTypeRowData?.name === "LTL + Final Mile") && (
                      <div className="formCol">
                        <div className="formRow formRow__3">
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationExtramen"
                                className="form__label"
                              >
                                Extra Men
                              </label>
                              <input
                                type="number"
                                id="destinationExtramen"
                                name="destinationExtramen"
                                className="form__input form__input--sm"
                                placeholder="Extra Men"
                                autoComplete="off"
                                value={
                                  fields.destinationExtramen !== ""
                                    ? Math.abs(Number(fields.destinationExtramen))
                                    : fields.destinationExtramen
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationNoOfFlights"
                                className="form__label"
                              >
                                No. of Flights (Stairs)
                              </label>
                              <input
                                type="number"
                                id="destinationNoOfFlights"
                                name="destinationNoOfFlights"
                                className="form__input form__input--sm"
                                placeholder="No. of Flights"
                                autoComplete="off"
                                value={
                                  fields.destinationNoOfFlights !== ""
                                    ? Math.abs(
                                      Number(fields.destinationNoOfFlights)
                                    )
                                    : fields.destinationNoOfFlights
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          <div className="formCol">
                            <div className="form__Field">
                              <label
                                htmlFor="destinationWaitTimeMinutes"
                                className="form__label"
                              >
                                Wait Time (Minutes)
                              </label>
                              <input
                                type="number"
                                id="destinationWaitTimeMinutes"
                                name="destinationWaitTimeMinutes"
                                className="form__input form__input--sm"
                                placeholder="Wait Time"
                                autoComplete="off"
                                value={
                                  fields.destinationWaitTimeMinutes !== ""
                                    ? Math.abs(
                                      Number(fields.destinationWaitTimeMinutes)
                                    )
                                    : fields.destinationWaitTimeMinutes
                                }
                                onChange={handleFieldChange}
                                onKeyDown={handleKeyDown}
                              />
                            </div>
                          </div>
                          {/* <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationStorage"
                            className="form__label"
                          >
                            Storage
                          </label>
                          <input
                            type="number"
                            id="destinationStorage"
                            name="destinationStorage"
                            className="form__input form__input--sm"
                            placeholder="Storage"
                            value={fields.destinationStorage}
                            onChange={handleFieldChange}
                            onKeyDown={handleKeyDown}
                          />
                        </div>
                      </div> */}
                        </div>
                      </div>
                    )}
                </div>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationHandingUnits"
                        className="form__label"
                      >
                        Handling Units
                      </label>
                      <input
                        type="number"
                        id="destinationHandingUnits"
                        name="destinationHandingUnits"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Handling Units"
                        autoComplete="off"
                        value={fields.destinationHandingUnits}
                        onChange={handleFieldChange}
                        disabled={true}
                        onKeyDown={handleKeyDown}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationWeight"
                        className="form__label"
                      >
                        Total Weight(LBS)
                      </label>
                      <input
                        type="number"
                        id="destinationWeight"
                        name="destinationWeight"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Total Weight"
                        autoComplete="off"
                        value={fields.destinationWeight}
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationPieces"
                        className="form__label"
                      >
                        Total Items/Pieces
                      </label>
                      <input
                        type="number"
                        id="destinationPieces"
                        name="destinationPieces"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Pieces"
                        autoComplete="off"
                        value={fields.destinationPieces}
                        onChange={handleFieldChange}
                        disabled={true}
                      />
                    </div>
                  </div>
                </div>
                {!notLTL && orderTypeRowData?.name !== "Expedited" && (
                  <div className="formRow formRow__2 mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <MultiSelectChips
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          dropdownData={dropAccessorials}
                          selectedData={dropAccessorialsSelected}
                          setDropdownData={setDropAccessorialDropdownData}
                          setSelectedData={setDropAccessorialsSelected}
                          searchPlaceholder="Search"
                          extendedUrl={{ isPickup: "false" }}
                          labelTitle="LTL Delivery Accessorials"
                          keyBindName="name"
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <MutliDropdown
                          columns={accessorialsColumn}
                          data={dropLimitedAccess}
                          setData={setDropLimitedAccessDropdownData}
                          captionTitles="Select Limited Access Type"
                          rowData={dropLimitedAccessSelected}
                          setRowData={setDropLimitedAccesssSelected}
                          displayColumn="name"
                          apiUrl={"/order/ltl-accessorial/wwex"}
                          extendedUrl={{
                            isPickup: "false",
                            isLimitAccess: "true",
                          }}
                          labelTitle="Limited Access Type"
                          enableRemove={true}
                          mandatory={false}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}

                {(notLTL || orderTypeRowData?.name === "Expedited") && (
                  <>
                    <div className="formRow">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationSpecialInstructions"
                            className="form__label"
                          >
                            Special Instructions
                          </label>
                          <textarea
                            id="destinationSpecialInstructions"
                            name="destinationSpecialInstructions"
                            className="form__textarea form__textarea--xs"
                            placeholder="Special Instructions"
                            autoComplete="off"
                            value={fields.destinationSpecialInstructions}
                            onChange={handleTextAreaChange}
                            maxLength={255}
                          ></textarea>
                        </div>
                      </div>
                    </div>
                    <div className="formRow formRow--gapNone">
                      <h1 className="form__section__title form__section__title--flex">
                        Add Handling Unit Details
                        <div className="btn__group">
                          <button
                            className="btn btn__white"
                            onClick={handleAddItemDetails}
                          >
                            <AddIcon />
                            Add Handling Units
                          </button>
                        </div>
                      </h1>
                      <OrderItemDetailTable
                        invalidCells={invalidCells}
                        invalidPackageCells={invalidPackageCells}
                        emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                        fieldKey={HANDLING_UNITS_FIELD_KEY}
                        focusCell={focusCell}
                        columns={tableConfig.columnsLTL}
                        packageType={apiPackageType}
                        data={data}
                        setData={setData}
                        setLoading={setLoading}
                        onDeleteConfig={tableConfig.delete}
                        showDelete={true}
                        isLTL={true}
                        isEditable={true}
                        setFields={setFields}
                        orderType={orderTypeRowData?.name}
                        customerId={assCustomerRowData?.id || customerRowData?.id}
                      />
                      {/* <div className="tableWrap tableWrap--order p_0">
                      <ItemDetailsTable
                        invalidCells={invalidCells}
                        fieldKey={HANDLING_UNITS_FIELD_KEY}
                        focusCell={focusCell}
                        columns={
                          orderTypeRowData?.name === "Expedited"
                            ? tableConfig.columnsExpediet
                            : tableConfig.columns
                        }
                        data={data}
                        setData={setData}
                        setLoading={setLoading}
                        onDeleteConfig={tableConfig.delete}
                        showDelete={true}
                        isEditable={true}
                        setFields={setFields}
                        isLTL={true}
                      />
                    </div> */}
                    </div>
                    <div className="formRow formRow__3">
                      <div className="formCol">
                        <div className="form__Field">
                          <label
                            htmlFor="destinationConsigneePhoneNumber"
                            className="form__label"
                          >
                            Total Cubic Meter
                          </label>
                        </div>
                        <input
                          type="text"
                          id="MidStopAPTSuit"
                          name="MidStopAPTSuit"
                          autoComplete="off"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Total Cubic Meter"
                          value={parseFloat(fields.totalCBM).toFixed(3)}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </>
                )}
              </section>
            )}

            {notLTL === false && orderTypeRowData?.name !== "Expedited" ? (
              <div className="form__sectionGrid">
                <section className="form__section">
                  <h1 className="form__section__title">Pickup Dock Data</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="PickUpDockName" className="form__label">
                          Dock Name
                        </label>
                        <input
                          type="text"
                          id="PickUpDockName"
                          name="PickUpDockName"
                          className="form__input form__input--sm"
                          placeholder="Dock Name"
                          autoComplete="off"
                          value={fields.PickUpDockName}
                          onChange={handleFieldChange}
                        />
                        {errors.PickUpDockName && (
                          <span className="form__error">
                            {errors.PickUpDockName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDOckNumber"
                          className="form__label"
                        >
                          Pickup Number
                        </label>
                        <input
                          type="text"
                          id="PickUpDOckNumber"
                          name="PickUpDOckNumber"
                          className="form__input form__input--sm"
                          placeholder="Pickup Number"
                          autoComplete="off"
                          value={fields.PickUpDOckNumber}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Pickup Date <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="date"
                          id="PickUpDockDate"
                          name="PickUpDockDate"
                          data-field="PickUpDockDate"
                          className={`form__date form__date--sm${fieldErrorClass("PickUpDockDate")}`}
                          placeholder="DD/MM/YYYY"
                          autoComplete="off"
                          min={moment().format("YYYY-MM-DD")}
                          onChange={handleFieldChange}
                          value={
                            normalizeDate(fields.PickUpDockDate) ||
                            state?.data?.PickUpDockDate
                          }
                        />
                      </div>
                      {errors.PickUpDockDate && (
                        <span className="form__error">
                          {errors.PickUpDockDate}
                        </span>
                      )}
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="PickUpDockTime" className="form__label">
                          Pickup Time <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            fields.PickUpDockTime || state?.data?.PickUpDockTime
                          }
                          onChange={handleTimeChange}
                          property="PickUpDockTime"
                          fieldKey="PickUpDockTime"
                          error={errors.PickUpDockTime}
                        />
                      </div>
                      {errors.PickUpDockTime && (
                        <span className="form__error">
                          {errors.PickUpDockTime}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDockOpenTime"
                          className="form__label"
                        >
                          Dock Open Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={fields.PickUpDockOpenTime}
                          onChange={handleTimeChange}
                          property="PickUpDockOpenTime"
                          fieldKey="PickUpDockOpenTime"
                          error={errors.PickUpDockOpenTime}
                        />
                        {errors.PickUpDockOpenTime && (
                          <span className="form__error">{errors.PickUpDockOpenTime}</span>
                        )}
                      </div>
                      <div
                        className="form__checkbox mt_12"
                        style={{ width: "max-content" }}
                      >
                        <div className="form__checkbox__option mb_0">
                          <input
                            type="checkbox"
                            id="PickUpFCFS"
                            className={`form__checkbox__input `}
                            checked={fields.PickUpFCFS}
                            onChange={handleCheckboxChange}
                          />
                          <label className="form__checkbox__label">
                            First come first serve
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="PickUpDOckCloseTime"
                          className="form__label"
                        >
                          Dock Close Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={fields.PickUpDOckCloseTime}
                          onChange={handleTimeChange}
                          property="PickUpDOckCloseTime"
                          fieldKey="PickUpDOckCloseTime"
                          error={errors.PickUpDOckCloseTime}
                        />
                        {errors.PickUpDOckCloseTime && (
                          <span className="form__error">{errors.PickUpDOckCloseTime}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Note</label>
                        <textarea
                          id="PickUpDOckNote"
                          name="PickUpDOckNote"
                          className="form__textarea form__textarea--xs"
                          placeholder="Special Instructions"
                          autoComplete="off"
                          value={fields.PickUpDOckNote}
                          onChange={handleTextAreaChange}
                          maxLength={255}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="form__section">
                  <h1 className="form__section__title">Drop Dock Data</h1>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="DropDockName" className="form__label">
                          Dock Name
                        </label>
                        <input
                          type="text"
                          id="DropDockName"
                          name="DropDockName"
                          className={`form__input form__input--sm ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__input--disabled"
                            : ""
                            }`}
                          placeholder="Dock Name"
                          autoComplete="off"
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDockName
                          }
                          onChange={handleFieldChange}
                        />
                        {errors.DropDockName && (
                          <span className="form__error">
                            {errors.DropDockName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="DropDOckNumber" className="form__label">
                          Pickup Number
                        </label>
                        <input
                          type="text"
                          id="DropDOckNumber"
                          name="DropDOckNumber"
                          className={`form__input form__input--sm ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__input--disabled"
                            : ""
                            }`}
                          placeholder="Pickup Number"
                          autoComplete="off"
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckNumber
                          }
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="waittime" className="form__label">
                          Dock Open Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDockOpenTime
                          }
                          onChange={handleTimeChange}
                          property="DropDockOpenTime"
                          fieldKey="DropDockOpenTime"
                          error={errors.DropDockOpenTime}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? true
                              : false
                          }
                        />
                        {errors.DropDockOpenTime && (
                          <span className="form__error">{errors.DropDockOpenTime}</span>
                        )}
                      </div>
                      <div
                        className="form__checkbox mt_12"
                        style={{ width: "max-content" }}
                      >
                        <div className="form__checkbox__option mb_0">
                          <input
                            type="checkbox"
                            id="DropFCFS"
                            className={`form__checkbox__input `}
                            checked={fields.DropFCFS}
                            disabled={
                              orderTypeRowData?.name === "LTL + Final Mile"
                            }
                            autoComplete="off"
                            onChange={handleCheckboxChange}
                          />
                          <label className="form__checkbox__label">
                            First come first serve
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="waittime" className="form__label">
                          Dock Close Time{" "}
                          <span className="mandatory-mark">*</span>
                        </label>
                        <CustomTimePicker
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckCloseTime
                          }
                          onChange={handleTimeChange}
                          property="DropDOckCloseTime"
                          fieldKey="DropDOckCloseTime"
                          error={errors.DropDOckCloseTime}
                          disable={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? true
                              : false
                          }
                        />
                        {errors.DropDOckCloseTime && (
                          <span className="form__error">{errors.DropDOckCloseTime}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow mb_0">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Note</label>
                        <textarea
                          id="DropDOckNote"
                          name="DropDOckNote"
                          // className="form__textarea form__textarea--xs"
                          className={`form__textarea form__textarea--xs ${orderTypeRowData?.name === "LTL + Final Mile"
                            ? "form__textarea--disabled"
                            : ""
                            }`}
                          placeholder="Special Instructions"
                          autoComplete="off"
                          value={
                            orderTypeRowData?.name === "LTL + Final Mile"
                              ? ""
                              : fields.DropDOckNote
                          }
                          disabled={
                            orderTypeRowData?.name === "LTL + Final Mile"
                          }
                          onChange={handleTextAreaChange}
                          maxLength={255}
                        ></textarea>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            ) : (
              ""
            )}

            {notLTL ? (
              <section className="form__section">
                <h1 className="form__section__title">
                  Select Additional Services
                </h1>
                <div className="formRow">
                  <div className="form__checkbox form__checkbox--gray">
                    <div className="form__checkbox__option mb_0">
                      <input
                        type="checkbox"
                        id="Remove & Disposal"
                        autoComplete="off"
                        className={`form__checkbox__input`}
                        checked={fields.additionalServices.some(
                          (x) => x === "Remove & Disposal"
                        )}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form__checkbox__label">
                        Remove & Disposal
                      </label>
                    </div>
                    <div className="form__checkbox__option mb_0">
                      <input
                        type="checkbox"
                        id="Order Swap & Exchange"
                        autoComplete="off"
                        className={`form__checkbox__input`}
                        checked={fields.additionalServices.some(
                          (x) => x === "Order Swap & Exchange"
                        )}
                        onChange={handleCheckboxChange}
                      />
                      <label className="form__checkbox__label">
                        Order Swap & Exchange
                      </label>
                    </div>
                  </div>
                </div>
                <div className="formRow mb_0">
                  {/* Flush regardless of results: the quote list below brings its
                      own spacing, so the 15px `.form--innersec` adds once results
                      show up only opened a void under the button. */}
                  <div className="btn__group mb_0">
                    <button
                      className="btn btn__success"
                      type="button"
                      onClick={() => {
                        getQuotesData();
                      }}
                      disabled={isQuote}
                    >
                      Get Quote
                    </button>
                  </div>
                </div>
                {getQuote &&
                  (quoteData?.length > 0 ? (
                    <>
                      <GetQuote
                        setFields={setFields}
                        quoteData={quoteData}
                        setIsQuote={setIsQuote}
                        saveQuotation={handleSaveQuotation}
                        quoteSelectedId={stateQuoteId}
                        masterHubName={masterHubName}
                        masterHubAddress={masterHubAddress}
                        setIsSelectedParent={setIsSelectedParent}
                      />
                    </>
                  ) : (
                    <span>
                      {orderTypeRowData?.name === "Final Mile"
                        ? "No Final Mile Agent Available!"
                        : orderTypeRowData?.name === "LTL"
                          ? "No LTL Agent Available!"
                          : orderTypeRowData?.name === "LTL + Final Mile"
                            ? "No LTL + Final Mile1 Agent Available!"
                            : "No Results Found!"}
                    </span>
                  ))}
              </section>
            ) : orderTypeRowData?.name !== "Expedited" ? (
              <section className="form__section">
                <div className="formRow formRow--gapNone">
                  <h1 className="form__section__title form__section__title--flex">
                    Add Handling Unit Details
                    <div className="btn__group mb_0">
                      <button
                        className="btn btn__white"
                        onClick={handleAddItemDetails}
                      >
                        <AddIcon />
                        Add Handling Units
                      </button>
                    </div>
                  </h1>

                  <OrderItemDetailTable
                    invalidCells={invalidCells}
                    invalidPackageCells={invalidPackageCells}
                    emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                    fieldKey={HANDLING_UNITS_FIELD_KEY}
                    focusCell={focusCell}
                    columns={tableConfig.columnsLTL}
                    packageType={
                      // orderTypeRowData?.name === "LTL" ||
                      //   orderTypeRowData?.name === "LTL + Final Mile"
                      //   ? PACKAGE_TYPE
                      //   : EXPEDIET_PACKAGE_TYPE
                      apiPackageType
                    }
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                    orderType={orderTypeRowData?.name}
                    customerId={assCustomerRowData?.id || customerRowData?.id}
                  />

                  {/* <div className="tableWrap tableWrap--order p_0">
                  <ItemDetailsTable
                    invalidCells={invalidCells}
                    fieldKey={HANDLING_UNITS_FIELD_KEY}
                    focusCell={focusCell}
                    columns={tableConfig.columnsLTL}
                    data={data}
                    setData={setData}
                    setLoading={setLoading}
                    onDeleteConfig={tableConfig.delete}
                    showDelete={true}
                    isLTL={true}
                    isEditable={true}
                    setFields={setFields}
                  />
                </div> */}
                </div>
                <div className="formRow formRow__3">
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Density
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Linear Feet"
                      value={parseFloat(fields.totalDensity).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Linear Feet
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Linear Feet"
                      value={parseFloat(fields.totalLinearFeet).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                  <div className="formCol">
                    <div className="form__Field">
                      <label
                        htmlFor="destinationConsigneePhoneNumber"
                        className="form__label"
                      >
                        Total Cubic Meter
                      </label>
                    </div>
                    <input
                      type="text"
                      id="MidStopAPTSuit"
                      name="MidStopAPTSuit"
                      autoComplete="off"
                      className="form__input form__input--sm form__input--disabled"
                      placeholder="Total Cubic Meter"
                      value={parseFloat(fields.totalCBM).toFixed(3)}
                      disabled={true}
                    />
                  </div>
                </div>
                <div className="formRow mb_0">
                  <div
                    className={showLTLList ? "btn__group" : "btn__group mb_0"}
                  >
                    <button
                      className="btn btn__success"
                      type="button"
                      onClick={() => {
                        getLTLQuotesData();
                      }}
                    >
                      {orderTypeRowData?.name === "LTL"
                        ? "Get Quote"
                        : "Get Quote"}
                    </button>
                  </div>
                </div>
                {showLTLList && !state?.isSelected && (
                  <GetLTLQuote
                    orderType={orderTypeRowData?.name || ""}
                    quoteData={TLList}
                    fields={fields}
                    setFields={setFields}
                    setIsQuote={setIsQuote}
                    isLtlFinalmile={isLtlFinalmile}
                    saveQuotation={handleSaveLTLQuotation}
                    setIsSelectedParent={setIsSelectedParent}
                    quoteSelectedId={stateQuoteId}
                  />
                )}
                {showLTLList && state?.isSelected && (
                  <GetTraditionalQuote
                    orderType={
                      orderTypeRowData?.name === "LTL"
                        ? "LTL"
                        : "LTL + Final Mile"
                    }
                    setFields={setFields}
                    fields={fields}
                    quoteData={quoteData}
                    setIsQuote={setIsQuote}
                    setIsSelectedParent={setIsSelectedParent}
                    saveQuotation={
                      orderTypeRowData?.name === "LTL"
                        ? handleSaveQuotationForLTL
                        : handleSaveQuotation
                    }
                    quoteSelectedId={stateQuoteId}
                    orderTypeRowData={orderTypeRowData}
                    setOrderTypeRowData={setOrderTypeRowData}
                    ltlFMData={ltlFMData}
                    setLtlFMData={setLtlFMData}
                    savedData={savedData}
                  />
                )}
                {showLTLData && (
                  <>
                    <div className="getQuoteBox" style={{ marginTop: "15px" }}>
                      <p className="para">
                        {/* Hub: Hub Name C/O Agent Name, City, State, Zipcode, Country, Hours of operation, Mile */}
                        {`Carrier: ${fields?.CarrierName}(${fields?.scac}) `}
                      </p>
                      <h3 className="title title--h3">
                        Quote Price: {fields?.serviceCost}
                      </h3>
                    </div>
                    <section className="form__section">
                      <h3 className="form__section__title">
                        LTL Carrier Details
                      </h3>
                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              Carrier Name
                            </label>
                            <input
                              type="text"
                              id="quoteCarrierName"
                              name="quoteCarrierName"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Carrier Name"
                              value={fields?.CarrierName}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              Carrier Code
                            </label>
                            <input
                              type="text"
                              id="quoteCarrierCode"
                              name="quoteCarrierCode"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Carrier Code"
                              value={fields?.scac}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="quoteOriginCity"
                              className="form__label"
                            >
                              Origin City
                            </label>
                            <input
                              type="text"
                              id="quoteOriginCity"
                              name="quoteOriginCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Origin City"
                              value={""}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              Service Cost
                            </label>
                            <input
                              type="text"
                              id="quoteServiceCost"
                              name="quoteServiceCost"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Service Cost"
                              value={fields?.serviceCost}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="formRow formRow__2">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              3PL Provider
                            </label>
                            <input
                              type="text"
                              id="plProvider"
                              name="plProvider"
                              className="form__input form__input--sm"
                              placeholder="3PL Provider"
                              value={fields.plProvider}
                              onChange={handleFieldChange}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              Courier Pro No.
                            </label>
                            <input
                              type="text"
                              id="courierPro1"
                              name="courierPro1"
                              className="form__input form__input--sm"
                              placeholder="Courier Pro No."
                              value={fields.courierPro1}
                              onChange={handleFieldChange}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="formRow formRow__2 mb_0">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopPhoneNumber"
                              className="form__label"
                            >
                              BOL No.
                            </label>
                            <input
                              type="text"
                              id="courierPro2"
                              name="courierPro2"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="BOL No."
                              value={""}
                              // onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </section>
                  </>
                )}
                {getQuote &&
                  notLTL &&
                  (quoteData?.length > 0 ? (
                    <GetQuote
                      setFields={setFields}
                      quoteData={quoteData}
                      setIsQuote={setIsQuote}
                      setIsSelectedParent={setIsSelectedParent}
                    />
                  ) : (
                    <span>No Results Found!</span>
                  ))}
              </section>
            ) : (
              ""
            )}
            <section className="form__section">
              {showFMList && notLTL && (
                <>
                  <div className="getQuoteBox" style={{ marginBottom: "15px" }}>
                    <p className="para">
                      {/* Hub: Hub Name C/O Agent Name, City, State, Zipcode, Country, Hours of operation, Mile */}
                      {`Hub: ${hasMasterHub ? masterHubName : fields?.hubName
                        }  C/O ${fields?.agentName || ""}, ${hasMasterHub ? masterHubAddress.city : fields?.city
                        }, ${hasMasterHub ? masterHubAddress.state : fields?.country
                        }, ${hasMasterHub
                          ? masterHubAddress.zipCode
                          : fields?.zipCode
                        }, ${hasMasterHub
                          ? masterHubAddress.country
                          : fields?.country
                        } | Hrs of Operation: ${hasMasterHub
                          ? masterHubAddress?.operationHrsFrom &&
                            masterHubAddress?.operationHrsTo
                            ? `${masterHubAddress.operationHrsFrom} - ${masterHubAddress.operationHrsTo}`
                            : ""
                          : fields?.operationHrsFrom && fields?.operationHrsTo
                            ? `${fields.operationHrsFrom} - ${fields.operationHrsTo}`
                            : ""
                        } | ${fields.distanceToDestination} mi`}
                    </p>
                    <h3 className="title title--h3">
                      Quote Price: $
                      {fields?.quotePrice
                        ? parseFloat(fields?.quotePrice).toFixed(2)
                        : 0.0}
                    </h3>
                  </div>
                </>
              )}
              {isQuote && notLTL && (
                <>
                  <h3 className="form__section__title">Mid Stop</h3>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopAddress" className="form__label">
                          Address
                        </label>
                        <input
                          type="text"
                          id="MidStopAddress"
                          name="MidStopAddress"
                          data-field="MidStopAddress"
                          className={`form__input form__input--sm form__input--disabled${fieldErrorClass("MidStopAddress")}`}
                          placeholder="Address"
                          autoComplete="off"
                          value={
                            masterHubAddress?.address
                              ? masterHubAddress?.address
                              : fields.MidStopAddress
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                        {errors.MidStopAddress && (
                          <span className="form__error">{errors.MidStopAddress}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopAPTSuit" className="form__label">
                          APT / Suite
                        </label>
                        <input
                          type="text"
                          id="MidStopAPTSuit"
                          name="MidStopAPTSuit"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="APT/Suite"
                          autoComplete="off"
                          value={
                            masterHubAddress?.aptSuit
                              ? masterHubAddress?.aptSuit
                              : fields.MidStopAPTSuit
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopZipcode" className="form__label">
                          Zip Code
                        </label>
                        <input
                          type="text"
                          id="MidStopZipcode"
                          name="MidStopZipcode"
                          data-field="MidStopZipcode"
                          autoComplete="off"
                          className={`form__input form__input--sm form__input--disabled${fieldErrorClass("MidStopZipcode")}`}
                          placeholder="Zip Code"
                          value={
                            masterHubAddress?.zipCode
                              ? masterHubAddress?.zipCode
                              : fields.MidStopZipcode
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                        {errors.MidStopZipcode && (
                          <span className="form__error">{errors.MidStopZipcode}</span>
                        )}
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="formRow formRow__3">
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopCity"
                              className="form__label"
                            >
                              City
                            </label>
                            <input
                              type="text"
                              id="MidStopCity"
                              name="MidStopCity"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="City"
                              autoComplete="off"
                              value={
                                masterHubAddress?.city
                                  ? masterHubAddress?.city
                                  : fields.MidStopCity
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopState"
                              className="form__label"
                            >
                              State
                            </label>
                            <input
                              type="text"
                              id="MidStopState"
                              name="MidStopState"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="State"
                              autoComplete="off"
                              value={
                                masterHubAddress?.state
                                  ? masterHubAddress?.state
                                  : fields.MidStopState
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                        <div className="formCol">
                          <div className="form__Field">
                            <label
                              htmlFor="MidStopCountry"
                              className="form__label"
                            >
                              Country
                            </label>
                            <input
                              type="text"
                              id="MidStopCountry"
                              name="MidStopCountry"
                              className="form__input form__input--sm form__input--disabled"
                              placeholder="Country"
                              autoComplete="off"
                              value={
                                masterHubAddress?.country
                                  ? masterHubAddress?.country
                                  : fields.MidStopCountry
                              }
                              onChange={handleFieldChange}
                              disabled={true}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopContactName"
                          className="form__label"
                        >
                          Contact Name
                        </label>
                        <input
                          type="text"
                          id="MidStopContactName"
                          name="MidStopContactName"
                          className="form__input form__input--sm form__input--disabled"
                          placeholder="Contact name"
                          autoComplete="off"
                          value={
                            masterHubAddress?.contactPerson
                              ? masterHubAddress?.contactPerson
                              : fields.MidStopContactName
                          }
                          onChange={handleFieldChange}
                          disabled={true}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopPhoneNumber"
                          className="form__label"
                        >
                          Phone Number
                        </label>
                        <div className="form__groupField form__groupField--phonenumber form__groupField--disabled">
                          <select
                            className="form__select form__select--sm"
                            disabled={true}
                            value={
                              masterHubAddress?.countryCallingCode
                                ? masterHubAddress?.countryCallingCode
                                : fields.MidStopPhoneNumberExt
                            }
                            onChange={(e) => {
                              setFields(() => ({
                                ...fields,
                                MidStopPhoneNumberExt: e.target.value,
                              }));
                            }}
                          >
                            {countryCode?.map((item: any) => (
                              <option
                                key={item?.countryCallingCode}
                                value={
                                  masterHubAddress?.countryCallingCode
                                    ? masterHubAddress?.countryCallingCode
                                    : item?.countryCallingCode
                                }
                              >
                                {masterHubAddress?.countryCallingCode
                                  ? masterHubAddress?.countryCallingCode
                                  : item?.countryCallingCode}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            id="MidStopPhoneNumber"
                            name="MidStopPhoneNumber"
                            className="form__input form__input--sm"
                            placeholder="Phone Number"
                            value={onInputPhNumFormat(
                              masterHubAddress?.phoneNumber
                                ? masterHubAddress?.phoneNumber
                                : fields.MidStopPhoneNumber,
                              masterHubAddress?.countryCallingCode
                                ? masterHubAddress?.countryCallingCode
                                : fields.MidStopPhoneNumberExt || "+1"
                            )}
                            onChange={handleFieldChange}
                            onSelect={handleFieldChange}
                            autoComplete="off"
                            disabled={true}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <EmailChips
                        emails={
                          masterHubAddress?.contactEmail
                            ? masterHubAddress?.contactEmail
                            : fields.MidStopContactEmail
                        }
                        setEmails={setFields}
                        placeholder="Contact Email"
                        label="Contact Email"
                        keyName="MidStopContactEmail"
                        error={errors}
                        setError={setErrors}
                        disable={true}
                      />
                    </div>
                  </div>
                </>
              )}
              {orderTypeRowData?.name === "Expedited" ? (
                <>
                  <div className="formRow formRow--gapNone">
                    <h1 className="form__section__title form__section__title--flex">
                      Add Handling Unit Details
                      <div className="btn__group">
                        <button
                          className="btn btn__white"
                          onClick={handleAddItemDetails}
                        >
                          <AddIcon />
                          Add Handling Units
                        </button>
                      </div>
                    </h1>
                    <OrderItemDetailTable
                      invalidCells={invalidCells}
                      invalidPackageCells={invalidPackageCells}
                      emptyMessage={itemsEmpty ? HANDLING_UNITS_REQUIRED_MESSAGE : undefined}
                      fieldKey={HANDLING_UNITS_FIELD_KEY}
                      focusCell={focusCell}
                      columns={tableConfig.columnsLTL}
                      packageType={apiPackageType}
                      data={data}
                      setData={setData}
                      setLoading={setLoading}
                      onDeleteConfig={tableConfig.delete}
                      showDelete={true}
                      isLTL={true}
                      isEditable={true}
                      setFields={setFields}
                      orderType={orderTypeRowData?.name}
                      customerId={assCustomerRowData?.id || customerRowData?.id}
                    />
                  </div>
                  <div className="formRow formRow__3">
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="destinationConsigneePhoneNumber"
                          className="form__label"
                        >
                          Total Cubic Meter
                        </label>
                      </div>
                      <input
                        type="text"
                        id="MidStopAPTSuit"
                        name="MidStopAPTSuit"
                        autoComplete="off"
                        className="form__input form__input--sm form__input--disabled"
                        placeholder="Total Cubic Meter"
                        value={parseFloat(fields.totalCBM).toFixed(3)}
                        disabled={true}
                      />
                    </div>
                  </div>
                  <h3 className="form__section__title">
                    Courier / On Demand Agent Details
                  </h3>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <MutliDropdown
                        columns={agentOrderColumns}
                        data={agentNameDropdown}
                        setData={setAgentNameDropdown}
                        captionTitles="Select Agent"
                        rowData={agentRowData}
                        setRowData={(row: any) => {
                          setAgentRowData(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.agent);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.agent}
                        error={errors[DROPDOWN_FIELD_KEYS.agent]}
                        displayColumn="name"
                        apiUrl={"/agent"}
                        labelTitle="Agent Name"
                        enableRemove={true}
                        mandatory={true}
                        type={"dropdown"}
                        extendedUrl={{ orderType: "expedited" }}
                      />
                    </div>
                    <div className="formCol">
                      <MutliDropdown
                        labelTitle="Agent Hub"
                        columns={agentHubColumns}
                        data={agentHubDropdownData}
                        setData={setAgentHubDropdownData}
                        captionTitles="Select Agent Hub"
                        rowData={agentHubRowData}
                        setRowData={(row: any) => {
                          setAgentHubRowData(row);
                          clearMandatory(DROPDOWN_FIELD_KEYS.agentHub);
                        }}
                        fieldKey={DROPDOWN_FIELD_KEYS.agentHub}
                        error={errors[DROPDOWN_FIELD_KEYS.agentHub]}
                        displayColumn="name"
                        apiUrl={agentRowData?.id ? "/agent/agent-hub" : ""}
                        type={"dropdown"}
                        extraParentId={{
                          key: "agentId",
                          id: agentRowData?.id ? agentRowData?.id : "",
                        }}
                        enableRemove={true}
                        mandatory={
                          agentRowData?.agentType &&
                            agentRowData?.agentType != "LTL"
                            ? true
                            : false
                        }
                        disable={
                          agentRowData?.agentType && agentRowData?.agentType === "LTL"
                            ? true
                            : false
                        }
                        extendedUrl={{
                          from: "dropdown",
                        }}
                        extraFields={[
                          "isVirtualHub",
                          "masterHubId",
                          "masterHubName",
                        ]}
                      />
                      {agentHubRowData?.isVirtualHub &&
                        String(agentHubRowData.masterHubId).trim() !== "" && (
                          <span className="form__error">
                            Note: This hub belongs to Master Hub :{" "}
                            {agentHubRowData.masterHubName || "—"}
                          </span>
                        )}
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">Origin City</label>
                        <input
                          type="text"
                          id="originCity"
                          name="originCity"
                          className="form__input form__input--sm"
                          placeholder="Origin City"
                          autoComplete="off"
                          value={fields.originCity}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label htmlFor="MidStopCity" className="form__label">
                          Service Cost <span className="mandatory-mark">*</span>
                        </label>
                        <input
                          type="number"
                          id="serviceCost"
                          name="serviceCost"
                          data-field="serviceCost"
                          className={`form__input form__input--sm${fieldErrorClass("serviceCost")}`}
                          placeholder="Service Cost"
                          autoComplete="off"
                          value={fields.serviceCost}
                          onChange={handleFieldChange}
                          onKeyDown={handleKeyDown}
                        />
                        {errors.serviceCost && (
                          <span className="form__error">{errors.serviceCost}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="formRow formRow__2">
                    <div className="formCol">
                      <div className="form__Field">
                        <label className="form__label">
                          Courier Pro. No. 1
                        </label>
                        <input
                          type="text"
                          id="courierPro1"
                          name="courierPro1"
                          className="form__input form__input--sm"
                          placeholder="Courier Pro. No. 1"
                          autoComplete="off"
                          value={fields.courierPro1}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                    <div className="formCol">
                      <div className="form__Field">
                        <label
                          htmlFor="MidStopPhoneNumber"
                          className="form__label"
                        >
                          Courier Pro. No. 2
                        </label>
                        <input
                          type="text"
                          id="courierPro2"
                          name="courierPro2"
                          className="form__input form__input--sm"
                          placeholder="Courier Pro. No. 2"
                          autoComplete="off"
                          value={fields.courierPro2}
                          onChange={handleFieldChange}
                        />
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                ""
              )}
              <div className="btn__group">
                <button
                  className="btn btn__white"
                  type="button"
                  onClick={handleBack}
                >
                  Cancel
                </button>
                <button
                  className="btn btn__primary"
                  type="submit"
                  onClick={handleSave}
                  // Picking a quote is a step, not a field the user can fill in place,
                  // so it stays a disabled button rather than a validation message.
                  disabled={!isQuoteSelected()}
                >
                  Book
                </button>
                {((isQuote && orderTypeRowData?.name != "LTL") ||
                  orderTypeRowData?.name === "Expedited") && (
                    <button
                      className="btn btn__primary"
                      type="submit"
                      onClick={() => {
                        setViewAddDocumentModal(true);
                      }}
                    >
                      Add Document & URL
                    </button>
                  )}
              </div>
            </section>
          </div>
        </div>
        {viewAddDocumentModal && (
          <AddDocumentURLModal
            confirmModal={viewAddDocumentModal}
            setConfirmModal={setViewAddDocumentModal}
            setDocumentInfo={setDocumentInfo}
            documentInfo={documentInfo}
          />
        )}
        {showModal && (
          <ConfirmModal
            confirmInfo={{
              heading: "Unsaved Changes!",
              para: "You have not saved your changes, do you want to continue",
            }}
            onConfirm={handleConfirmNavigation}
            setConfirmModal={handleCancelNavigation}
            firstMessageShow={true}
          />
        )}
      </div>
    </>
  );
};

export default CustomerOrder;
